import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Calculator, FileText, TrendingUp, LogIn, LogOut, Plus, Trash2, Download, Building2, Zap, Save, FolderOpen, RefreshCw, Truck } from 'lucide-react';
import toast, { Toaster } from 'react-hot-toast';
import supabaseService from './services/supabaseService';
import { useAuth } from './hooks/useAuth';
import * as simpleAuth from './utils/simpleAuth';
import logoImage from './logo-no-background.png';
import SearchableDropdown from './components/shared/SearchableDropdown';
import NogaHubLogo from './components/shared/NogaHubLogo';
import { BUSINESS_CONSTANTS, SERVICE_PRICING, SHIPPING_CUSTOMS, TAX_RATES, ORIGINATION_FEE_RATE } from './config/constants';
import RentalCalendar from './components/rentals/RentalCalendar';
import RentalsFinancials from './components/rentals/RentalsFinancials';
import rentalQuotationsService from './services/rentalQuotationsService';

// Helper function to get full currency name
const getCurrencyName = (currencyCode) => {
  const currencyNames = {
    'JOD': 'Jordanian Dinars (JOD)',
    'AED': 'UAE Dirhams (AED)',
    'SAR': 'Saudi Riyals (SAR)',
    'EGP': 'Egyptian Pounds (EGP)',
    'KWD': 'Kuwaiti Dinars (KWD)',
    'QAR': 'Qatari Riyals (QAR)',
    'BHD': 'Bahraini Dinars (BHD)',
    'OMR': 'Omani Rials (OMR)',
    'LBP': 'Lebanese Pounds (LBP)',
    'IQD': 'Iraqi Dinars (IQD)',
    'TND': 'Tunisian Dinars (TND)',
    'MAD': 'Moroccan Dirhams (MAD)',
    'DZD': 'Algerian Dinars (DZD)',
    'LYD': 'Libyan Dinars (LYD)',
    'SYP': 'Syrian Pounds (SYP)',
    'YER': 'Yemeni Rials (YER)',
    'SDG': 'Sudanese Pounds (SDG)',
    'USD': 'US Dollars (USD)',
    'EUR': 'Euros (EUR)',
    'GBP': 'British Pounds (GBP)',
    'JPY': 'Japanese Yen (JPY)',
    'CNY': 'Chinese Yuan (CNY)',
    'CHF': 'Swiss Francs (CHF)',
    'CAD': 'Canadian Dollars (CAD)',
    'AUD': 'Australian Dollars (AUD)',
    'INR': 'Indian Rupees (INR)',
    'SGD': 'Singapore Dollars (SGD)',
  };
  return currencyNames[currencyCode] || currencyCode;
};

const NogaHubAutomation = () => {
  // Use the authentication hook
  const {
    isAuthenticated,
    userRole,
    loginData,
    setLoginData,
    handleLogin: authHandleLogin,
    handleGoogleLogin,
    handleLogout: authHandleLogout,
    isLoading: authLoading
  } = useAuth();
  const [activeTab, setActiveTab] = useState('documents');
  const [activeSection, setActiveSection] = useState('installation');
  const [rentalTab, setRentalTab] = useState('calendar'); // 'calendar' | 'financials' | 'documents' | 'quotation'
  const [isCalculated, setIsCalculated] = useState(false);
  const [calculationResults, setCalculationResults] = useState(null);
  const [savedProjects, setSavedProjects] = useState([]);
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [showNcSaveModal, setShowNcSaveModal] = useState(false);
  const [loadedProjectId, setLoadedProjectId] = useState(null);

  // Equipment data will be fetched from secure backend API
  const [equipmentDatabase, setEquipmentDatabase] = useState([]);
  const [equipmentLoading, setEquipmentLoading] = useState(false);

  const [project, setProject] = useState({
    clientName: '',
    projectName: '',
    equipment: [],
    globalDiscount: 0,
    services: {
      commissioning: { enabled: false, customValue: 0 },
      noiseControl: { enabled: false, customValue: 0 },
      soundDesign: { enabled: false, customValue: 0 },
    },
    customServices: [],
    customEquipment: [],
    includeTax: true,
    terms: `All prices are in Jordanian Dinars (JOD)
Equipment prices include door-to-door delivery
VAT is calculated at 16% as per Jordanian tax regulations
Subject to ±10% change after technical study
Payment terms: 90% down payment, 10% after project completion
This quotation is valid for 30 days from the date of issue`,
    roles: {
      producer: '',
      director: '',
      projectManager: '',
      juniorProjectManager: '',
      accountant: '',
      logisticsManager: '',
      noiseControlEngineer: '',
      soundSystemDesigner: ''
    }
  });

  // Noise Control Quotation State
  const [customQuotation, setCustomQuotation] = useState({
    clientName: '',
    projectName: '',
    currency: 'JOD',
    items: [],
    services: [],
    globalDiscount: 0,
    includeTax: true,
    terms: `All prices are in Jordanian Dinars (JOD)
Equipment prices include door-to-door delivery
VAT is calculated at 16% as per Jordanian tax regulations
Subject to ±10% change after technical study
Payment terms: 90% down payment, 10% after project completion
This quotation is valid for 30 days from the date of issue`
  });
  const [ncCalculated, setNcCalculated] = useState(false);
  const [ncResults, setNcResults] = useState(null);

  // Rental Quotation State (standalone calculator, separate from Installation)
  const RENTAL_DEFAULT_TERMS = `All prices are in Jordanian Dinars (JOD)
Equipment rental includes delivery, setup, and collection
VAT is calculated at 16% as per Jordanian tax regulations
Payment terms: 50% deposit, 50% on day of event
This quotation is valid for 14 days from the date of issue`;

  const [rentalQuotation, setRentalQuotation] = useState({
    clientName: '',
    projectName: '',
    currency: 'JOD',
    items: [],
    riderItems: [],
    services: [],
    globalDiscount: 0,
    includeTax: true,
    terms: RENTAL_DEFAULT_TERMS,
  });
  const [rentalCalculated, setRentalCalculated] = useState(false);
  const [rentalResults, setRentalResults] = useState(null);
  const [savedRentalProjects, setSavedRentalProjects] = useState([]);
  const [loadedRentalProjectId, setLoadedRentalProjectId] = useState(null);
  const [showRentalSaveModal, setShowRentalSaveModal] = useState(false);

  // Function to load saved projects from backend
  const loadSavedProjects = async () => {
    try {
      const response = await supabaseService.getProjects();
      if (response.success) {
        setSavedProjects(response.projects || []);
      } else {
        // A failed load must not look identical to "you have no projects".
        console.error('Failed to load saved projects:', response.error);
        toast.error('Could not load saved projects. See console for details.');
        setSavedProjects([]);
      }
    } catch (error) {
      console.error('Failed to load saved projects:', error);
      toast.error('Could not load saved projects. See console for details.');
      setSavedProjects([]);
    }
  };

  const loadSavedRentalProjects = async () => {
    try {
      const projects = await rentalQuotationsService.listRentalQuotations();
      setSavedRentalProjects(projects || []);
    } catch (error) {
      console.error('Failed to load rental projects:', error);
      toast.error('Could not load rental projects. See console for details.');
      setSavedRentalProjects([]);
    }
  };

  // Load data when user authenticates
  useEffect(() => {
    if (isAuthenticated && !authLoading) {
      console.log('✅ User authenticated, loading data...');
      loadEquipmentData();
      loadSavedProjects();
      loadSavedRentalProjects();
    }
  }, [isAuthenticated, authLoading]); // eslint-disable-line react-hooks/exhaustive-deps

  // Remove localStorage sync - projects are now stored in backend

  // Function to load equipment data from API
  const loadEquipmentData = async () => {
    try {
      setEquipmentLoading(true);

      const response = await supabaseService.getEquipment({ limit: 1000 }); // Load all equipment
      if (response.success) {
        setEquipmentDatabase(response.equipment || []);
      }
    } catch (error) {
      console.error('Failed to load equipment:', error);

      if (error.message.includes('Authentication') || error.message.includes('token') || error.message.includes('Session')) {
        toast.error('Your session has expired. Please log in again.');
        window.location.href = '/login';
      } else {
        toast.error(`Failed to load equipment data: ${error.message}`);
      }
    } finally {
      setEquipmentLoading(false);
    }
  };

  // Function to save current project
  const saveCurrentProject = async () => {
    if (project.projectName.trim() && project.clientName.trim() && (project.equipment.length > 0 || project.customEquipment.length > 0)) {
      const currentUser = simpleAuth.getCurrentUser();
      if (!currentUser) {
        toast.error('Please log in to save projects.');
        return;
      }

      try {
        // First create base project data
        const baseProjectData = { ...project };

        // Then apply transformations that override the spread
        const projectData = {
          ...baseProjectData,
          ...(loadedProjectId ? { id: loadedProjectId } : {}),
          // Fix equipment - filter out empty codes
          equipment: baseProjectData.equipment.filter(item => item.code && item.code.trim()),
          // Fix services - convert to simple booleans (this must come after the spread)
          services: {
            commissioning: { enabled: baseProjectData.services.commissioning?.enabled || false, customValue: baseProjectData.services.commissioning?.customValue || 0 },
            noiseControl: { enabled: baseProjectData.services.noiseControl?.enabled || false, customValue: baseProjectData.services.noiseControl?.customValue || 0 },
            soundDesign: { enabled: baseProjectData.services.soundDesign?.enabled || false, customValue: baseProjectData.services.soundDesign?.customValue || 0 },
          },
          total: calculationResults ? (calculationResults.projectTotalJOD || calculationResults.totalCostUSD || 0) : 0,
          isCalculated: isCalculated,
          calculationResults: calculationResults,
          projectType: 'soundDesign'
        };

        const response = await supabaseService.saveProject(projectData);

        if (response.success) {
          setLoadedProjectId(response.project?.id ?? loadedProjectId);
          // Reload projects from backend to get updated list
          await loadSavedProjects();

          setShowSaveModal(false);
          toast.success(response.message || (loadedProjectId ? 'Quotation updated successfully!' : 'Project saved successfully!'));
        }
      } catch (error) {
        console.error('Failed to save project:', error);
        toast.error('Failed to save project. Please try again.');
      }
    } else {
      toast.error('Please ensure project has a name, client name, and at least one equipment item before saving.');
    }
  };

  // Function to save noise control quotation
  const saveCustomProject = async () => {
    if (customQuotation.projectName.trim() && customQuotation.clientName.trim() && (customQuotation.items.length > 0 || customQuotation.services.length > 0)) {
      const currentUser = simpleAuth.getCurrentUser();
      if (!currentUser) {
        toast.error('Please log in to save projects.');
        return;
      }

      try {
        const projectData = {
          ...(loadedProjectId ? { id: loadedProjectId } : {}),
          clientName: customQuotation.clientName,
          projectName: customQuotation.projectName,
          equipment: [],
          currency: customQuotation.currency,
          customEquipment: customQuotation.items.map(item => ({
            name: `${item.name} (x${Number(item.quantity) || 0})`,
            price: (Number(item.clientPrice) || 0) * (Number(item.quantity) || 0),
            weight: 0 // Noise control items don't have weight
          })),
          customServices: customQuotation.services.map(s => ({
            name: s.name,
            price: Number(s.price) || 0
          })),
          globalDiscount: customQuotation.globalDiscount,
          includeTax: customQuotation.includeTax,
          terms: customQuotation.terms,
          services: {
            commissioning: false,
            noiseControl: false,
            soundDesign: false
          },
          roles: {
            producer: '',
            director: '',
            projectManager: '',
            juniorProjectManager: '',
            accountant: '',
            logisticsManager: '',
            noiseControlEngineer: '',
            soundSystemDesigner: ''
          },
          total: ncResults ? ncResults.total : 0,
          isCalculated: ncCalculated,
          calculationResults: ncResults,
          projectType: 'custom'
        };

        const response = await supabaseService.saveProject(projectData);

        if (response.success) {
          setLoadedProjectId(response.project?.id ?? loadedProjectId);
          // Reload projects from backend to get updated list
          await loadSavedProjects();

          setShowNcSaveModal(false);
          toast.success(response.message || (loadedProjectId ? 'Quotation updated successfully!' : 'Custom project saved successfully!'));
        }
      } catch (error) {
        console.error('Failed to save custom project:', error);
        toast.error('Failed to save project. Please try again.');
      }
    } else {
      toast.error('Please ensure project has a name, client name, and at least one item or service before saving.');
    }
  };

  // Function to load saved project
  const loadSavedProject = (savedProject) => {
    setLoadedProjectId(savedProject.id || null);
    // Check if this is a noise control project
    if (savedProject.projectType === 'custom') {
      // Load noise control project
      const customItems = (savedProject.customEquipment || []).map(item => {
        // Extract quantity from name if it was saved with quantity in parentheses
        const quantityMatch = item.name.match(/\(x(\d+)\)$/);
        const quantity = quantityMatch ? parseInt(quantityMatch[1]) : 1;
        const name = quantityMatch ? item.name.replace(/\s*\(x\d+\)$/, '') : item.name;

        return {
          name: name,
          cost: 0, // We don't store cost separately for loaded projects
          clientPrice: (Number(item.price) || 0) / (quantity || 1), // Calculate unit price
          quantity: quantity
        };
      });

      setCustomQuotation({
        clientName: savedProject.clientName || '',
        projectName: savedProject.projectName || '',
        currency: savedProject.currency || 'JOD',
        items: customItems,
        services: (savedProject.customServices || []).map(s => ({
          name: s.name || '',
          price: String(s.price || '')
        })),
        globalDiscount: savedProject.globalDiscount || 0,
        includeTax: savedProject.includeTax !== undefined ? savedProject.includeTax : true,
        terms: savedProject.terms || `All prices are in Jordanian Dinars (JOD)
Equipment prices include door-to-door delivery
VAT is calculated at 16% as per Jordanian tax regulations
Subject to ±10% change after technical study
Payment terms: 90% down payment, 10% after project completion
This quotation is valid for 30 days from the date of issue`
      });

      if (savedProject.calculationResults) {
        setNcCalculated(true);
        const loadedResults = { ...savedProject.calculationResults };
        if (loadedResults.items) {
          loadedResults.items = loadedResults.items.map(item => ({
            ...item,
            clientPrice: Number(item.clientPrice) || 0,
            cost: Number(item.cost) || 0,
            quantity: Number(item.quantity) || 0
          }));
        }
        setNcResults(loadedResults);
      } else {
        setNcCalculated(false);
        setNcResults(null);
      }

      setActiveTab('custom');
      toast.success('Custom project loaded successfully!');
    } else {
      // Load sound design project (default behavior)
      setProject({
        clientName: savedProject.clientName || '',
        projectName: savedProject.projectName || '',
        equipment: savedProject.equipment || [],
        globalDiscount: savedProject.globalDiscount || 0,
        services: savedProject.services ? {
          commissioning: savedProject.services.commissioning?.enabled !== undefined
            ? savedProject.services.commissioning
            : { enabled: savedProject.services.commissioning || false, customValue: 0 },
          noiseControl: savedProject.services.noiseControl?.enabled !== undefined
            ? savedProject.services.noiseControl
            : { enabled: savedProject.services.noiseControl || false, customValue: 0 },
          soundDesign: savedProject.services.soundDesign?.enabled !== undefined
            ? savedProject.services.soundDesign
            : { enabled: savedProject.services.soundDesign || false, customValue: 0 }
        } : {
          commissioning: { enabled: false, customValue: 0 },
          noiseControl: { enabled: false, customValue: 0 },
          soundDesign: { enabled: false, customValue: 0 }
        },
        customServices: savedProject.customServices || [],
        customEquipment: savedProject.customEquipment || [],
        roles: savedProject.roles || { producer: '', projectManager: '' },
        includeTax: savedProject.includeTax !== undefined ? savedProject.includeTax : true,
        terms: savedProject.terms || project.terms
      });
      if (savedProject.hasCalculation) {
        setIsCalculated(true);
        setCalculationResults(savedProject.calculationResults || null);
      } else {
        setIsCalculated(false);
        setCalculationResults(null);
      }
      setActiveTab('quotation');
      toast.success('Project loaded successfully!');
    }
  };

  // Function to delete saved project
  const deleteSavedProject = async (projectId) => {
    const currentUser = simpleAuth.getCurrentUser();
    if (!currentUser) {
      toast.error('Please log in to delete projects.');
      return;
    }

    try {
      const response = await supabaseService.deleteProject(projectId);
      if (response.success) {
        if (projectId === loadedProjectId) setLoadedProjectId(null);
        // Reload projects from backend to get updated list
        await loadSavedProjects();
        toast.success('Project deleted successfully!');
      }
    } catch (error) {
      console.error('Failed to delete project:', error);
      toast.error('Failed to delete project. Please try again.');
    }
  };

  const startNewQuotation = () => {
    setLoadedProjectId(null);
    setProject({
      clientName: '',
      projectName: '',
      equipment: [],
      globalDiscount: 0,
      services: {
        commissioning: { enabled: false, customValue: 0 },
        noiseControl: { enabled: false, customValue: 0 },
        soundDesign: { enabled: false, customValue: 0 },
      },
      customServices: [],
      customEquipment: [],
      includeTax: true,
      terms: `All prices are in Jordanian Dinars (JOD)
Equipment prices include door-to-door delivery
VAT is calculated at 16% as per Jordanian tax regulations
Subject to ±10% change after technical study
Payment terms: 90% down payment, 10% after project completion
This quotation is valid for 30 days from the date of issue`,
      roles: {
        producer: '',
        director: '',
        projectManager: '',
        juniorProjectManager: '',
        accountant: '',
        logisticsManager: '',
        noiseControlEngineer: '',
        soundSystemDesigner: ''
      }
    });
    setIsCalculated(false);
    setCalculationResults(null);
  };

  const startNewCustomQuotation = () => {
    setLoadedProjectId(null);
    setCustomQuotation({
      clientName: '',
      projectName: '',
      currency: 'JOD',
      items: [],
      services: [],
      globalDiscount: 0,
      includeTax: true,
      terms: `All prices are in Jordanian Dinars (JOD)
Equipment prices include door-to-door delivery
VAT is calculated at 16% as per Jordanian tax regulations
Subject to ±10% change after technical study
Payment terms: 90% down payment, 10% after project completion
This quotation is valid for 30 days from the date of issue`
    });
    setNcCalculated(false);
    setNcResults(null);
  };

  // ── Rental Quotation Calculator ────────────────────────────────────────────

  const calculateRental = () => {
    const riderItems = rentalQuotation.riderItems || [];
    if (rentalQuotation.items.length === 0 && riderItems.length === 0 && rentalQuotation.services.length === 0) {
      toast.error('Please add at least one item or service');
      return;
    }
    let totalCost = 0;
    let totalRevenueBeforeDiscount = 0;
    [...rentalQuotation.items, ...riderItems].forEach(item => {
      totalCost += (Number(item.cost) || 0) * (Number(item.quantity) || 0);
      totalRevenueBeforeDiscount += (Number(item.clientPrice) || 0) * (Number(item.quantity) || 0);
    });
    const validServices = rentalQuotation.services.filter(s => s.name.trim() || Number(s.price) > 0);
    validServices.forEach(s => { totalRevenueBeforeDiscount += Number(s.price) || 0; });
    const discountAmount = totalRevenueBeforeDiscount * (rentalQuotation.globalDiscount / 100);
    const subtotal = totalRevenueBeforeDiscount - discountAmount;
    const tax = rentalQuotation.includeTax ? subtotal * 0.16 : 0;
    const total = subtotal + tax;
    const profit = subtotal - totalCost;
    const profitMargin = subtotal > 0 ? (profit / subtotal) * 100 : 0;
    const normalizeItem = item => ({
      ...item,
      clientPrice: Number(item.clientPrice) || 0,
      cost: Number(item.cost) || 0,
      quantity: Number(item.quantity) || 0,
    });
    setRentalResults({
      items: rentalQuotation.items.map(normalizeItem),
      riderItems: riderItems.map(normalizeItem),
      services: validServices.map(s => ({ name: s.name, price: Number(s.price) || 0 })),
      totalCost, totalRevenueBeforeDiscount, discountAmount, subtotal, tax, total, profit, profitMargin,
    });
    setRentalCalculated(true);
    toast.success('Rental quotation calculated');
  };

  const saveRentalProject = async () => {
    if (!rentalQuotation.projectName.trim() || !rentalQuotation.clientName.trim()) {
      toast.error('Please enter a project name and client name before saving.');
      return;
    }
    if (rentalQuotation.items.length === 0 && (rentalQuotation.riderItems || []).length === 0 && rentalQuotation.services.length === 0) {
      toast.error('Please add at least one item or service before saving.');
      return;
    }
    // Full item fields persisted; `price` (line total) kept for legacy consumers.
    const toSavedItem = item => ({
      name: item.name,
      cost: Number(item.cost) || 0,
      clientPrice: Number(item.clientPrice) || 0,
      quantity: Number(item.quantity) || 0,
      price: (Number(item.clientPrice) || 0) * (Number(item.quantity) || 0),
    });
    try {
      const result = await rentalQuotationsService.saveRentalQuotation({
        ...(loadedRentalProjectId ? { id: loadedRentalProjectId } : {}),
        clientName: rentalQuotation.clientName,
        projectName: rentalQuotation.projectName,
        currency: rentalQuotation.currency,
        customEquipment: rentalQuotation.items.map(toSavedItem),
        technicalRider: (rentalQuotation.riderItems || []).map(toSavedItem),
        customServices: rentalQuotation.services.map(s => ({ name: s.name, price: Number(s.price) || 0 })),
        globalDiscount: rentalQuotation.globalDiscount,
        includeTax: rentalQuotation.includeTax,
        terms: rentalQuotation.terms,
        total: rentalResults ? rentalResults.total : 0,
        isCalculated: rentalCalculated,
        calculationResults: rentalResults,
      });
      setLoadedRentalProjectId(result.id || loadedRentalProjectId);
      await loadSavedRentalProjects();
      setShowRentalSaveModal(false);
      toast.success(result.message || 'Rental quotation saved!');
    } catch (err) {
      console.error('Failed to save rental project:', err);
      toast.error('Failed to save rental quotation. Please try again.');
    }
  };

  const loadRentalProject = (saved) => {
    setLoadedRentalProjectId(saved.id || null);
    const fromSavedItem = item => {
      if (item.clientPrice !== undefined) {
        // New shape: full fields incl. cost were persisted.
        return { name: item.name, cost: Number(item.cost) || 0, clientPrice: Number(item.clientPrice) || 0, quantity: Number(item.quantity) || 0 };
      }
      // Legacy shape: qty baked into name, price is line total, cost was never saved.
      const match = item.name.match(/\(x(\d+)\)$/);
      const qty = match ? parseInt(match[1]) : 1;
      const name = match ? item.name.replace(/\s*\(x\d+\)$/, '') : item.name;
      return { name, cost: 0, clientPrice: (Number(item.price) || 0) / (qty || 1), quantity: qty };
    };
    setRentalQuotation({
      clientName: saved.clientName || '',
      projectName: saved.projectName || '',
      currency: saved.currency || 'JOD',
      items: (saved.customEquipment || []).map(fromSavedItem),
      riderItems: (saved.technicalRider || []).map(fromSavedItem),
      services: (saved.customServices || []).map(s => ({ name: s.name || '', price: String(s.price || '') })),
      globalDiscount: saved.globalDiscount || 0,
      includeTax: saved.includeTax !== undefined ? saved.includeTax : true,
      terms: saved.terms || RENTAL_DEFAULT_TERMS,
    });
    if (saved.calculationResults) {
      setRentalCalculated(true);
      setRentalResults(saved.calculationResults);
    } else {
      setRentalCalculated(false);
      setRentalResults(null);
    }
    setRentalTab('quotation');
    toast.success('Rental quotation loaded!');
  };

  const deleteRentalProject = async (id) => {
    if (!window.confirm('Delete this rental quotation?')) return;
    try {
      await rentalQuotationsService.deleteRentalQuotation(id);
      if (id === loadedRentalProjectId) setLoadedRentalProjectId(null);
      await loadSavedRentalProjects();
      toast.success('Rental quotation deleted.');
    } catch (err) {
      toast.error('Failed to delete rental quotation.');
    }
  };

  const startNewRentalQuotation = () => {
    setLoadedRentalProjectId(null);
    setRentalQuotation({ clientName: '', projectName: '', currency: 'JOD', items: [], riderItems: [], services: [], globalDiscount: 0, includeTax: true, terms: RENTAL_DEFAULT_TERMS });
    setRentalCalculated(false);
    setRentalResults(null);
  };

  const addRentalItem = () => {
    setRentalQuotation(prev => ({ ...prev, items: [...prev.items, { name: '', cost: '', clientPrice: '', quantity: '' }] }));
    setRentalCalculated(false);
  };

  const addRentalRiderItem = () => {
    setRentalQuotation(prev => ({ ...prev, riderItems: [...(prev.riderItems || []), { name: '', cost: '', clientPrice: '', quantity: '' }] }));
    setRentalCalculated(false);
  };

  const setRentalListItem = (listKey, index, field, value) => {
    setRentalQuotation(prev => ({ ...prev, [listKey]: prev[listKey].map((it, i) => i === index ? { ...it, [field]: value } : it) }));
    setRentalCalculated(false);
  };

  const removeRentalListItem = (listKey, index) => {
    setRentalQuotation(prev => ({ ...prev, [listKey]: prev[listKey].filter((_, i) => i !== index) }));
    setRentalCalculated(false);
  };

  // Shared row renderer for BOQ and Technical Rider item lists.
  const renderRentalItemRows = (listKey, list) => (
    <div className="space-y-3">
      {list.map((item, index) => (
        <div key={index} className="grid grid-cols-1 md:grid-cols-5 gap-3 items-center p-4 bg-gray-50 rounded-lg">
          <input type="text" placeholder="Item name / description" value={item.name}
            onChange={e => setRentalListItem(listKey, index, 'name', e.target.value)}
            className="md:col-span-2 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-black focus:border-black" />
          <input type="number" placeholder="Cost price" value={item.cost}
            onChange={e => setRentalListItem(listKey, index, 'cost', e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-black focus:border-black" />
          <input type="number" placeholder="Rental price" value={item.clientPrice}
            onChange={e => setRentalListItem(listKey, index, 'clientPrice', e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-black focus:border-black" />
          <div className="flex items-center space-x-2">
            <input type="number" placeholder="Quantity" value={item.quantity}
              onChange={e => setRentalListItem(listKey, index, 'quantity', e.target.value)}
              className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-black focus:border-black" />
            <button onClick={() => removeRentalListItem(listKey, index)}
              className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"><Trash2 size={16} /></button>
          </div>
        </div>
      ))}
    </div>
  );

  const addRentalService = () => {
    setRentalQuotation(prev => ({ ...prev, services: [...prev.services, { name: '', price: '' }] }));
    setRentalCalculated(false);
  };

  const downloadRentalQuotationPDF = () => {
    if (!rentalCalculated || !rentalResults) { toast.error('Please calculate the quotation first'); return; }
    const quoteNum = `RNT-${Date.now().toString().slice(-6)}`;
    const dateStr = new Date().toLocaleDateString('en-GB');
    const currency = rentalQuotation.currency || 'JOD';
    const win = window.open('', '_blank');
    win.document.write(`<!DOCTYPE html><html><head><title>Rental Quotation - ${rentalQuotation.projectName || 'Project'}</title>
<style>
@media print { @page { margin:0.5in 0.5in 0.75in 0.5in; } body{margin:0;} }
body{font-family:Arial,sans-serif;margin:20px;font-size:12px;}
.header{border-bottom:2px solid #000;padding-bottom:10px;margin-bottom:20px;}
.company-info{font-size:11px;}
table{width:100%;border-collapse:collapse;margin-bottom:15px;font-size:11px;}
th,td{border:1px solid #ddd;padding:6px;text-align:left;}
th{background:#f2f2f2;}
.totals-row{display:flex;justify-content:space-between;margin-bottom:3px;}
.total-final{font-weight:bold;font-size:13px;border-top:2px solid #000;padding-top:8px;}
.terms{margin-top:15px;font-size:10px;color:#555;border-top:1px solid #eee;padding-top:10px;white-space:pre-line;}
.badge{display:inline-block;background:#111827;color:#fff;padding:2px 8px;border-radius:4px;font-size:10px;margin-bottom:8px;}
</style></head><body>
<div class="header"><div style="display:flex;justify-content:space-between;align-items:flex-start;">
<div><img src="${logoImage}" alt="Logo" style="width:60px;height:60px;object-fit:contain;display:block;margin-bottom:6px;" onerror="this.style.display='none'"/>
<div class="company-info"><strong>Deep Sound For Technical Consultation LLC</strong><br/>Housing Bank Complex 93 - Ground Floor 102<br/>Q. Nour St. - Welbdeh - Amman - Jordan<br/>Phone: +962 (0) 795144821<br/>Website: www.nogahub.com</div></div>
<div style="text-align:right;"><h1 style="margin:0 0 6px;font-size:1.8em;letter-spacing:2px;">RENTAL QUOTATION</h1>
<div class="badge">RENTAL</div>
<div style="font-size:11px;margin-top:4px;"><strong>Quote No.:</strong> ${quoteNum}<br/><strong>Date:</strong> ${dateStr}<br/><strong>Valid for:</strong> 14 days</div></div></div></div>
<div style="margin-bottom:15px;font-size:11px;"><strong>Client:</strong> ${rentalQuotation.clientName || 'Client Name'}<br/><strong>Project:</strong> ${rentalQuotation.projectName || 'Project Name'}</div>
<table><thead><tr><th>Description</th><th>Qty</th><th>Unit Price (${currency})</th><th>Total (${currency})</th></tr></thead><tbody>
${[...rentalResults.items, ...(rentalResults.riderItems||[])].map(i => `<tr><td>${i.name}</td><td>${i.quantity}</td><td>${(Number(i.clientPrice)||0).toFixed(1)}</td><td>${((Number(i.clientPrice)||0)*(Number(i.quantity)||0)).toFixed(1)}</td></tr>`).join('')}
${(rentalResults.services||[]).filter(s=>s.name&&Number(s.price)>0).map(s=>`<tr><td>${s.name}</td><td>1</td><td>${(Number(s.price)||0).toFixed(1)}</td><td>${(Number(s.price)||0).toFixed(1)}</td></tr>`).join('')}
</tbody></table>
<div>
<div class="totals-row"><span>Subtotal:</span><span>${(rentalResults.totalRevenueBeforeDiscount||0).toFixed(1)} ${currency}</span></div>
${rentalQuotation.globalDiscount>0?`<div class="totals-row" style="color:red;"><span>Discount:</span><span>-${(rentalResults.discountAmount||0).toFixed(1)} ${currency}</span></div>`:''}
${rentalQuotation.globalDiscount>0&&rentalQuotation.includeTax?`<div class="totals-row"><span>Subtotal:</span><span>${(rentalResults.subtotal||0).toFixed(1)} ${currency}</span></div>`:''}
${rentalQuotation.includeTax?`<div class="totals-row"><span>VAT (16%):</span><span>${(rentalResults.tax||0).toFixed(1)} ${currency}</span></div>`:''}
<div class="totals-row total-final"><span>Total:</span><span>${(rentalResults.total||0).toFixed(1)} ${currency}</span></div>
</div>
${rentalQuotation.terms?`<div class="terms">${rentalQuotation.terms}</div>`:''}
</body></html>`);
    win.document.close();
    win.focus();
    setTimeout(() => { win.print(); win.close(); }, 300);
  };

  const downloadRentalInvoicePDF = () => {
    if (!rentalCalculated || !rentalResults) { toast.error('Please calculate the quotation first'); return; }
    const invoiceNum = `RINV-${Date.now().toString().slice(-6)}`;
    const dateStr = new Date().toLocaleDateString('en-GB');
    const currency = rentalQuotation.currency || 'JOD';
    const win = window.open('', '_blank');
    win.document.write(`<!DOCTYPE html><html><head><title>Rental Invoice - ${rentalQuotation.projectName || 'Project'}</title>
<style>
@media print { @page { margin:0.5in 0.5in 0.75in 0.5in; } body{margin:0;} }
body{font-family:Arial,sans-serif;margin:20px;font-size:12px;}
.header{border-bottom:2px solid #000;padding-bottom:10px;margin-bottom:20px;}
table{width:100%;border-collapse:collapse;margin-bottom:15px;font-size:11px;}
th,td{border:1px solid #ddd;padding:6px;text-align:left;}
th{background:#f2f2f2;}
.totals-row{display:flex;justify-content:space-between;margin-bottom:3px;}
.total-final{font-weight:bold;font-size:13px;border-top:2px solid #000;padding-top:8px;}
.payment-box{margin-top:20px;border:1px solid #ddd;padding:12px;font-size:11px;background:#f9fafb;}
</style></head><body>
<div class="header"><div style="display:flex;justify-content:space-between;align-items:flex-start;">
<div><img src="${logoImage}" alt="Logo" style="width:60px;height:60px;object-fit:contain;display:block;margin-bottom:6px;" onerror="this.style.display='none'"/>
<div style="font-size:11px;"><strong>Deep Sound For Technical Consultation LLC</strong><br/>Housing Bank Complex 93 - Ground Floor 102<br/>Q. Nour St. - Welbdeh - Amman - Jordan<br/>Tax Reg No: 40261328</div></div>
<div style="text-align:right;"><h1 style="margin:0 0 6px;font-size:1.8em;letter-spacing:2px;">INVOICE</h1>
<div style="font-size:11px;"><strong>Invoice No.:</strong> ${invoiceNum}<br/><strong>Date:</strong> ${dateStr}<br/><strong>Due:</strong> Due on receipt</div></div></div></div>
<div style="margin-bottom:15px;font-size:11px;"><strong>Bill To:</strong><br/>${rentalQuotation.clientName||'Client Name'}<br/>${rentalQuotation.projectName||'Project Name'}</div>
<table><thead><tr><th>Description</th><th>Qty</th><th>Unit Price (${currency})</th><th>Total (${currency})</th></tr></thead><tbody>
${[...rentalResults.items, ...(rentalResults.riderItems||[])].map(i=>`<tr><td>${i.name}</td><td>${i.quantity}</td><td>${(Number(i.clientPrice)||0).toFixed(2)}</td><td>${((Number(i.clientPrice)||0)*(Number(i.quantity)||0)).toFixed(2)}</td></tr>`).join('')}
${(rentalResults.services||[]).filter(s=>s.name&&Number(s.price)>0).map(s=>`<tr><td>${s.name}</td><td>1</td><td>${(Number(s.price)||0).toFixed(2)}</td><td>${(Number(s.price)||0).toFixed(2)}</td></tr>`).join('')}
</tbody></table>
<div>
${rentalQuotation.globalDiscount>0?`<div class="totals-row"><span>Subtotal (before discount):</span><span>${(rentalResults.totalRevenueBeforeDiscount||0).toFixed(2)} ${currency}</span></div><div class="totals-row" style="color:red;"><span>Discount (${rentalQuotation.globalDiscount}%):</span><span>-${(rentalResults.discountAmount||0).toFixed(2)} ${currency}</span></div>`:''}
<div class="totals-row"><span>Subtotal:</span><span>${(rentalResults.subtotal||0).toFixed(2)} ${currency}</span></div>
${rentalQuotation.includeTax?`<div class="totals-row"><span>VAT (16%):</span><span>${(rentalResults.tax||0).toFixed(2)} ${currency}</span></div>`:''}
<div class="totals-row total-final"><span>Total Due:</span><span>${(rentalResults.total||0).toFixed(2)} ${currency}</span></div>
</div>
<div class="payment-box"><h4 style="margin:0 0 8px;">Payment Details</h4>
<div><strong>Bank:</strong> Etihad Bank &nbsp;|&nbsp; <strong>CliQ:</strong> Deepsound</div>
<div><strong>IBAN:</strong> JO37UBSI1200000310179526015101</div></div>
</body></html>`);
    win.document.close();
    win.focus();
    setTimeout(() => { win.print(); win.close(); }, 300);
  };

  // Business entities structure based on the report
  const businessEntities = {
    nogahub: {
      name: "Nogahub",
      ownership: {
        nadeem: 0.40,
        issa: 0.40,
        wewealth: 0.20  // Being liquidated
      },
      services: ["Sound system design", "Acoustic consultancy", "Noise control studies"]
    },
    voidAcoustics: {
      name: "Void Acoustics Jordan",
      ownership: {
        nadeem: 0.55,
        issa: 0.225,
        omar: 0.225
      },
      services: ["Equipment sales", "Exclusive Void dealership"]
    },
    avecion: {
      name: "Avecion",
      ownership: {
        nadeem: 0.40,
        issa: 0.40,
        nogahub: 0.20
      },
      services: ["Web3 sound research", "Urban sound management"]
    }
  };

  // Authentication functions (using the auth hook)
  const handleLogin = async () => {
    console.log('🔐 Login button clicked (using useAuth hook)');
    await authHandleLogin();
    // Data loading is handled by the useEffect above when isAuthenticated changes
  };

  const handleLogout = async () => {
    await authHandleLogout();
    // Clear app state
    setEquipmentDatabase([]);
    setSavedProjects([]);
  };

  // Constants from centralized config
  const exchangeRate = BUSINESS_CONSTANTS.EXCHANGE_RATE_USD_TO_JOD;
  const shippingRatePerKg = BUSINESS_CONSTANTS.SHIPPING_RATE_PER_KG;

  // Service pricing from centralized config
  const servicePricing = useMemo(() => ({
    commissioning: SERVICE_PRICING.COMMISSIONING,
    noiseControl: SERVICE_PRICING.NOISE_CONTROL,
    soundDesign: SERVICE_PRICING.SOUND_DESIGN,
    projectManagement: SERVICE_PRICING.PROJECT_MANAGEMENT
  }), []);

   // Updated calculation based on new logic - v2
  const calculateProjectCosts = useCallback(() => {
    if (project.equipment.length === 0 && project.customEquipment.length === 0) return null;

    // Step 1: First pass - calculate basic equipment totals
    let equipmentDealerTotalJOD = 0;
    let equipmentClientTotalJOD = 0;
    let totalWeight = 0;

    // First pass: calculate totals without shipping/customs
    project.equipment.forEach(item => {
      const equipment = equipmentDatabase.find(eq => eq.code === item.code);
      if (equipment && item.quantity > 0) {
        const dealerPriceJOD = equipment.dealerUSD * exchangeRate;
        const clientPriceJOD = (equipment.msrpUSD || equipment.dealerUSD) * exchangeRate;
        const dealerTotalJOD = dealerPriceJOD * item.quantity;
        const clientTotalJOD = clientPriceJOD * item.quantity;
        const weightTotal = equipment.weight * item.quantity;

        equipmentDealerTotalJOD += dealerTotalJOD;
        equipmentClientTotalJOD += clientTotalJOD;
        totalWeight += weightTotal;
      }
    });

    // First pass over shipped custom equipment — pools into the same shipping/customs base
    let customShippedCostTotalJOD = 0;
    let customShippedWeight = 0;
    project.customEquipment.forEach(equipment => {
      const qty = Number(equipment.weight) || 0;
      const cost = Number(equipment.cost) || 0;
      if (equipment.shipped && equipment.name && qty > 0 && equipment.shipWeight > 0) {
        customShippedCostTotalJOD += cost * qty;
        customShippedWeight += Number(equipment.shipWeight) * qty;
      }
    });
    const voidWeight = totalWeight; // Void's own weight, before pooling in shipped custom items
    totalWeight += customShippedWeight;

    // Step 2: Calculate door-to-door cost for internal use (keeping old logic for profit calculations)
    const shippingCost = totalWeight * shippingRatePerKg;
    const clearanceCost = SHIPPING_CUSTOMS.CLEARANCE_COST;
    const transportCost = SHIPPING_CUSTOMS.TRANSPORT_COST;
    const deliveryOrderCost = SHIPPING_CUSTOMS.DELIVERY_ORDER_COST;
    const totalShippingCost = shippingCost + clearanceCost + transportCost + deliveryOrderCost;

    // Import base = catalog dealer cost + shipped custom equipment cost
    const importBaseJOD = equipmentDealerTotalJOD + customShippedCostTotalJOD;

    const taxableAmount = importBaseJOD + totalShippingCost;
    const tax001 = taxableAmount * TAX_RATES.TAX_001;
    const tax020 = (taxableAmount + tax001) * TAX_RATES.TAX_020;
    const tax215 = importBaseJOD * TAX_RATES.TAX_215;
    const tax301 = TAX_RATES.TAX_301;
    const tax111 = shippingCost * TAX_RATES.TAX_111;
    const tax016 = TAX_RATES.TAX_016;
    const tax019 = TAX_RATES.TAX_019;
    const tax070 = taxableAmount * TAX_RATES.TAX_070;

    const totalCustoms = tax001 + tax020 + tax215 + tax301 + tax111 + tax016 + tax019 + tax070;
    const totalCustomsExclTax020 = tax001 + tax215 + tax301 + tax111 + tax016 + tax019 + tax070;

    // Door-to-door cost including tax020 (for internal calculations)
    const doorToDoorCostJOD = importBaseJOD + totalShippingCost + totalCustoms;

    // Door-to-door cost excluding tax020 (for final equipment pricing to avoid tax duplication)
    const doorToDoorCostExclTax020JOD = importBaseJOD + totalShippingCost + totalCustomsExclTax020;

    // Step 3: the flat clearance/transport/DO fees (one bill per shipment) are split between
    // Void and shipped custom equipment by weight share — Void alone ships → Void carries the
    // full 150; nothing shipped on the custom side → custom carries none; mixed shipment → split
    // proportionally. Void's per-kg weight-based freight itself is never diluted (each side pays
    // its own weight × rate), only the flat fee pool is shared.
    const fixedShippingFeesJOD = clearanceCost + transportCost + deliveryOrderCost;
    const voidFixedFeeShareJOD = totalWeight > 0 ? fixedShippingFeesJOD * (voidWeight / totalWeight) : 0;
    const shippingCostVoidOnly = voidWeight * shippingRatePerKg;
    const totalShippingCostVoidOnly = shippingCostVoidOnly + voidFixedFeeShareJOD;
    const taxableAmountVoidOnly = equipmentDealerTotalJOD + totalShippingCostVoidOnly;
    const tax001VoidOnly = taxableAmountVoidOnly * TAX_RATES.TAX_001;
    const tax215VoidOnly = equipmentDealerTotalJOD * TAX_RATES.TAX_215;
    const tax111VoidOnly = shippingCostVoidOnly * TAX_RATES.TAX_111;
    const tax070VoidOnly = taxableAmountVoidOnly * TAX_RATES.TAX_070;
    const totalCustomsExclTax020VoidOnly = tax001VoidOnly + tax215VoidOnly + tax301 + tax111VoidOnly + tax016 + tax019 + tax070VoidOnly;

    // ponytail: guard divide-by-zero when weight/dealer base is 0 (e.g. custom-only project)
    const shippingPerKg = voidWeight > 0 ? totalShippingCostVoidOnly / voidWeight : 0;
    const customsShare = equipmentDealerTotalJOD > 0 ? totalCustomsExclTax020VoidOnly / equipmentDealerTotalJOD : 0;

    const voidShippingJOD = totalShippingCostVoidOnly;
    const voidCustomsJOD = totalCustomsExclTax020VoidOnly;
    const voidPassThroughJOD = voidShippingJOD + voidCustomsJOD;

    // Custom's shipping = its own weight × rate + its weight-share of the flat fees (falls out of
    // the subtraction below since voidFixedFeeShareJOD + customFixedFeeShareJOD === fixedShippingFeesJOD).
    // Customs/duty marginal stays value-share based (see customsShareCustom).
    const customShippedShippingMarginalJOD = totalShippingCost - totalShippingCostVoidOnly;
    const customShippedCustomsMarginalJOD = totalCustomsExclTax020 - totalCustomsExclTax020VoidOnly;
    const shippingPerKgCustom = customShippedWeight > 0 ? customShippedShippingMarginalJOD / customShippedWeight : 0;
    const customsShareCustom = customShippedCostTotalJOD > 0 ? customShippedCustomsMarginalJOD / customShippedCostTotalJOD : 0;

    // Step 4: Second pass - calculate final equipment details with shipping/customs
    const equipmentDetails = project.equipment.map(item => {
      const equipment = equipmentDatabase.find(eq => eq.code === item.code);
      if (equipment && item.quantity > 0) {
        // Convert to JOD
        const dealerPriceJOD = equipment.dealerUSD * exchangeRate;
        const clientPriceJOD = (equipment.msrpUSD || equipment.dealerUSD) * exchangeRate;
        // Use MSRP if available, otherwise fall back to dealerUSD
        const msrpPriceJOD = (equipment.msrpUSD || equipment.dealerUSD) * exchangeRate;

        // Calculate shipping cost per unit (item's own weight × per-kg rate)
        const shippingPerUnit = equipment.weight * shippingPerKg;

        // Calculate customs/clearance per unit (dealer cost × customs share)
        const customsPerUnit = dealerPriceJOD * customsShare;

        // Calculate final unit price (MSRP price + shipping + customs) - BOQ should be above MSRP
        // Round to displayed precision first so line total = displayed unit price × quantity exactly
        const finalUnitPriceJOD = Math.round((msrpPriceJOD + shippingPerUnit + customsPerUnit) * 10) / 10;

        // Calculate total for this item (rounded unit price × quantity)
        const finalTotalJOD = Math.round(finalUnitPriceJOD * item.quantity * 10) / 10;

        // Calculate totals for other purposes
        const dealerTotalJOD = dealerPriceJOD * item.quantity;
        const clientTotalJOD = clientPriceJOD * item.quantity;
        const weightTotal = equipment.weight * item.quantity;

        return {
          ...equipment,
          quantity: item.quantity,
          dealerPriceJOD: dealerPriceJOD,
          dealerPriceUSD: equipment.dealerUSD,
          clientPriceJOD: clientPriceJOD,
          dealerTotalJOD: dealerTotalJOD,
          clientTotalJOD: clientTotalJOD,
          weightTotal: weightTotal,
          shippingPerUnit: shippingPerUnit,
          customsPerUnit: customsPerUnit,
          finalUnitPriceJOD: finalUnitPriceJOD,
          finalTotalJOD: finalTotalJOD
        };
      }
      return null;
    }).filter(Boolean);

    // Step 5: Calculate totals without discount for BOQ
    const equipmentTotalJODBeforeDiscount = equipmentDetails.reduce((sum, item) => sum + item.finalTotalJOD, 0);
    // Keep original pricing for BOQ display (no discount applied)
    const equipmentDetailsFinal = equipmentDetails;
    
    // Apply global discount only for project summary calculations
    // Discount applies to the MSRP component only — shipping is weight-based and
    // customs/import taxes are assessed on the dealer import value, so neither
    // shrinks when the client gets a discount (logistics stay a true 0-margin pass-through)
    const globalDiscountMultiplier = (100 - project.globalDiscount) / 100;
    const equipmentTotalJOD = equipmentClientTotalJOD * globalDiscountMultiplier + voidPassThroughJOD;
    
    // Step 5.1: Process custom equipment — no discount applied; shipped items get pooled
    // shipping (weight-based) and customs (cost-based) share, same rates as catalog equipment.
    // Local items stay a straight price/cost line with no logistics.
    const customEquipmentDetails = project.customEquipment.map((equipment, index) => {
      const qty = Number(equipment.weight) || 0; // "weight" field doubles as quantity
      const price = Number(equipment.price) || 0;
      const cost = Number(equipment.cost) || price; // fallback preserves old zero-margin behavior
      if (equipment.name && price > 0 && qty > 0) {
        const isShipped = !!equipment.shipped && Number(equipment.shipWeight) > 0;
        const shippingPerUnit = isShipped ? Number(equipment.shipWeight) * shippingPerKgCustom : 0;
        const customsPerUnit = isShipped ? cost * customsShareCustom : 0;
        const weightTotal = isShipped ? Number(equipment.shipWeight) * qty : 0;

        // Shipped items pass shipping + customs through to the client on top of the entered
        // price (0-margin pass-through, same model as catalog equipment) — the client total
        // rises when an item is marked shipped instead of quietly eating into margin.
        const finalUnitPriceJOD = Math.round((price + shippingPerUnit + customsPerUnit) * 10) / 10;
        const finalTotalJOD = Math.round(finalUnitPriceJOD * qty * 10) / 10;

        return {
          code: `CUSTOM-${index + 1}`,
          name: equipment.name,
          quantity: qty,
          dealerPriceJOD: cost,
          clientPriceJOD: price,
          dealerTotalJOD: cost * qty,
          clientTotalJOD: price * qty,
          weightTotal,
          shippingPerUnit,
          customsPerUnit,
          finalUnitPriceJOD,
          finalTotalJOD,
          weight: isShipped ? Number(equipment.shipWeight) : 0,
          category: "custom",
          shipped: isShipped
        };
      }
      return null;
    }).filter(Boolean);

    // Calculate custom equipment totals — split by shipped vs local
    const customEquipmentTotalJOD = customEquipmentDetails.reduce((sum, item) => sum + item.finalTotalJOD, 0);
    const customShippedItems = customEquipmentDetails.filter(item => item.shipped);
    const customLocalItems = customEquipmentDetails.filter(item => !item.shipped);
    const customShippedRevenueJOD = customShippedItems.reduce((sum, item) => sum + item.finalTotalJOD, 0);
    const customShippedPriceOnlyJOD = customShippedItems.reduce((sum, item) => sum + item.clientTotalJOD, 0);
    const customShippedCostAllocatedJOD = customShippedItems.reduce((sum, item) => sum + item.dealerTotalJOD, 0);
    const customShippedShippingJOD = customShippedItems.reduce((sum, item) => sum + item.shippingPerUnit * item.quantity, 0);
    const customShippedCustomsJOD = customShippedItems.reduce((sum, item) => sum + item.customsPerUnit * item.quantity, 0);
    const customShippedLogisticsJOD = customShippedShippingJOD + customShippedCustomsJOD;
    const customShippedProfitJOD = customShippedRevenueJOD - customShippedCostAllocatedJOD - customShippedLogisticsJOD;
    const customLocalRevenueJOD = customLocalItems.reduce((sum, item) => sum + item.finalTotalJOD, 0);
    const customLocalCostJOD = customLocalItems.reduce((sum, item) => sum + item.dealerTotalJOD, 0);
    const customLocalProfitJOD = customLocalRevenueJOD - customLocalCostJOD;
    const customShippedWeightKg = customShippedItems.reduce((sum, item) => sum + item.weightTotal, 0);

    // Step 4: Calculate services based on equipment dealer cost percentages
    let servicesTotal = 0;
    let commissioningServiceCost = 0;
    let noiseControlServiceCost = 0;
    let soundDesignServiceCost = 0;
    
    if (project.services.commissioning.enabled) {
      commissioningServiceCost = project.services.commissioning.customValue > 0 
        ? project.services.commissioning.customValue 
        : equipmentDealerTotalJOD * servicePricing.commissioning;
      servicesTotal += commissioningServiceCost;
    }
    if (project.services.noiseControl.enabled) {
      noiseControlServiceCost = project.services.noiseControl.customValue > 0 
        ? project.services.noiseControl.customValue 
        : servicePricing.noiseControl; // Fixed value of 5000 JOD
      servicesTotal += noiseControlServiceCost;
    }
    if (project.services.soundDesign.enabled) {
      soundDesignServiceCost = project.services.soundDesign.customValue > 0 
        ? project.services.soundDesign.customValue 
        : equipmentDealerTotalJOD * servicePricing.soundDesign;
      servicesTotal += soundDesignServiceCost;
    }
    if (project.services.projectManagement) {
      servicesTotal += equipmentDealerTotalJOD * servicePricing.projectManagement;
    }
    
    project.customServices.forEach(service => {
      servicesTotal += service.price || 0;
    });

    // Step 5: Project totals - apply discount to the final subtotal
    // For BOQ display: use full prices without discount (equipmentTotalJODBeforeDiscount)
    // For project summary: apply discount to final subtotal
    const subtotalBeforeDiscount = equipmentTotalJODBeforeDiscount + customEquipmentTotalJOD + servicesTotal;
    const discountAmount = equipmentTotalJODBeforeDiscount - equipmentTotalJOD; // discount applies to equipment only
    const projectSubtotalJOD = equipmentTotalJOD + customEquipmentTotalJOD + servicesTotal;
    const projectTaxJOD = project.includeTax ? Math.round(projectSubtotalJOD * 0.16 * 10) / 10 : 0; // 16% VAT if includeTax is true
    const projectTotalJOD = projectSubtotalJOD + projectTaxJOD;

    // Step 6: Void profit calculations (Void catalog stream only)
    const voidLandedCostExclTax020JOD = equipmentDealerTotalJOD + voidPassThroughJOD;
    const voidSalesProfit = equipmentTotalJOD - voidLandedCostExclTax020JOD;

    // Board-level margin metrics
    // Correct denominator: discounted MSRP value (excludes logistics pass-through and VAT)
    const equipmentMsrpValueJOD = equipmentClientTotalJOD; // MSRP total before discount
    const discountedEquipmentValueJOD = equipmentClientTotalJOD * globalDiscountMultiplier;
    const passThroughJOD = voidPassThroughJOD; // billed at cost, 0 margin (import VAT excluded — never billed to client)
    const vatJOD = tax020; // import VAT (input tax) paid at customs
    const vatToRemitJOD = project.includeTax ? Math.max(0, projectTaxJOD - vatJOD) : 0; // ponytail: floor at 0 — output<input is possible on heavy discount; remit 0 not negative
    const equipmentGrossMargin = discountedEquipmentValueJOD > 0 ? voidSalesProfit / discountedEquipmentValueJOD : 0;
    const effectiveDiscountPct = project.globalDiscount || 0;

    // Service provider pools — all services are 100% pass-through to providers, 0 profit for NogaHub
    const noiseControlEngineerFee = noiseControlServiceCost;
    const soundSystemDesignerFee = soundDesignServiceCost;
    const serviceProviderPayout = servicesTotal;
    const nogahubServiceProfit = 0;

    // Step 7: Gross profit across all streams, then the 2% origination fee (charged only on
    // Void equipment revenue, after discount) to land on the final distributable profit.
    const grossProfitJOD = voidSalesProfit + customShippedProfitJOD + customLocalProfitJOD + nogahubServiceProfit;
    const originationFeeJOD = discountedEquipmentValueJOD * ORIGINATION_FEE_RATE;
    const finalProfitJOD = grossProfitJOD - originationFeeJOD;

    // Calculate equipment dealer total in USD for PO
    const equipmentDealerTotalUSD = project.equipment.reduce((sum, item) => {
      const equipment = equipmentDatabase.find(eq => eq.code === item.code);
      if (equipment && item.quantity > 0) {
        return sum + (equipment.dealerUSD * item.quantity);
      }
      return sum;
    }, 0);

    return {
      equipmentDetails: equipmentDetailsFinal,
      customEquipmentDetails,
      customEquipmentTotalJOD,
      equipmentDealerTotalJOD,
      equipmentDealerTotalUSD,
      equipmentClientTotalJOD,
      equipmentTotalJOD: equipmentTotalJOD + customEquipmentTotalJOD, // Use discounted BOQ total + custom equipment for display
      equipmentTotalJODBeforeDiscount,
      doorToDoorCostJOD,
      doorToDoorCostExclTax020JOD,
      totalWeight,
      servicesTotal,
      subtotalBeforeDiscount,
      discountAmount,
      projectSubtotalJOD,
      projectTaxJOD,
      projectTotalJOD,
      voidSalesProfit,
      commissioningServiceCost,
      noiseControlServiceCost,
      soundDesignServiceCost,
      noiseControlEngineerFee,
      soundSystemDesignerFee,
      shipping: totalShippingCost,
      customs: totalCustomsExclTax020,
      tax: tax020,
      internalDoorToDoorPrice: doorToDoorCostJOD,
      clientDoorToDoorCost: doorToDoorCostExclTax020JOD,
      // Board-level metrics
      equipmentMsrpValueJOD,
      discountedEquipmentValueJOD,
      passThroughJOD,
      vatJOD,
      vatToRemitJOD,
      equipmentGrossMargin,
      effectiveDiscountPct,
      serviceProviderPayout,
      nogahubServiceProfit,
      // Per-stream revenue/cost/profit split for the Profit Analysis section
      streams: {
        voidShipped: {
          revenue: equipmentTotalJOD,
          cost: equipmentDealerTotalJOD,
          shipping: voidShippingJOD,
          customs: voidCustomsJOD,
          profit: voidSalesProfit,
          weightKg: voidWeight,
          itemCount: project.equipment.filter(item => item.quantity > 0).length
        },
        customShipped: {
          revenue: customShippedRevenueJOD,
          priceOnly: customShippedPriceOnlyJOD,
          cost: customShippedCostAllocatedJOD,
          shipping: customShippedShippingJOD,
          customs: customShippedCustomsJOD,
          logistics: customShippedLogisticsJOD,
          profit: customShippedProfitJOD,
          weightKg: customShippedWeightKg,
          itemCount: customShippedItems.length
        },
        customLocal: {
          revenue: customLocalRevenueJOD,
          cost: customLocalCostJOD,
          profit: customLocalProfitJOD,
          itemCount: customLocalItems.length
        },
        services: {
          revenue: servicesTotal,
          cost: 0,
          profit: nogahubServiceProfit
        }
      },
      grossProfitJOD,
      originationFeeJOD,
      finalProfitJOD
    };
  }, [project, equipmentDatabase, exchangeRate, shippingRatePerKg, servicePricing]);

  // Handle calculate button
  const handleCalculate = useCallback(() => {
    const results = calculateProjectCosts();
    if (results) {
      setCalculationResults(results);
      setIsCalculated(true);
    } else {
      toast.error('Please add equipment before calculating');
    }
  }, [calculateProjectCosts]);

  const generateQuotationNumber = (projectName) => {
    const prefix = (projectName || 'XX').replace(/\s+/g, '').toUpperCase().slice(0, 2).padEnd(2, 'X');
    const now = new Date();
    const date = `${now.getFullYear()}${String(now.getMonth() + 1).padStart(2, '0')}${String(now.getDate()).padStart(2, '0')}`;
    const rand = String(Math.floor(Math.random() * 900) + 100);
    return `${prefix}${date}${rand}`;
  };

  // PDF Download function
  const downloadQuotationPDF = (calculationResults, project) => {
    const quotationNumber = generateQuotationNumber(project.projectName);
    const printContent = `
      <html>
        <head>
          <title>Quotation - ${project.projectName || 'Project'}</title>
          <style>
            @media print {
              @page {
                margin: 0.5in 0.5in 0.75in 0.5in;
                @bottom-left {
                  content: "Page " counter(page) " of " counter(pages);
                  font-size: 9pt;
                  font-family: Arial, sans-serif;
                  color: #555;
                }
                @bottom-center { content: ""; }
                @bottom-right { content: ""; }
              }
              body { margin: 0; }
            }
            body { font-family: Arial, sans-serif; margin: 20px; font-size: 12px; }
            .header { text-align: center; margin-bottom: 20px; border-bottom: 2px solid #000; padding-bottom: 10px; }
            .company-info { margin-bottom: 15px; font-size: 11px; }
            .project-info { margin-bottom: 15px; font-size: 11px; }
            .equipment-table { width: 100%; border-collapse: collapse; margin-bottom: 15px; font-size: 11px; }
            .equipment-table th, .equipment-table td { border: 1px solid #ddd; padding: 6px; text-align: left; }
            .equipment-table th { background-color: #f2f2f2; font-size: 11px; }
            .totals { margin-top: 15px; font-size: 12px; }
            .totals-row { display: flex; justify-content: space-between; margin-bottom: 3px; }
            .total-final { font-weight: bold; font-size: 13px; border-top: 2px solid #000; padding-top: 8px; }
            .discount { color: red; }
          </style>
        </head>
        <body>
          <div class="header">
            <div style="text-align: left; margin-bottom: 20px;">
              <img src="${logoImage}" alt="NogaHub Logo" style="width: 80px; height: 80px; margin-bottom: 15px; object-fit: contain; display: block;" onerror="this.style.display='none'"/>
              <h1 style="margin: 0 0 10px 0; font-size: 1.8em;">QUOTATION</h1>
              <div class="company-info">
                <strong>Deep Sound For Technical Consultations</strong><br/>
                Housing Bank Complex 93 - Ground Floor 102<br/>
                Q. Nour St. - Welbdeh - Amman - Jordan<br/>
                Phone: +962 (0) 795144821<br/>
                Website: www.nogahub.com
              </div>
            </div>
          </div>
          
          <div class="project-info">
            <strong>Quotation No.:</strong> ${quotationNumber}<br/>
            <strong>Client:</strong> ${project.clientName || 'Client Name'}<br/>
            <strong>Project:</strong> ${project.projectName || 'Project Name'}<br/>
            <strong>Date:</strong> ${new Date().toLocaleDateString()}<br/>
          </div>

          <h3>Void Acoustics Equipment</h3>
          <table class="equipment-table">
            <thead>
              <tr>
                <th>Description</th>
                <th>Quantity</th>
                <th>Unit Price (JOD)</th>
                <th>Total (JOD)</th>
              </tr>
            </thead>
            <tbody>
              ${calculationResults.equipmentDetails.map(item => `
                <tr>
                  <td>${item.name}</td>
                  <td>${item.quantity}</td>
                  <td>${(Number(item.finalUnitPriceJOD)||0).toFixed(1)}</td>
                  <td>${(Number(item.finalTotalJOD)||0).toFixed(1)}</td>
                </tr>
              `).join('')}
            </tbody>
          </table>
          
          ${calculationResults.customEquipmentDetails && calculationResults.customEquipmentDetails.length > 0 ? `
            <h3 style="margin-top: 30px;">Custom Equipment</h3>
            <table class="equipment-table">
              <thead>
                <tr>
                  <th>Description</th>
                  <th>Quantity</th>
                  <th>Unit Price (JOD)</th>
                  <th>Total (JOD)</th>
                </tr>
              </thead>
              <tbody>
                ${calculationResults.customEquipmentDetails.map(item => `
                  <tr>
                    <td>${item.name}</td>
                    <td>${item.quantity}</td>
                    <td>${(Number(item.finalUnitPriceJOD)||0).toFixed(1)}</td>
                    <td>${(Number(item.finalTotalJOD)||0).toFixed(1)}</td>
                  </tr>
                `).join('')}
              </tbody>
            </table>
            <p style="font-size: 10px; color: #666; margin-top: 5px; font-style: italic;">
              Note: Custom equipment is not subject to discount
            </p>
          ` : ''}
          
          ${calculationResults.servicesTotal > 0 ? `
            <h3 style="margin-top: 30px;">Professional Services</h3>
            <table class="equipment-table">
              <thead>
                <tr>
                  <th>Service Description</th>
                  <th>Price (JOD)</th>
                </tr>
              </thead>
              <tbody>
                ${project.services.commissioning.enabled ? `
                  <tr>
                    <td>Sub-contracting Commissioning</td>
                    <td>${(calculationResults.commissioningServiceCost).toFixed(1)}</td>
                  </tr>
                ` : ''}
                ${project.services.noiseControl.enabled ? `
                  <tr>
                    <td>Noise Control Studies</td>
                    <td>${(calculationResults.noiseControlServiceCost).toFixed(1)}</td>
                  </tr>
                ` : ''}
                ${project.services.soundDesign.enabled ? `
                  <tr>
                    <td>Sound System Design</td>
                    <td>${(calculationResults.soundDesignServiceCost).toFixed(1)}</td>
                  </tr>
                ` : ''}
                ${project.services.projectManagement ? `
                  <tr>
                    <td>Project Management</td>
                    <td>${(calculationResults.equipmentDealerTotalJOD * 0.10).toFixed(1)}</td>
                  </tr>
                ` : ''}
                ${project.customServices.map(service => `
                  <tr>
                    <td>${service.name}</td>
                    <td>${(service.price).toFixed(1)}</td>
                  </tr>
                `).join('')}
              </tbody>
            </table>
          ` : ''}
          
          <div class="totals">
            <div class="totals-row">
              <span>Subtotal:</span>
              <span>${((calculationResults.equipmentTotalJODBeforeDiscount || 0) + (calculationResults.customEquipmentTotalJOD || 0) + (calculationResults.servicesTotal || 0)).toFixed(1)} JOD</span>
            </div>
            ${project.globalDiscount > 0 ? `
              <div class="totals-row discount">
                <span>Equipment Discount:</span>
                <span>-${(calculationResults.discountAmount || 0).toFixed(1)} JOD</span>
              </div>
            ` : ''}
            ${project.globalDiscount > 0 && project.includeTax ? `
              <div class="totals-row">
                <span>Subtotal:</span>
                <span>${(calculationResults.projectSubtotalJOD || 0).toFixed(1)} JOD</span>
              </div>
            ` : ''}
            ${project.includeTax ? `
            <div class="totals-row">
              <span>VAT (16%):</span>
              <span>${(calculationResults.projectTaxJOD || 0).toFixed(1)} JOD</span>
            </div>
            ` : ''}
            <div class="totals-row total-final">
              <span>TOTAL:</span>
              <span>${(calculationResults.projectTotalJOD || 0).toFixed(1)} JOD</span>
            </div>
          </div>
          
          <div style="margin-top: 25px; font-size: 10px; color: #666;">
            <p><strong>Terms & Conditions:</strong></p>
            <p style="white-space: pre-line;">${project.terms || ''}</p>
          </div>
        </body>
      </html>
    `;
    
    const printWindow = window.open('about:blank', '_blank');
    if (printWindow) {
      try {
        printWindow.document.open();
        // @ts-ignore - Using document.write for printing functionality
        printWindow.document.write(printContent);
        printWindow.document.close();
        
        // Set window title to hide about:blank
        printWindow.document.title = `Quotation - ${project.projectName || 'Project'}`;
        
        // Wait for content to load then print
        setTimeout(() => {
          printWindow.print();
        }, 100);
      } catch (error) {
        console.error('Error generating PDF:', error);
        toast.error('Error generating PDF. Please check if popup blocker is disabled.');
      }
    } else {
      toast.error('Unable to open print window. Please check if popup blocker is disabled.');
    }
  };

  // Download Purchase Order PDF
  const downloadPurchaseOrderPDF = (calculationResults, _project) => {
    const poNumber = (() => {
      const now = new Date();
      const year = now.getFullYear().toString().slice(-2);
      const month = String(now.getMonth() + 1).padStart(2, '0');
      const day = String(now.getDate()).padStart(2, '0');
      return `20${year}${month}${day}1`;
    })();

    const deliveryDate = (() => {
      const deliveryDate = new Date();
      deliveryDate.setDate(deliveryDate.getDate() + 14);
      return deliveryDate.toLocaleDateString('en-GB');
    })();

    // Filter to only include 'void' category equipment
    const voidEquipmentDetails = calculationResults.equipmentDetails.filter(item => {
      const equipment = equipmentDatabase.find(eq => eq.name === item.name);
      return equipment && equipment.category === 'void';
    });

    // Calculate void equipment totals
    const voidEquipmentTotal = voidEquipmentDetails.reduce((sum, item) => 
      sum + ((item.dealerPriceUSD || item.dealerUSD || 0) * (item.quantity || 0)), 0
    );

    const printContent = `
      <html>
        <head>
          <title>Purchase Order - ${poNumber}</title>
          <style>
            @media print {
              @page {
                margin: 0.5in 0.5in 0.75in 0.5in;
                @bottom-left {
                  content: "Page " counter(page) " of " counter(pages);
                  font-size: 9pt;
                  font-family: Arial, sans-serif;
                  color: #555;
                }
                @bottom-center { content: ""; }
                @bottom-right { content: ""; }
              }
              body { margin: 0; }
            }
            body { font-family: Arial, sans-serif; margin: 20px; font-size: 12px; }
            .header { display: flex; justify-content: space-between}
            .company-info { padding-right: 20px; }
            .po-info { flex: 1; text-align: right; }
            .po-banner { background-color:rgb(108, 108, 108); color: Black; padding: 10px; text-align: Right; font-weight: bold; margin-bottom: 10px; font-size: 14px; }
            .section { margin-bottom: 15px; }
            .section-title { font-weight: bold; margin-bottom: 8px; }
            .two-column { display: flex; gap: 20px; margin-bottom: 15px; }
            .column { flex: 1; }
            .delivery-info { background-color: #f9fafb; padding: 10px; margin-bottom: 15px; display: flex; justify-content: space-between; }
            .equipment-table { width: 100%; border-collapse: collapse; margin-bottom: 15px; font-size: 10px; }
            .equipment-table th, .equipment-table td { border: 1px solid #d1d5db; padding: 6px; }
            .equipment-table th { background-color: #f3f4f6; }
            .totals { text-align: right; margin-bottom: 15px; }
            .total-banner { background-color:rgb(108, 108, 108); color: white; padding: 12px; text-align: center; font-weight: bold; font-size: 16px; margin-bottom: 15px; }
            .instructions { margin-bottom: 15px; font-size: 11px; }
            .signature-section { border-top: 1px solid #000; padding-top: 15px; margin-top: 20px; }
            .signature-row { display: flex; gap: 30px; margin-bottom: 30px; }
            .signature-field { flex: 1; }
            .signature-line { border-bottom: 1px solid #000; height: 20px; margin-top: 5px; }
            .footer { text-align: center; border-top: 1px solid #000; padding-top: 10px; margin-top: 20px; font-size: 11px; color: #6b7280; }
          </style>
        </head>
        <body>
          <div class="header">
            <div class="company-info">
              <h2 style="margin: 0 0 0 0;">Deep Sound For Technical Consultation LLC</h2>
            </div>
            <div class="po-info">
              <div class="po-banner">PURCHASE ORDER</div>
              <div style="font-size: 11px;">
                <strong>Document Number:</strong> ${poNumber}<br/>
                <strong>Document Date:</strong> ${new Date().toLocaleDateString('en-GB')}<br/>
                <strong>Page:</strong> 1/1<br/><br/>
                <strong>Customer No.:</strong> _____________
              </div>
            </div>
          </div>

          <div class="section">
            <div class="section-title">Bill To:</div>
            <div style="font-size: 11px;">
              <strong>Deep Sound For Technical Consultation LLC</strong><br/>
              Trading Name: Nogahub<br/>
              Housing Bank Complex 93 - Ground Floor 102<br/>
              Q. Nour St. - Welbdeh - Amman - Jordan
            </div>
          </div>

          <div class="section">
            <div class="section-title">Ship To:</div>
            <div style="font-size: 11px;">
              <strong>Company:</strong> Deep Sound For Technical Consultation LLC<br/>
              <strong>Contact Person:</strong> Tarik Zuraikat<br/>
              <strong>Address:</strong> 15 Al rojoum, Downtown Amman, 11118, Jordan<br/>
              <strong>City, Country:</strong> Amman, Jordan<br/>
              <strong>Phone:</strong> +96779061007<br/>
              <strong>Email:</strong> T.zuraikat@moab-llift.com
            </div>
          </div>

          <div class="section">
            <div class="section-title">Vendor Contact:</div>
            <div style="font-size: 11px;">
              Void Acoustics Research Ltd.<br/>
              Unit L, The Fulcrum<br/>
              Vantage Way<br/>
              Poole, Dorset, BH12 4NU<br/>
              United Kingdom
            </div>
          </div>

          <div class="delivery-info">
            <div><strong>Delivery Date:</strong> ${deliveryDate}</div>
            <div><strong>Currency:</strong> USD</div>
          </div>

          <table class="equipment-table">
            <thead>
              <tr>
                <th style="width: 8%;">#</th>
                <th>Description</th>
                <th style="width: 8%;">Qty</th>
                <th style="width: 15%;">Unit Price</th>
                <th style="width: 15%;">Total</th>
              </tr>
            </thead>
            <tbody>
              ${voidEquipmentDetails.map((item, index) => `
                <tr>
                  <td style="text-align: center;">${String(index + 1).padStart(3, '0')}</td>
                  <td>
                    <strong>${item.name}</strong><br/>
                    <span style="font-size: 9px; color: #9ca3af;">Delivery Date: ${deliveryDate}</span>
                  </td>
                  <td style="text-align: center;">${item.quantity}</td>
                  <td style="text-align: right;">$${(parseFloat(item.dealerPriceUSD || item.dealerUSD) || 0).toFixed(2)}</td>
                  <td style="text-align: right;">$${(parseFloat(item.dealerPriceUSD || item.dealerUSD || 0) * parseFloat(item.quantity || 0)).toFixed(2)}</td>
                </tr>
              `).join('')}
            </tbody>
          </table>

          <div class="totals">
            <div><strong>Total Amount:</strong> $${voidEquipmentTotal.toFixed(2)}</div>
          </div>

          <div style="font-size: 11px; margin-bottom: 15px;">
            Professional audio equipment purchase for manufacturing facility.<br/>
            Based on Purchase Request ${poNumber}.
          </div>

          <div class="instructions">
            <div class="section-title">Special Instructions:</div>
            • Invoice to: Deep Sound For Technical Consultation LLC (Bill To address above)<br/>
            • Coordinate delivery schedule with shipping company contact person<br/>
            • All equipment must be factory sealed and include manufacturer warranties<br/>
            • Delivery confirmation required upon receipt at shipping company
          </div>

          <div class="instructions">
            <div class="section-title">Terms and Conditions:</div>
            • Payment: Net 30 days from invoice date<br/>
            • All prices are in USD and FOB origin<br/>
            • Vendor responsible for proper packaging and documentation<br/>
            • Any changes to this order must be approved in writing<br/>
            • Risk of loss transfers upon delivery to shipping company
          </div>

          <div class="signature-section">
            <div class="section-title">Confirmation of Purchase Order</div>
            <div class="signature-row">
              <div class="signature-field">
                <div><strong>Name</strong></div>
                <div class="signature-line"></div>
              </div>
              <div class="signature-field">
                <div><strong>Signature</strong></div>
                <div class="signature-line"></div>
              </div>
              <div class="signature-field">
                <div><strong>Date</strong></div>
                <div class="signature-line"></div>
              </div>
            </div>
          </div>

          <div class="footer">
            <strong>Deep Sound For Technical Consultation LLC</strong><br/>
            Phone: +962 (0) 795 4468 | Email: purchasing@nogahub.jo<br/>
            Website: www.nogahub.com
          </div>
        </body>
      </html>
    `;

    try {
      const printWindow = window.open('', '_blank');
      if (printWindow) {
        printWindow.document.documentElement.innerHTML = printContent;
        
        setTimeout(() => {
          printWindow.focus();
          printWindow.print();
        }, 100);
      } else {
        toast.error('Unable to open print window. Please check if popup blocker is disabled.');
      }
    } catch (error) {
      console.error('Error generating PDF:', error);
      toast.error('Error generating PDF. Please check if popup blocker is disabled.');
    }
  };

  // Add equipment item
  const addEquipment = () => {
    setProject(prev => ({
      ...prev,
      equipment: [...prev.equipment, { code: '', quantity: '' }]
    }));
    setIsCalculated(false);
  };

  // Update equipment item
  const updateEquipment = (index, field, value) => {
    setProject(prev => ({
      ...prev,
      equipment: prev.equipment.map((item, i) => 
        i === index ? { ...item, [field]: value } : item
      )
    }));
    setIsCalculated(false);
  };

  // Remove equipment item
  const removeEquipment = (index) => {
    setProject(prev => ({
      ...prev,
      equipment: prev.equipment.filter((_, i) => i !== index)
    }));
    setIsCalculated(false);
  };

  // Add custom service
  const addCustomService = () => {
    setProject(prev => ({
      ...prev,
      customServices: [...prev.customServices, { name: '', price: '' }]
    }));
    setIsCalculated(false);
  };

  // Add custom equipment
  const addCustomEquipment = () => {
    setProject(prev => ({
      ...prev,
      customEquipment: [...prev.customEquipment, { name: '', cost: '', price: '', weight: '', shipped: false, shipWeight: '' }]
    }));
    setIsCalculated(false);
  };

  // Noise Control Quotation Functions
  const addCustomItem = () => {
    setCustomQuotation(prev => ({
      ...prev,
      items: [...prev.items, { name: '', cost: '', clientPrice: '', quantity: '' }]
    }));
    setNcCalculated(false);
  };

  const addQuotationService = () => {
    setCustomQuotation(prev => ({
      ...prev,
      services: [...prev.services, { name: '', price: '' }]
    }));
    setNcCalculated(false);
  };

  const calculateCustom = () => {
    if (customQuotation.items.length === 0 && customQuotation.services.length === 0) {
      toast.error('Please add at least one item or service');
      return;
    }

    let totalCost = 0;
    let totalRevenueBeforeDiscount = 0;

    customQuotation.items.forEach(item => {
      const itemCost = (Number(item.cost) || 0) * (Number(item.quantity) || 0);
      const itemRevenue = (Number(item.clientPrice) || 0) * (Number(item.quantity) || 0);
      totalCost += itemCost;
      totalRevenueBeforeDiscount += itemRevenue;
    });

    const validServices = customQuotation.services.filter(s => s.name.trim() || Number(s.price) > 0);
    validServices.forEach(service => {
      totalRevenueBeforeDiscount += Number(service.price) || 0;
    });

    const discountAmount = totalRevenueBeforeDiscount * (customQuotation.globalDiscount / 100);
    const subtotal = totalRevenueBeforeDiscount - discountAmount;
    const tax = customQuotation.includeTax ? subtotal * 0.16 : 0;
    const total = subtotal + tax;
    const profit = subtotal - totalCost;
    const profitMargin = subtotal > 0 ? (profit / subtotal) * 100 : 0;

    const results = {
      items: customQuotation.items.map(item => ({
        ...item,
        clientPrice: Number(item.clientPrice) || 0,
        cost: Number(item.cost) || 0,
        quantity: Number(item.quantity) || 0
      })),
      services: validServices.map(s => ({
        name: s.name,
        price: Number(s.price) || 0
      })),
      totalCost,
      totalRevenueBeforeDiscount,
      discountAmount,
      subtotal,
      tax,
      total,
      profit,
      profitMargin
    };

    setNcResults(results);
    setNcCalculated(true);
    toast.success('Custom quotation calculated successfully');
  };

  const downloadCustomPDF = () => {
    if (!ncCalculated || !ncResults) {
      toast.error('Please calculate the quotation first');
      return;
    }

    const quotationNumber = generateQuotationNumber(customQuotation.projectName);
    const printContent = `
      <html>
        <head>
          <title>Quotation - ${customQuotation.projectName || 'Project'}</title>
          <style>
            @media print {
              @page {
                margin: 0.5in 0.5in 0.75in 0.5in;
                @bottom-left {
                  content: "Page " counter(page) " of " counter(pages);
                  font-size: 9pt;
                  font-family: Arial, sans-serif;
                  color: #555;
                }
                @bottom-center { content: ""; }
                @bottom-right { content: ""; }
              }
              body { margin: 0; }
            }
            body { font-family: Arial, sans-serif; margin: 20px; font-size: 12px; }
            .header { text-align: center; margin-bottom: 20px; border-bottom: 2px solid #000; padding-bottom: 10px; }
            .company-info { margin-bottom: 15px; font-size: 11px; }
            .project-info { margin-bottom: 15px; font-size: 11px; }
            .equipment-table { width: 100%; border-collapse: collapse; margin-bottom: 15px; font-size: 11px; }
            .equipment-table th, .equipment-table td { border: 1px solid #ddd; padding: 6px; text-align: left; }
            .equipment-table th { background-color: #f2f2f2; font-size: 11px; }
            .totals { margin-top: 15px; font-size: 12px; }
            .totals-row { display: flex; justify-content: space-between; margin-bottom: 3px; }
            .total-final { font-weight: bold; font-size: 13px; border-top: 2px solid #000; padding-top: 8px; }
            .discount { color: red; }
          </style>
        </head>
        <body>
          <div class="header">
            <div style="text-align: left; margin-bottom: 20px;">
              <img src="${logoImage}" alt="NogaHub Logo" style="width: 80px; height: 80px; margin-bottom: 15px; object-fit: contain; display: block;" onerror="this.style.display='none'"/>
              <h1 style="margin: 0 0 10px 0; font-size: 1.8em;">QUOTATION</h1>
              <div class="company-info">
                <strong>Deep Sound For Technical Consultations</strong><br/>
                Housing Bank Complex 93 - Ground Floor 102<br/>
                Q. Nour St. - Welbdeh - Amman - Jordan<br/>
                Phone: +962 (0) 795144821<br/>
                Website: www.nogahub.com
              </div>
            </div>
          </div>

          <div class="project-info">
            <strong>Quotation No.:</strong> ${quotationNumber}<br/>
            <strong>Client:</strong> ${customQuotation.clientName || 'Client Name'}<br/>
            <strong>Project:</strong> ${customQuotation.projectName || 'Project Name'}<br/>
            <strong>Date:</strong> ${new Date().toLocaleDateString()}<br/>
          </div>

          <table class="equipment-table">
            <thead>
              <tr>
                <th>Description</th>
                <th>Quantity</th>
                <th>Unit Price (${customQuotation.currency})</th>
                <th>Total (${customQuotation.currency})</th>
              </tr>
            </thead>
            <tbody>
              ${ncResults.items.map(item => `
                <tr>
                  <td>${item.name || 'Unnamed Item'}</td>
                  <td>${item.quantity || 0}</td>
                  <td>${(Number(item.clientPrice) || 0).toFixed(1)}</td>
                  <td>${((Number(item.clientPrice) || 0) * (Number(item.quantity) || 0)).toFixed(1)}</td>
                </tr>
              `).join('')}
              ${(ncResults.services || []).length > 0 ? `
                <tr><td colspan="4" style="background:#f9f9f9;font-weight:bold;font-size:10px;padding:4px 6px;">Services</td></tr>
                ${ncResults.services.map(service => `
                  <tr>
                    <td>${service.name || 'Service'}</td>
                    <td>1</td>
                    <td>${(service.price).toFixed(1)}</td>
                    <td>${(service.price).toFixed(1)}</td>
                  </tr>
                `).join('')}
              ` : ''}
            </tbody>
          </table>

          <div class="totals">
            <div class="totals-row">
              <span>Subtotal:</span>
              <span>${(ncResults.totalRevenueBeforeDiscount || 0).toFixed(1)} ${customQuotation.currency}</span>
            </div>
            ${customQuotation.globalDiscount > 0 ? `
              <div class="totals-row discount">
                <span>Discount:</span>
                <span>-${(ncResults.discountAmount || 0).toFixed(1)} ${customQuotation.currency}</span>
              </div>
            ` : ''}
            ${customQuotation.globalDiscount > 0 && customQuotation.includeTax ? `
              <div class="totals-row">
                <span>Subtotal:</span>
                <span>${(ncResults.subtotal || 0).toFixed(1)} ${customQuotation.currency}</span>
              </div>
            ` : ''}
            ${customQuotation.includeTax ? `
            <div class="totals-row">
              <span>VAT (16%):</span>
              <span>${(ncResults.tax || 0).toFixed(1)} ${customQuotation.currency}</span>
            </div>
            ` : ''}
            <div class="totals-row total-final">
              <span>TOTAL:</span>
              <span>${(ncResults.total || 0).toFixed(1)} ${customQuotation.currency}</span>
            </div>
          </div>

          <div style="margin-top: 25px; font-size: 10px; color: #666;">
            <p><strong>Terms & Conditions:</strong></p>
            <p style="white-space: pre-line;">${customQuotation.terms || ''}</p>
          </div>
        </body>
      </html>
    `;

    try {
      const printWindow = window.open('', '_blank');
      if (printWindow) {
        printWindow.document.documentElement.innerHTML = printContent;
        setTimeout(() => {
          printWindow.focus();
          printWindow.print();
        }, 100);
        toast.success('PDF opened in new window');
      } else {
        toast.error('Please allow pop-ups to download PDF');
      }
    } catch (error) {
      console.error('PDF generation error:', error);
      toast.error('Failed to generate PDF');
    }
  };

  const downloadSoundDesignInvoicePDF = (calculationResults, project) => {
    const invoiceNumber = generateQuotationNumber(project.projectName);
    const invoiceDate = new Date().toLocaleDateString('en-GB');
    const printContent = `
      <html>
        <head>
          <title>Invoice - ${project.projectName || 'Project'}</title>
          <style>
            @media print {
              @page {
                margin: 0.5in 0.5in 0.75in 0.5in;
                @bottom-left {
                  content: "Page " counter(page) " of " counter(pages);
                  font-size: 9pt;
                  font-family: Arial, sans-serif;
                  color: #555;
                }
                @bottom-center { content: ""; }
                @bottom-right { content: ""; }
              }
              body { margin: 0; }
            }
            body { font-family: Arial, sans-serif; margin: 20px; font-size: 12px; }
            .header { margin-bottom: 20px; border-bottom: 2px solid #000; padding-bottom: 10px; }
            .company-info { font-size: 11px; }
            .meta-grid { display: flex; justify-content: space-between; margin-bottom: 15px; font-size: 11px; }
            .bill-to { margin-bottom: 15px; font-size: 11px; }
            .equipment-table { width: 100%; border-collapse: collapse; margin-bottom: 15px; font-size: 11px; }
            .equipment-table th, .equipment-table td { border: 1px solid #ddd; padding: 6px; text-align: left; }
            .equipment-table th { background-color: #f2f2f2; }
            .totals { margin-top: 10px; font-size: 12px; }
            .totals-row { display: flex; justify-content: space-between; margin-bottom: 3px; }
            .total-final { font-weight: bold; font-size: 13px; border-top: 2px solid #000; padding-top: 8px; }
            .payment-box { margin-top: 20px; border: 1px solid #ddd; padding: 12px; font-size: 11px; background: #f9fafb; }
            .payment-box h4 { margin: 0 0 8px 0; font-size: 12px; }
            .payment-row { display: flex; gap: 20px; flex-wrap: wrap; }
            .payment-item { margin-bottom: 4px; }
            .terms { margin-top: 15px; font-size: 10px; color: #555; border-top: 1px solid #eee; padding-top: 10px; }
          </style>
        </head>
        <body>
          <div class="header">
            <div style="display:flex; justify-content:space-between; align-items:flex-start;">
              <div>
                <img src="${logoImage}" alt="NogaHub Logo" style="width:70px;height:70px;object-fit:contain;display:block;margin-bottom:8px;" onerror="this.style.display='none'"/>
                <div class="company-info">
                  <strong>Deep Sound For Technical Consultation LLC</strong><br/>
                  Housing Bank Complex 93 - Ground Floor 102<br/>
                  Q. Nour St. - Welbdeh - Amman - Jordan<br/>
                  Phone: +962 (0) 795144821<br/>
                  Website: www.nogahub.com<br/>
                  Tax Reg No: 40261328
                </div>
              </div>
              <div style="text-align:right;">
                <h1 style="margin:0 0 8px 0;font-size:2em;letter-spacing:2px;">INVOICE</h1>
                <div style="font-size:11px;">
                  <strong>Invoice No.:</strong> ${invoiceNumber}<br/>
                  <strong>Date:</strong> ${invoiceDate}<br/>
                  <strong>Due:</strong> Due on receipt
                </div>
              </div>
            </div>
          </div>

          <div class="bill-to">
            <strong>Bill To:</strong><br/>
            ${project.clientName || 'Client Name'}<br/>
            ${project.projectName || 'Project Name'}
          </div>

          <table class="equipment-table">
            <thead>
              <tr>
                <th>Description</th>
                <th>Qty</th>
                <th>Unit Price (JOD)</th>
                <th>Total (JOD)</th>
              </tr>
            </thead>
            <tbody>
              ${calculationResults.equipmentDetails.map(item => `
                <tr>
                  <td>${item.name}</td>
                  <td>${item.quantity}</td>
                  <td>${(item.finalTotalJOD / item.quantity).toFixed(2)}</td>
                  <td>${(item.finalTotalJOD || 0).toFixed(2)}</td>
                </tr>
              `).join('')}
              ${(calculationResults.customEquipmentDetails || []).map(item => `
                <tr>
                  <td>${item.name}</td>
                  <td>${item.quantity}</td>
                  <td>${(item.finalTotalJOD / item.quantity).toFixed(2)}</td>
                  <td>${(item.finalTotalJOD || 0).toFixed(2)}</td>
                </tr>
              `).join('')}
              ${calculationResults.servicesTotal > 0 ? `
                <tr>
                  <td colspan="3"><em>Professional Services</em></td>
                  <td>${Math.round(calculationResults.servicesTotal || 0).toFixed(2)}</td>
                </tr>
              ` : ''}
            </tbody>
          </table>

          <div class="totals">
            ${project.globalDiscount > 0 ? `
              <div class="totals-row"><span>Subtotal (before discount):</span><span>${(calculationResults.equipmentTotalJODBeforeDiscount || 0).toFixed(2)} JOD</span></div>
              <div class="totals-row" style="color:red;"><span>Discount (${project.globalDiscount}%):</span><span>-${(calculationResults.discountAmount || 0).toFixed(2)} JOD</span></div>
            ` : ''}
            <div class="totals-row"><span>Subtotal:</span><span>${(calculationResults.projectSubtotalJOD || 0).toFixed(2)} JOD</span></div>
            ${project.includeTax ? `<div class="totals-row"><span>VAT (16%):</span><span>${(calculationResults.projectTaxJOD || 0).toFixed(2)} JOD</span></div>` : ''}
            <div class="totals-row total-final"><span>Total Due:</span><span>${Math.round(calculationResults.projectTotalJOD || 0).toFixed(2)} JOD</span></div>
          </div>

          <div class="payment-box">
            <h4>Payment Details</h4>
            <div class="payment-row">
              <div class="payment-item"><strong>Bank:</strong> Etihad Bank</div>
              <div class="payment-item"><strong>Account No.:</strong> 0310179526015101</div>
              <div class="payment-item"><strong>IBAN:</strong> JO37UBSI1200000310179526015101</div>
              <div class="payment-item"><strong>SWIFT:</strong> UBSIJOAXXXX</div>
              <div class="payment-item"><strong>CliQ Alias:</strong> Deepsound</div>
            </div>
          </div>

          <div class="terms">
            Payment is due upon receipt of this invoice. Thank you for your business.
          </div>
        </body>
      </html>
    `;
    const printWindow = window.open('', '_blank');
    if (printWindow) {
      printWindow.document.write(printContent);
      printWindow.document.close();
      printWindow.focus();
      setTimeout(() => { printWindow.print(); }, 500);
    }
  };

  const downloadCustomInvoicePDF = () => {
    if (!ncCalculated || !ncResults) {
      toast.error('Please calculate the quotation first');
      return;
    }
    const invoiceNumber = generateQuotationNumber(customQuotation.projectName);
    const invoiceDate = new Date().toLocaleDateString('en-GB');
    const currency = customQuotation.currency || 'JOD';
    const printContent = `
      <html>
        <head>
          <title>Invoice - ${customQuotation.projectName || 'Project'}</title>
          <style>
            @media print {
              @page {
                margin: 0.5in 0.5in 0.75in 0.5in;
                @bottom-left {
                  content: "Page " counter(page) " of " counter(pages);
                  font-size: 9pt;
                  font-family: Arial, sans-serif;
                  color: #555;
                }
                @bottom-center { content: ""; }
                @bottom-right { content: ""; }
              }
              body { margin: 0; }
            }
            body { font-family: Arial, sans-serif; margin: 20px; font-size: 12px; }
            .header { margin-bottom: 20px; border-bottom: 2px solid #000; padding-bottom: 10px; }
            .company-info { font-size: 11px; }
            .bill-to { margin-bottom: 15px; font-size: 11px; }
            .equipment-table { width: 100%; border-collapse: collapse; margin-bottom: 15px; font-size: 11px; }
            .equipment-table th, .equipment-table td { border: 1px solid #ddd; padding: 6px; text-align: left; }
            .equipment-table th { background-color: #f2f2f2; }
            .totals { margin-top: 10px; font-size: 12px; }
            .totals-row { display: flex; justify-content: space-between; margin-bottom: 3px; }
            .total-final { font-weight: bold; font-size: 13px; border-top: 2px solid #000; padding-top: 8px; }
            .payment-box { margin-top: 20px; border: 1px solid #ddd; padding: 12px; font-size: 11px; background: #f9fafb; }
            .payment-box h4 { margin: 0 0 8px 0; font-size: 12px; }
            .payment-row { display: flex; gap: 20px; flex-wrap: wrap; }
            .payment-item { margin-bottom: 4px; }
            .terms { margin-top: 15px; font-size: 10px; color: #555; border-top: 1px solid #eee; padding-top: 10px; }
          </style>
        </head>
        <body>
          <div class="header">
            <div style="display:flex; justify-content:space-between; align-items:flex-start;">
              <div>
                <img src="${logoImage}" alt="NogaHub Logo" style="width:70px;height:70px;object-fit:contain;display:block;margin-bottom:8px;" onerror="this.style.display='none'"/>
                <div class="company-info">
                  <strong>Deep Sound For Technical Consultation LLC</strong><br/>
                  Housing Bank Complex 93 - Ground Floor 102<br/>
                  Q. Nour St. - Welbdeh - Amman - Jordan<br/>
                  Phone: +962 (0) 795144821<br/>
                  Website: www.nogahub.com<br/>
                  Tax Reg No: 40261328
                </div>
              </div>
              <div style="text-align:right;">
                <h1 style="margin:0 0 8px 0;font-size:2em;letter-spacing:2px;">INVOICE</h1>
                <div style="font-size:11px;">
                  <strong>Invoice No.:</strong> ${invoiceNumber}<br/>
                  <strong>Date:</strong> ${invoiceDate}<br/>
                  <strong>Due:</strong> Due on receipt
                </div>
              </div>
            </div>
          </div>

          <div class="bill-to">
            <strong>Bill To:</strong><br/>
            ${customQuotation.clientName || 'Client Name'}<br/>
            ${customQuotation.projectName || 'Project Name'}
          </div>

          <table class="equipment-table">
            <thead>
              <tr>
                <th>Description</th>
                <th>Qty</th>
                <th>Unit Price (${currency})</th>
                <th>Total (${currency})</th>
              </tr>
            </thead>
            <tbody>
              ${ncResults.items.map(item => `
                <tr>
                  <td>${item.name}</td>
                  <td>${item.quantity}</td>
                  <td>${(Number(item.clientPrice) || 0).toFixed(2)}</td>
                  <td>${((Number(item.clientPrice) || 0) * (Number(item.quantity) || 0)).toFixed(2)}</td>
                </tr>
              `).join('')}
              ${(ncResults.services || []).filter(s => s.name && Number(s.price) > 0).map(s => `
                <tr>
                  <td>${s.name}</td>
                  <td>1</td>
                  <td>${(Number(s.price) || 0).toFixed(2)}</td>
                  <td>${(Number(s.price) || 0).toFixed(2)}</td>
                </tr>
              `).join('')}
            </tbody>
          </table>

          <div class="totals">
            ${customQuotation.globalDiscount > 0 ? `
              <div class="totals-row"><span>Subtotal (before discount):</span><span>${(ncResults.totalRevenueBeforeDiscount || 0).toFixed(2)} ${currency}</span></div>
              <div class="totals-row" style="color:red;"><span>Discount (${customQuotation.globalDiscount}%):</span><span>-${(ncResults.discountAmount || 0).toFixed(2)} ${currency}</span></div>
            ` : ''}
            <div class="totals-row"><span>Subtotal:</span><span>${(ncResults.subtotal || 0).toFixed(2)} ${currency}</span></div>
            ${customQuotation.includeTax ? `<div class="totals-row"><span>VAT (16%):</span><span>${(ncResults.tax || 0).toFixed(2)} ${currency}</span></div>` : ''}
            <div class="totals-row total-final"><span>Total Due:</span><span>${(ncResults.total || 0).toFixed(2)} ${currency}</span></div>
          </div>

          <div class="payment-box">
            <h4>Payment Details</h4>
            <div class="payment-row">
              <div class="payment-item"><strong>Bank:</strong> Etihad Bank</div>
              <div class="payment-item"><strong>Account No.:</strong> 0310179526015101</div>
              <div class="payment-item"><strong>IBAN:</strong> JO37UBSI1200000310179526015101</div>
              <div class="payment-item"><strong>SWIFT:</strong> UBSIJOAXXXX</div>
              <div class="payment-item"><strong>CliQ Alias:</strong> Deepsound</div>
            </div>
          </div>

          <div class="terms">
            Payment is due upon receipt of this invoice. Thank you for your business.
          </div>
        </body>
      </html>
    `;
    const printWindow = window.open('', '_blank');
    if (printWindow) {
      printWindow.document.write(printContent);
      printWindow.document.close();
      printWindow.focus();
      setTimeout(() => { printWindow.print(); }, 500);
    }
  };

  // Login Screen
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-100 flex items-center justify-center p-3 sm:p-6">
        <div className="bg-white rounded-2xl shadow-2xl p-4 sm:p-8 w-full max-w-md border border-gray-100">
          <div className="text-center mb-6 sm:mb-8">
            <NogaHubLogo size={window.innerWidth < 640 ? 80 : 120} className="justify-center mb-3 sm:mb-4" />
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">Nogahub Administration</h1>
            <p className="text-sm sm:text-base text-gray-600">Deep Sound Technical Consultations</p>
          </div>

          <div className="space-y-4 sm:space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Email</label>
              <input
                type="email"
                value={loginData.username}
                onChange={(e) => setLoginData(prev => ({ ...prev, username: e.target.value }))}
                className="w-full px-3 sm:px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-black focus:border-black transition-all text-base"
                placeholder="Enter email address"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Password</label>
              <input
                type="password"
                value={loginData.password}
                onChange={(e) => setLoginData(prev => ({ ...prev, password: e.target.value }))}
                className="w-full px-3 sm:px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-black focus:border-black transition-all text-base"
                placeholder="Enter password"
              />
            </div>

            <button
              onClick={handleLogin}
              className="w-full flex items-center justify-center space-x-2 px-4 sm:px-6 py-3 bg-black text-white rounded-xl hover:bg-gray-800 transition-colors duration-200 font-medium text-sm sm:text-base"
            >
              <LogIn size={18} />
              <span>Login</span>
            </button>

            <div className="flex items-center gap-3">
              <div className="flex-1 h-px bg-gray-200" />
              <span className="text-xs text-gray-400">or</span>
              <div className="flex-1 h-px bg-gray-200" />
            </div>

            <button
              onClick={handleGoogleLogin}
              className="w-full flex items-center justify-center space-x-2 px-4 sm:px-6 py-3 bg-white text-gray-700 border border-gray-300 rounded-xl hover:bg-gray-50 transition-colors duration-200 font-medium text-sm sm:text-base"
            >
              <svg width="18" height="18" viewBox="0 0 18 18" aria-hidden="true">
                <path fill="#4285F4" d="M17.64 9.2c0-.64-.06-1.25-.16-1.84H9v3.48h4.84a4.14 4.14 0 0 1-1.8 2.72v2.26h2.92c1.7-1.57 2.68-3.88 2.68-6.62z" />
                <path fill="#34A853" d="M9 18c2.43 0 4.47-.8 5.96-2.18l-2.92-2.26c-.81.54-1.84.86-3.04.86-2.34 0-4.32-1.58-5.03-3.7H.96v2.33A9 9 0 0 0 9 18z" />
                <path fill="#FBBC05" d="M3.97 10.72a5.4 5.4 0 0 1 0-3.44V4.95H.96a9 9 0 0 0 0 8.1l3.01-2.33z" />
                <path fill="#EA4335" d="M9 3.58c1.32 0 2.5.45 3.44 1.35l2.58-2.58C13.46.89 11.43 0 9 0A9 9 0 0 0 .96 4.95l3.01 2.33C4.68 5.16 6.66 3.58 9 3.58z" />
              </svg>
              <span>Sign in with Google</span>
            </button>

          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto p-3 sm:p-6 bg-gray-50 min-h-screen">
      <div className="bg-white rounded-2xl shadow-xl border border-gray-100">
        {/* Header */}
        <div className="border-b border-gray-200 px-3 sm:px-6 py-3 sm:py-4">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 sm:gap-0">
            <div className="flex items-center">
              <NogaHubLogo size={window.innerWidth < 640 ? 60 : 80} />
              <div className="ml-3 sm:ml-4">
                <h1 className="text-lg sm:text-2xl font-bold text-gray-900">Nogahub Administration</h1>
                <p className="text-xs sm:text-base text-gray-600 mt-1">
                  Welcome, {userRole === 'admin' ? 'Administrator' : 'User'} | Deep Sound Technical Consultations
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2 self-end sm:self-auto">
              <button
                onClick={() => {
                  if (activeTab === 'quotation') startNewQuotation();
                  else if (activeTab === 'custom') startNewCustomQuotation();
                }}
                title="New quotation"
                className="flex items-center justify-center w-9 h-9 rounded-xl border border-gray-300 text-gray-600 hover:bg-gray-100 transition-colors"
              >
                <RefreshCw size={16} />
              </button>
              <button
                onClick={handleLogout}
                className="flex items-center space-x-2 px-3 sm:px-4 py-2 bg-gray-900 text-white rounded-xl hover:bg-gray-800 transition-colors text-sm sm:text-base"
              >
                <LogOut size={14} />
                <span>Logout</span>
              </button>
            </div>
          </div>
        </div>

        {/* Company Activities Section */}
        <div className="border-b border-gray-200 bg-gray-50">
          <div className="px-3 sm:px-6 py-4 sm:py-6">
            <h2 className="text-lg sm:text-xl font-bold text-gray-900 mb-3 sm:mb-4">Company Activities</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
              {/* Installation Section */}
              <div 
                onClick={() => setActiveSection('installation')}
                className={`border-2 rounded-xl p-4 sm:p-6 cursor-pointer transition-all ${
                  activeSection === 'installation' 
                    ? 'border-black bg-white shadow-lg' 
                    : 'border-gray-300 bg-white hover:border-gray-400 hover:shadow-md'
                }`}
              >
                <div className="flex items-center mb-2 sm:mb-3">
                  <Calculator size={20} className="mr-2 sm:mr-3 text-black" />
                  <h3 className="text-base sm:text-lg font-semibold text-gray-900">Installation</h3>
                </div>
                <p className="text-gray-600 text-xs sm:text-sm">
                  Sound system design, equipment procurement, installation, and commissioning services.
                </p>
                {activeSection === 'installation' && (
                  <div className="mt-2 sm:mt-3 px-2 sm:px-3 py-1 bg-black text-white text-xs rounded-full inline-block">
                    Active
                  </div>
                )}
              </div>

              {/* Rental Section */}
              <div 
                onClick={() => setActiveSection('rental')}
                className={`border-2 rounded-xl p-4 sm:p-6 cursor-pointer transition-all ${
                  activeSection === 'rental' 
                    ? 'border-black bg-white shadow-lg' 
                    : 'border-gray-300 bg-white hover:border-gray-400 hover:shadow-md'
                }`}
              >
                <div className="flex items-center mb-2 sm:mb-3">
                  <Zap size={20} className="mr-2 sm:mr-3 text-black" />
                  <h3 className="text-base sm:text-lg font-semibold text-gray-900">Rental</h3>
                </div>
                <p className="text-gray-600 text-xs sm:text-sm">
                  Equipment rental services for events, temporary installations, and short-term projects.
                </p>
                {activeSection === 'rental' && (
                  <div className="mt-2 sm:mt-3 px-2 sm:px-3 py-1 bg-black text-white text-xs rounded-full inline-block">
                    Active
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Navigation Tabs - Only show when Installation is active */}
        {activeSection === 'installation' && (
          <div className="border-b border-gray-200">
            <nav className="flex flex-wrap gap-2 sm:gap-0 sm:space-x-8 px-3 sm:px-6 overflow-x-auto">
              {[
                { id: 'documents', label: 'Documentation', icon: FileText, roles: ['admin', 'user'] },
                { id: 'quotation', label: 'Sound Design Quote', icon: Calculator, roles: ['admin', 'user'] },
                { id: 'custom', label: 'Custom Quote', icon: Zap, roles: ['admin', 'user'] },
                { id: 'saved-projects', label: 'Saved Projects', icon: FolderOpen, roles: ['admin', 'user'] },
                { id: 'results', label: 'Financial Analysis', icon: TrendingUp, roles: ['admin', 'user'] },
                { id: 'entities', label: 'Business Entities', icon: Building2, roles: ['admin', 'user'] }
              ].filter(tab => tab.roles.includes(userRole)).map(tab => {
                const Icon = tab.icon;
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`py-2 sm:py-3 px-2 sm:px-1 border-b-2 font-medium text-xs sm:text-sm flex items-center space-x-1 sm:space-x-2 transition-all whitespace-nowrap ${
                      activeTab === tab.id
                        ? 'border-black text-black'
                        : 'border-transparent text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    <Icon size={14} />
                    <span className="hidden sm:inline">{tab.label}</span>
                    <span className="sm:hidden">{tab.label.split(' ')[0]}</span>
                  </button>
                );
              })}
            </nav>
          </div>
        )}

        <div className="p-3 sm:p-6">
          {/* Installation Section Content */}
          {activeSection === 'installation' && (
            <>
              {/* Quotation Builder Tab */}
              {activeTab === 'quotation' && (
                <div className="space-y-6">
                  {/* Project Info */}
                  <div className="mb-4 sm:mb-6 grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Client Name</label>
                      <input
                        type="text"
                        value={project.clientName}
                        onChange={(e) => setProject(prev => ({ ...prev, clientName: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-black focus:border-black text-base"
                        placeholder="Enter client name"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Project Name</label>
                      <input
                        type="text"
                        value={project.projectName}
                        onChange={(e) => setProject(prev => ({ ...prev, projectName: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-black focus:border-black text-base"
                        placeholder="Enter project name"
                      />
                    </div>
                  </div>
              {/* Equipment Selection */}
              <div>
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-lg font-semibold text-gray-900">Void Acoustics Equipment Selection</h3>
                  {equipmentLoading && (
                    <div className="text-sm text-gray-500">Loading equipment...</div>
                  )}
                  {!equipmentLoading && equipmentDatabase.length === 0 && (
                    <div className="text-sm text-red-500">Equipment database not loaded</div>
                  )}
                  <button
                    onClick={addEquipment}
                    className="flex items-center space-x-2 px-4 py-2 bg-black text-white rounded-lg hover:bg-red-600 transition-colors"
                  >
                    <Plus size={16} />
                    <span>Add Equipment</span>
                  </button>
                </div>
                
                {/* Global Discount */}
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Global Discount (%)</label>
                  <input
                    type="number"
                    min="0"
                    max="50"
                    value={project.globalDiscount}
                    onChange={(e) => {
                      setProject(prev => ({ ...prev, globalDiscount: parseFloat(e.target.value) || 0 }));
                      setIsCalculated(false);
                    }}
                    className="flex-1 sm:flex-none sm:w-32 min-w-0 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-black focus:border-black"
                    placeholder="0-50%"
                  />
                </div>
                
                <div className="space-y-3">
                  {project.equipment.map((item, index) => (
                    <div key={index} className="flex items-center space-x-4 p-4 border border-gray-200 rounded-lg bg-gray-50">
                      <div className="flex-1">
                        <SearchableDropdown
                          options={equipmentDatabase}
                          value={item.code}
                          onChange={(value) => updateEquipment(index, 'code', value)}
                          placeholder="Search equipment..."
                          disabled={equipmentLoading}
                        />
                      </div>
                      <input
                        type="number"
                        min="1"
                        value={item.quantity}
                        onChange={(e) => updateEquipment(index, 'quantity', e.target.value)}
                        className="w-20 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-black focus:border-black"
                        placeholder="Qty"
                      />
                      <button
                        onClick={() => removeEquipment(index)}
                        className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  ))}
                </div>
              </div>

              {/* Services Selection */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Professional Services</h3>
                
                <div className="space-y-3 mb-4">
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 sm:gap-0 p-3 border border-gray-200 rounded-lg hover:bg-gray-50 sm:min-h-[52px]">
                    <label className="flex items-center space-x-3 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={project.services.commissioning.enabled}
                        onChange={(e) => {
                          setProject(prev => {
                            const equipmentTotal = prev.equipment.reduce((sum, item) => {
                              const equipment = equipmentDatabase.find(eq => eq.code === item.code);
                              if (equipment && item.quantity > 0) {
                                const dealerPriceJOD = equipment.dealerUSD * exchangeRate;
                                return sum + (dealerPriceJOD * item.quantity);
                              }
                              return sum;
                            }, 0);
                            const defaultValue = e.target.checked ? Math.round(equipmentTotal * 0.06) : 0;
                            
                            return {
                              ...prev,
                              services: { 
                                ...prev.services, 
                                commissioning: { 
                                  enabled: e.target.checked, 
                                  customValue: defaultValue 
                                }
                              }
                            };
                          });
                          setIsCalculated(false);
                        }}
                        className="h-4 w-4 text-black border-gray-300 rounded focus:ring-black"
                      />
                      <span>Commissioning</span>
                    </label>
                    <div className="flex items-center space-x-2 sm:min-w-[180px] justify-end">
                      {project.services.commissioning.enabled && (
                        <>
                          <span className="text-sm text-gray-600">Custom Value (JOD):</span>
                          <input
                            type="number"
                            value={project.services.commissioning.customValue}
                            onChange={(e) => {
                              setProject(prev => ({
                                ...prev,
                                services: { 
                                  ...prev.services, 
                                  commissioning: { ...prev.services.commissioning, customValue: parseFloat(e.target.value) || 0 }
                                }
                              }));
                              setIsCalculated(false);
                            }}
                            className="w-24 px-2 py-1 border border-gray-300 rounded text-sm"
                            placeholder={`Default: ${(() => {
                              const equipmentTotal = project.equipment.reduce((sum, item) => {
                                const equipment = equipmentDatabase.find(eq => eq.code === item.code);
                                if (equipment && item.quantity > 0) {
                                  const dealerPriceJOD = equipment.dealerUSD * exchangeRate;
                                  return sum + (dealerPriceJOD * item.quantity);
                                }
                                return sum;
                              }, 0);
                              return Math.round(equipmentTotal * 0.06);
                            })()}`}
                            min="0"
                            step="0.01"
                          />
                        </>
                      )}
                    </div>
                  </div>
                  
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 sm:gap-0 p-3 border border-gray-200 rounded-lg hover:bg-gray-50 sm:min-h-[52px]">
                    <label className="flex items-center space-x-3 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={project.services.noiseControl.enabled}
                        onChange={(e) => {
                          setProject(prev => {
                            const defaultValue = e.target.checked ? 5000 : 0; // Fixed 5000 JOD
                            
                            return {
                              ...prev,
                              services: { 
                                ...prev.services, 
                                noiseControl: { 
                                  enabled: e.target.checked, 
                                  customValue: defaultValue 
                                }
                              }
                            };
                          });
                          setIsCalculated(false);
                        }}
                        className="h-4 w-4 text-black border-gray-300 rounded focus:ring-black"
                      />
                      <span>Noise Control Studies</span>
                    </label>
                    <div className="flex items-center space-x-2 sm:min-w-[180px] justify-end">
                      {project.services.noiseControl.enabled && (
                        <>
                          <span className="text-sm text-gray-600">Custom Value (JOD):</span>
                          <input
                            type="number"
                            value={project.services.noiseControl.customValue}
                            onChange={(e) => {
                              setProject(prev => ({
                                ...prev,
                                services: { 
                                  ...prev.services, 
                                  noiseControl: { ...prev.services.noiseControl, customValue: parseFloat(e.target.value) || 0 }
                                }
                              }));
                              setIsCalculated(false);
                            }}
                            className="w-24 px-2 py-1 border border-gray-300 rounded text-sm"
                            placeholder="Default: 5000"
                            min="0"
                            step="0.01"
                          />
                        </>
                      )}
                    </div>
                  </div>
                  
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 sm:gap-0 p-3 border border-gray-200 rounded-lg hover:bg-gray-50 sm:min-h-[52px]">
                    <label className="flex items-center space-x-3 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={project.services.soundDesign.enabled}
                        onChange={(e) => {
                          setProject(prev => {
                            const equipmentTotal = prev.equipment.reduce((sum, item) => {
                              const equipment = equipmentDatabase.find(eq => eq.code === item.code);
                              if (equipment && item.quantity > 0) {
                                const dealerPriceJOD = equipment.dealerUSD * exchangeRate;
                                return sum + (dealerPriceJOD * item.quantity);
                              }
                              return sum;
                            }, 0);
                            const defaultValue = e.target.checked ? Math.round(equipmentTotal * 0.03) : 0;
                            
                            return {
                              ...prev,
                              services: { 
                                ...prev.services, 
                                soundDesign: { 
                                  enabled: e.target.checked, 
                                  customValue: defaultValue 
                                }
                              }
                            };
                          });
                          setIsCalculated(false);
                        }}
                        className="h-4 w-4 text-black border-gray-300 rounded focus:ring-black"
                      />
                      <span>Sound System Design</span>
                    </label>
                    <div className="flex items-center space-x-2 sm:min-w-[180px] justify-end">
                      {project.services.soundDesign.enabled && (
                        <>
                          <span className="text-sm text-gray-600">Custom Value (JOD):</span>
                          <input
                            type="number"
                            value={project.services.soundDesign.customValue}
                            onChange={(e) => {
                              setProject(prev => ({
                                ...prev,
                                services: { 
                                  ...prev.services, 
                                  soundDesign: { ...prev.services.soundDesign, customValue: parseFloat(e.target.value) || 0 }
                                }
                              }));
                              setIsCalculated(false);
                            }}
                            className="w-24 px-2 py-1 border border-gray-300 rounded text-sm"
                            placeholder={`Default: ${(() => {
                              const equipmentTotal = project.equipment.reduce((sum, item) => {
                                const equipment = equipmentDatabase.find(eq => eq.code === item.code);
                                if (equipment && item.quantity > 0) {
                                  const dealerPriceJOD = equipment.dealerUSD * exchangeRate;
                                  return sum + (dealerPriceJOD * item.quantity);
                                }
                                return sum;
                              }, 0);
                              return Math.round(equipmentTotal * 0.03);
                            })()}`}
                            min="0"
                            step="0.01"
                          />
                        </>
                      )}
                    </div>
                  </div>
                </div>

                {/* Custom Equipment */}
                <div className="mb-4">
                  <div className="flex justify-between items-center mb-2">
                    <h4 className="font-medium text-gray-700">Custom Equipment</h4>
                    <button
                      onClick={addCustomEquipment}
                      className="text-sm px-3 py-1 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
                    >
                      Add Custom Equipment
                    </button>
                  </div>
                  
                  {project.customEquipment.map((equipment, index) => {
                    const updateItem = (patch) => {
                      setProject(prev => ({
                        ...prev,
                        customEquipment: prev.customEquipment.map((eq, i) =>
                          i === index ? { ...eq, ...patch } : eq
                        )
                      }));
                      setIsCalculated(false);
                    };
                    return (
                    <div key={index} className="flex flex-wrap items-center gap-2 sm:gap-3 mb-2">
                      <input
                        type="text"
                        placeholder="Equipment name"
                        value={equipment.name}
                        onChange={(e) => updateItem({ name: e.target.value })}
                        className="w-full sm:w-auto sm:flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-black focus:border-black"
                      />
                      <input
                        type="number"
                        placeholder="Cost (JOD)"
                        value={equipment.cost}
                        onChange={(e) => updateItem({ cost: e.target.value })}
                        className="flex-1 sm:flex-none sm:w-28 min-w-0 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-black focus:border-black"
                      />
                      <input
                        type="number"
                        placeholder="Price (JOD)"
                        value={equipment.price}
                        onChange={(e) => updateItem({ price: e.target.value })}
                        className="flex-1 sm:flex-none sm:w-28 min-w-0 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-black focus:border-black"
                      />
                      <input
                        type="number"
                        placeholder="Qty"
                        value={equipment.weight}
                        onChange={(e) => updateItem({ weight: e.target.value })}
                        className="flex-1 sm:flex-none sm:w-24 min-w-0 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-black focus:border-black"
                      />
                      <button
                        type="button"
                        title={equipment.shipped ? 'Shipped item — click to mark local' : 'Local item — click to mark shipped'}
                        onClick={() => updateItem({ shipped: !equipment.shipped })}
                        className={`p-2 rounded-lg transition-colors ${equipment.shipped ? 'bg-blue-100 text-blue-700 hover:bg-blue-200' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
                      >
                        <Truck size={16} />
                      </button>
                      {equipment.shipped && (
                        <input
                          type="number"
                          placeholder="kg/unit"
                          value={equipment.shipWeight}
                          onChange={(e) => updateItem({ shipWeight: e.target.value })}
                          className="flex-1 sm:flex-none sm:w-24 min-w-0 px-3 py-2 border border-blue-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        />
                      )}
                      <button
                        onClick={() => {
                          setProject(prev => ({
                            ...prev,
                            customEquipment: prev.customEquipment.filter((_, i) => i !== index)
                          }));
                          setIsCalculated(false);
                        }}
                        className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                    );
                  })}
                </div>

                {/* Custom Services */}
                <div>
                  <div className="flex justify-between items-center mb-2">
                    <h4 className="font-medium text-gray-700">Custom Services</h4>
                    <button
                      onClick={addCustomService}
                      className="text-sm px-3 py-1 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
                    >
                      Add Service
                    </button>
                  </div>
                  
                  {project.customServices.map((service, index) => (
                    <div key={index} className="flex flex-wrap items-center gap-2 sm:gap-3 mb-2">
                      <input
                        type="text"
                        placeholder="Service name"
                        value={service.name}
                        onChange={(e) => {
                          setProject(prev => ({
                            ...prev,
                            customServices: prev.customServices.map((s, i) =>
                              i === index ? { ...s, name: e.target.value } : s
                            )
                          }));
                          setIsCalculated(false);
                        }}
                        className="w-full sm:w-auto sm:flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-black focus:border-black"
                      />
                      <input
                        type="number"
                        placeholder="Price (JOD)"
                        value={service.price}
                        onChange={(e) => {
                          setProject(prev => ({
                            ...prev,
                            customServices: prev.customServices.map((s, i) =>
                              i === index ? { ...s, price: e.target.value } : s
                            )
                          }));
                          setIsCalculated(false);
                        }}
                        className="flex-1 sm:flex-none sm:w-32 min-w-0 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-black focus:border-black"
                      />
                      <button
                        onClick={() => {
                          setProject(prev => ({
                            ...prev,
                            customServices: prev.customServices.filter((_, i) => i !== index)
                          }));
                          setIsCalculated(false);
                        }}
                        className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  ))}
                </div>

                {/* Tax Toggle */}
                <div className="mt-6">
                  <label className="flex items-center space-x-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={project.includeTax}
                      onChange={(e) => {
                        setProject(prev => ({
                          ...prev,
                          includeTax: e.target.checked
                        }));
                        setIsCalculated(false);
                      }}
                      className="w-5 h-5 text-black border-gray-300 rounded focus:ring-2 focus:ring-black cursor-pointer"
                    />
                    <span className="font-medium text-gray-700">Include Tax (16% VAT)</span>
                  </label>
                </div>

                {/* Terms & Conditions */}
                <div className="mt-6">
                  <label className="block mb-2">
                    <span className="font-medium text-gray-700">Terms & Conditions</span>
                  </label>
                  <textarea
                    value={project.terms}
                    onChange={(e) => {
                      setProject(prev => ({
                        ...prev,
                        terms: e.target.value
                      }));
                    }}
                    placeholder="All prices are in Jordanian Dinars (JOD)
Equipment prices include door-to-door delivery
VAT is calculated at 16% as per Jordanian tax regulations
Subject to ±10% change after technical study
Payment terms: 90% down payment, 10% after project completion
This quotation is valid for 30 days from the date of issue"
                    rows="6"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-black focus:border-black resize-y"
                  />
                </div>

                {/* Action Buttons */}
                <div className="flex flex-wrap justify-end gap-3 mt-6">
                  <button
                    onClick={handleCalculate}
                    className="flex items-center space-x-2 px-6 py-3 bg-black text-white rounded-lg hover:bg-gray-800 transition-colors"
                  >
                    <Calculator size={16} />
                    <span>Calculate</span>
                  </button>
                  <button
                    onClick={() => setShowSaveModal(true)}
                    disabled={project.equipment.length === 0 && project.customEquipment.length === 0}
                    className="flex items-center space-x-2 px-6 py-3 bg-black text-white rounded-lg hover:bg-red-600 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed"
                  >
                    <Save size={16} />
                    <span>{loadedProjectId ? 'Update Quotation' : 'Save Project'}</span>
                  </button>
                  {loadedProjectId && (
                    <button
                      onClick={() => { setLoadedProjectId(null); setShowSaveModal(true); }}
                      className="flex items-center space-x-2 px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 transition-colors"
                    >
                      <Save size={16} />
                      <span>Save Quotation</span>
                    </button>
                  )}
                </div>
              </div>

              {/* BOQ Display */}
              {isCalculated && calculationResults && (
                <div className="bg-gray-50 border border-gray-200 rounded-xl p-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Bill of Quantities (BOQ)</h3>

                  {/* Mobile card list — table hidden below sm */}
                  <div className="sm:hidden space-y-3">
                    {calculationResults.equipmentDetails.map((item, index) => (
                      <div key={index} className="bg-white border border-gray-200 rounded-lg p-3">
                        <div className="flex justify-between items-start gap-2 mb-2">
                          <p className="font-medium text-sm">{item.name}</p>
                          <span className="font-mono text-xs text-gray-500 whitespace-nowrap">{equipmentDatabase.find(eq => eq.name === item.name)?.code || 'N/A'}</span>
                        </div>
                        <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
                          <div className="flex justify-between"><span className="text-gray-500">Qty</span><span>{item.quantity}</span></div>
                          <div className="flex justify-between"><span className="text-gray-500">Unit (JOD)</span><span>{(Number(item.finalUnitPriceJOD)||0).toFixed(1)}</span></div>
                          <div className="flex justify-between"><span className="text-gray-500">Weight (kg)</span><span>{item.weightTotal.toFixed(1)}</span></div>
                          <div className="flex justify-between"><span className="text-gray-500">Total (JOD)</span><span className="font-semibold">{(Number(item.finalTotalJOD)||0).toFixed(1)}</span></div>
                        </div>
                      </div>
                    ))}
                    {calculationResults.customEquipmentDetails && calculationResults.customEquipmentDetails.map((item, index) => (
                      <div key={`custom-${index}`} className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                        <div className="flex justify-between items-start gap-2 mb-2">
                          <p className="font-medium text-sm">{item.name}</p>
                          <span className="font-mono text-xs text-gray-500 whitespace-nowrap">{item.code}</span>
                        </div>
                        <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
                          <div className="flex justify-between"><span className="text-gray-500">Qty</span><span>{item.quantity}</span></div>
                          <div className="flex justify-between"><span className="text-gray-500">Unit (JOD)</span><span>{Math.round(item.finalUnitPriceJOD) || 'N/A'}</span></div>
                          <div className="flex justify-between"><span className="text-gray-500">Weight (kg)</span><span>{item.weightTotal.toFixed(1)}</span></div>
                          <div className="flex justify-between"><span className="text-gray-500">Total (JOD)</span><span className="font-semibold">{Math.round(item.finalTotalJOD) || 'N/A'}</span></div>
                        </div>
                      </div>
                    ))}
                    <div className="bg-gray-100 rounded-lg p-3 text-sm font-semibold flex justify-between">
                      <span>Equipment Subtotal</span>
                      <span>{(calculationResults.equipmentTotalJODBeforeDiscount || 0).toFixed(1)} JOD · {calculationResults.totalWeight.toFixed(1)} kg</span>
                    </div>
                    {calculationResults.customEquipmentDetails && calculationResults.customEquipmentDetails.length > 0 && (
                      <div className="bg-blue-100 rounded-lg p-3 text-sm font-semibold flex justify-between">
                        <span>Custom Equipment Subtotal</span>
                        <span>{(calculationResults.customEquipmentTotalJOD || 0).toFixed(1)} JOD</span>
                      </div>
                    )}
                  </div>

                  <div className="hidden sm:block overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="bg-black text-white">
                          <th className="text-left p-3 font-medium">Item Code</th>
                          <th className="text-left p-3 font-medium">Description</th>
                          <th className="text-center p-3 font-medium">Qty</th>
                          <th className="text-right p-3 font-medium">Unit Price (JOD)</th>
                          <th className="text-right p-3 font-medium">Total (JOD)</th>
                          <th className="text-right p-3 font-medium">Weight (kg)</th>
                        </tr>
                      </thead>
                      <tbody>
                        {calculationResults.equipmentDetails.map((item, index) => (
                          <tr key={index} className="border-b border-gray-200 hover:bg-gray-50">
                            <td className="p-3 font-mono">{equipmentDatabase.find(eq => eq.name === item.name)?.code || 'N/A'}</td>
                            <td className="p-3">{item.name}</td>
                            <td className="p-3 text-center">{item.quantity}</td>
                            <td className="p-3 text-right">{(Number(item.finalUnitPriceJOD)||0).toFixed(1)}</td>
                            <td className="p-3 text-right font-semibold">{(Number(item.finalTotalJOD)||0).toFixed(1)}</td>
                            <td className="p-3 text-right">{item.weightTotal.toFixed(1)}</td>
                          </tr>
                        ))}
                        {calculationResults.customEquipmentDetails && calculationResults.customEquipmentDetails.map((item, index) => (
                          <tr key={`custom-${index}`} className="border-b border-gray-200 hover:bg-gray-50 bg-blue-50">
                            <td className="p-3 font-mono">{item.code}</td>
                            <td className="p-3">{item.name}</td>
                            <td className="p-3 text-center">{item.quantity}</td>
                            <td className="p-3 text-right">{Math.round(item.finalUnitPriceJOD) || 'N/A'}</td>
                            <td className="p-3 text-right font-semibold">{Math.round(item.finalTotalJOD) || 'N/A'}</td>
                            <td className="p-3 text-right">{item.weightTotal.toFixed(1)}</td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot>
                        <tr className="bg-gray-100 font-semibold">
                          <td colSpan="4" className="p-3 text-right">Equipment Subtotal:</td>
                          <td className="p-3 text-right">{(calculationResults.equipmentTotalJODBeforeDiscount || 0).toFixed(1)} JOD</td>
                          <td className="p-3 text-right">{calculationResults.totalWeight.toFixed(1)} kg</td>
                        </tr>
                        {calculationResults.customEquipmentDetails && calculationResults.customEquipmentDetails.length > 0 && (
                          <tr className="bg-blue-100 font-semibold">
                            <td colSpan="4" className="p-3 text-right">Custom Equipment Subtotal:</td>
                            <td className="p-3 text-right">{(calculationResults.customEquipmentTotalJOD || 0).toFixed(1)} JOD</td>
                            <td className="p-3 text-right">0.0 kg</td>
                          </tr>
                        )}
                      </tfoot>
                    </table>
                  </div>
                </div>
              )}

              {/* Calculation Summary */}
              {isCalculated && calculationResults && (
                <div className="bg-gray-50 p-4 rounded-xl border">
                  <h4 className="font-semibold text-gray-900 mb-2">Project Summary</h4>
                  <div className={`grid grid-cols-2 ${project.globalDiscount > 0 ? 'sm:grid-cols-5' : 'sm:grid-cols-4'} gap-4 text-sm`}>
                    <div>
                      <span className="text-gray-600">Equipment Total:</span>
                      <p className="font-semibold">{(calculationResults.equipmentTotalJOD || 0).toFixed(1)} JOD</p>
                    </div>
                    <div>
                      <span className="text-gray-600">Services Total:</span>
                      <p className="font-semibold">{(calculationResults.servicesTotal || 0).toFixed(1)} JOD</p>
                    </div>
                    <div>
                      <span className="text-gray-600">Subtotal:</span>
                      <p className="font-semibold">{(calculationResults.projectSubtotalJOD || 0).toFixed(1)} JOD</p>
                    </div>
                    {project.globalDiscount > 0 && (
                      <div>
                        <span className="text-gray-600">Discount:</span>
                        <p className="font-semibold text-red-600">-{(parseFloat(project.globalDiscount) || 0).toFixed(1)}%</p>
                      </div>
                    )}
                    <div>
                      <span className="text-gray-600">{project.includeTax ? 'Final (with 16% VAT):' : 'Final (VAT not included):'}</span>
                      <p className="font-semibold text-green-600">{(calculationResults.projectTotalJOD || 0).toFixed(1)} JOD</p>
                    </div>
                  </div>
                </div>
              )}

              {!isCalculated && project.equipment.length > 0 && (
                <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4">
                  <p className="text-yellow-800">
                    <strong>Please click "Calculate" to generate accurate door-to-door pricing and BOQ.</strong>
                    <br />
                    Equipment prices include shipping, customs, and all door-to-door costs based on business model.
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Custom Quotation Tab */}
          {activeTab === 'custom' && (
            <div className="space-y-6">
              {/* Project Info */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Client Name</label>
                  <input
                    type="text"
                    placeholder="Enter client name"
                    value={customQuotation.clientName}
                    onChange={(e) => setCustomQuotation(prev => ({ ...prev, clientName: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-black focus:border-black"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Project Name</label>
                  <input
                    type="text"
                    placeholder="Enter project name"
                    value={customQuotation.projectName}
                    onChange={(e) => setCustomQuotation(prev => ({ ...prev, projectName: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-black focus:border-black"
                  />
                </div>
              </div>

              {/* Items Section */}
              <div className="bg-white border border-gray-200 rounded-xl p-6">
                <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 mb-4">
                  <h3 className="text-lg font-semibold text-gray-900">Custom Equipment</h3>
                  <div className="flex flex-wrap items-center gap-2 sm:gap-3">
                    <div className="flex rounded-lg border border-gray-300 overflow-hidden text-sm">
                      {[
                        { code: 'JOD', label: 'JOD' },
                        { code: 'QAR', label: 'QAR' },
                        { code: 'USD', label: '$' },
                        { code: 'EUR', label: '€' },
                      ].map(({ code, label }) => (
                        <button
                          key={code}
                          onClick={() => {
                            setCustomQuotation(prev => ({
                              ...prev,
                              currency: code,
                              terms: `All prices are in ${getCurrencyName(code)}
Equipment prices include door-to-door delivery
VAT is calculated at 16% as per Jordanian tax regulations
Subject to ±10% change after technical study
Payment terms: 90% down payment, 10% after project completion
This quotation is valid for 30 days from the date of issue`
                            }));
                            setNcCalculated(false);
                          }}
                          className={`px-3 py-2 font-medium transition-colors ${
                            customQuotation.currency === code
                              ? 'bg-black text-white'
                              : 'bg-white text-gray-700 hover:bg-gray-100'
                          }`}
                        >
                          {label}
                        </button>
                      ))}
                    </div>
                    <button
                      onClick={addCustomItem}
                      className="flex items-center space-x-2 px-4 py-2 bg-black text-white rounded-lg hover:bg-gray-800 transition-colors"
                    >
                      <Plus size={16} />
                      <span>Add Item</span>
                    </button>
                  </div>
                </div>

                {customQuotation.items.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    <p>No items added yet. Click "Add Item" to get started.</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {customQuotation.items.map((item, index) => (
                      <div key={index} className="grid grid-cols-1 md:grid-cols-5 gap-3 items-center p-4 bg-gray-50 rounded-lg">
                        <input
                          type="text"
                          placeholder="Item name/description"
                          value={item.name}
                          onChange={(e) => {
                            setCustomQuotation(prev => ({
                              ...prev,
                              items: prev.items.map((itm, i) =>
                                i === index ? { ...itm, name: e.target.value } : itm
                              )
                            }));
                            setNcCalculated(false);
                          }}
                          className="md:col-span-2 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-black focus:border-black"
                        />
                        <input
                          type="number"
                          placeholder="Cost price"
                          value={item.cost}
                          onChange={(e) => {
                            setCustomQuotation(prev => ({
                              ...prev,
                              items: prev.items.map((itm, i) =>
                                i === index ? { ...itm, cost: e.target.value } : itm
                              )
                            }));
                            setNcCalculated(false);
                          }}
                          className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-black focus:border-black"
                        />
                        <input
                          type="number"
                          placeholder="Selling price"
                          value={item.clientPrice}
                          onChange={(e) => {
                            setCustomQuotation(prev => ({
                              ...prev,
                              items: prev.items.map((itm, i) =>
                                i === index ? { ...itm, clientPrice: e.target.value } : itm
                              )
                            }));
                            setNcCalculated(false);
                          }}
                          className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-black focus:border-black"
                        />
                        <div className="flex items-center space-x-2">
                          <input
                            type="number"
                            placeholder="Qty"
                            value={item.quantity}
                            onChange={(e) => {
                              setCustomQuotation(prev => ({
                                ...prev,
                                items: prev.items.map((itm, i) =>
                                  i === index ? { ...itm, quantity: e.target.value } : itm
                                )
                              }));
                              setNcCalculated(false);
                            }}
                            className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-black focus:border-black"
                          />
                          <button
                            onClick={() => {
                              setCustomQuotation(prev => ({
                                ...prev,
                                items: prev.items.filter((_, i) => i !== index)
                              }));
                              setNcCalculated(false);
                            }}
                            className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* Services Section */}
                <div className="mt-6 border-t border-gray-200 pt-6">
                  <div className="flex justify-between items-center mb-4">
                    <h4 className="text-base font-semibold text-gray-900">Services</h4>
                    <button
                      onClick={addQuotationService}
                      className="flex items-center space-x-2 px-3 py-2 bg-gray-800 text-white rounded-lg hover:bg-gray-700 transition-colors text-sm"
                    >
                      <Plus size={14} />
                      <span>Add Service</span>
                    </button>
                  </div>
                  {customQuotation.services.length === 0 ? (
                    <p className="text-sm text-gray-400 italic">No services added. Click "Add Service" to include services like installation, commissioning, design, etc.</p>
                  ) : (
                    <div className="space-y-2">
                      {customQuotation.services.map((service, index) => (
                        <div key={index} className="grid grid-cols-1 md:grid-cols-3 gap-3 items-center p-3 bg-gray-50 rounded-lg">
                          <input
                            type="text"
                            placeholder="Service name (e.g. Installation)"
                            value={service.name}
                            onChange={(e) => {
                              setCustomQuotation(prev => ({
                                ...prev,
                                services: prev.services.map((s, i) =>
                                  i === index ? { ...s, name: e.target.value } : s
                                )
                              }));
                              setNcCalculated(false);
                            }}
                            className="md:col-span-2 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-black focus:border-black text-sm"
                          />
                          <div className="flex items-center space-x-2">
                            <input
                              type="number"
                              placeholder="Price"
                              value={service.price}
                              onChange={(e) => {
                                setCustomQuotation(prev => ({
                                  ...prev,
                                  services: prev.services.map((s, i) =>
                                    i === index ? { ...s, price: e.target.value } : s
                                  )
                                }));
                                setNcCalculated(false);
                              }}
                              className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-black focus:border-black text-sm"
                            />
                            <button
                              onClick={() => {
                                setCustomQuotation(prev => ({
                                  ...prev,
                                  services: prev.services.filter((_, i) => i !== index)
                                }));
                                setNcCalculated(false);
                              }}
                              className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                            >
                              <Trash2 size={16} />
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Global Discount */}
                <div className="mt-6">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Global Discount (%)</label>
                  <input
                    type="number"
                    min="0"
                    max="100"
                    placeholder="0-100%"
                    value={customQuotation.globalDiscount}
                    onChange={(e) => {
                      const value = Math.min(100, Math.max(0, parseFloat(e.target.value) || 0));
                      setCustomQuotation(prev => ({
                        ...prev,
                        globalDiscount: value
                      }));
                      setNcCalculated(false);
                    }}
                    className="w-full sm:w-64 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-black focus:border-black"
                  />
                </div>

                {/* Tax Toggle */}
                <div className="mt-6">
                  <label className="flex items-center space-x-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={customQuotation.includeTax}
                      onChange={(e) => {
                        setCustomQuotation(prev => ({
                          ...prev,
                          includeTax: e.target.checked
                        }));
                        setNcCalculated(false);
                      }}
                      className="w-5 h-5 text-black border-gray-300 rounded focus:ring-2 focus:ring-black cursor-pointer"
                    />
                    <span className="font-medium text-gray-700">Include Tax (16% VAT)</span>
                  </label>
                </div>

                {/* Terms & Conditions */}
                <div className="mt-6">
                  <label className="block mb-2">
                    <span className="font-medium text-gray-700">Terms & Conditions</span>
                  </label>
                  <textarea
                    value={customQuotation.terms}
                    onChange={(e) => {
                      setCustomQuotation(prev => ({
                        ...prev,
                        terms: e.target.value
                      }));
                    }}
                    placeholder="All prices are in Jordanian Dinars (JOD)
Equipment prices include door-to-door delivery
VAT is calculated at 16% as per Jordanian tax regulations
Subject to ±10% change after technical study
Payment terms: 90% down payment, 10% after project completion
This quotation is valid for 30 days from the date of issue"
                    rows="6"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-black focus:border-black resize-y"
                  />
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex flex-wrap justify-end gap-3">
                <button
                  onClick={calculateCustom}
                  className="flex items-center space-x-2 px-6 py-3 bg-black text-white rounded-lg hover:bg-gray-800 transition-colors"
                >
                  <Calculator size={16} />
                  <span>Calculate</span>
                </button>
                <button
                  onClick={() => setShowNcSaveModal(true)}
                  disabled={customQuotation.items.length === 0 && customQuotation.services.length === 0}
                  className="flex items-center space-x-2 px-6 py-3 bg-black text-white rounded-lg hover:bg-red-600 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed"
                >
                  <Save size={16} />
                  <span>{loadedProjectId ? 'Update Quotation' : 'Save Project'}</span>
                </button>
                {loadedProjectId && (
                  <button
                    onClick={() => { setLoadedProjectId(null); setShowNcSaveModal(true); }}
                    className="flex items-center space-x-2 px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 transition-colors"
                  >
                    <Save size={16} />
                    <span>Save Quotation</span>
                  </button>
                )}
              </div>

              {/* Results Display */}
              {ncCalculated && ncResults && (
                <div className="bg-gray-50 border border-gray-200 rounded-xl p-6 space-y-6">
                  <h3 className="text-lg font-semibold text-gray-900">Quotation Summary</h3>

                  {/* Items Table */}
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="bg-black text-white">
                          <th className="text-left p-3 font-medium">Item Description</th>
                          <th className="text-center p-3 font-medium">Qty</th>
                          <th className="text-right p-3 font-medium">Unit Price</th>
                          <th className="text-right p-3 font-medium">Total</th>
                        </tr>
                      </thead>
                      <tbody>
                        {ncResults.items.map((item, index) => (
                          <tr key={index} className="border-b border-gray-200">
                            <td className="p-3">{item.name || 'Unnamed Item'}</td>
                            <td className="text-center p-3">{item.quantity}</td>
                            <td className="text-right p-3">{(Number(item.clientPrice) || 0).toFixed(1)} {customQuotation.currency}</td>
                            <td className="text-right p-3">{((Number(item.clientPrice) || 0) * (Number(item.quantity) || 0)).toFixed(1)} {customQuotation.currency}</td>
                          </tr>
                        ))}
                        {(ncResults.services || []).length > 0 && (
                          <>
                            <tr className="bg-gray-100">
                              <td colSpan="4" className="p-2 text-xs font-semibold text-gray-600 uppercase tracking-wide">Services</td>
                            </tr>
                            {ncResults.services.map((service, index) => (
                              <tr key={`svc-${index}`} className="border-b border-gray-200">
                                <td className="p-3">{service.name || 'Service'}</td>
                                <td className="text-center p-3">1</td>
                                <td className="text-right p-3">{(Number(service.price) || 0).toFixed(1)} {customQuotation.currency}</td>
                                <td className="text-right p-3">{(Number(service.price) || 0).toFixed(1)} {customQuotation.currency}</td>
                              </tr>
                            ))}
                          </>
                        )}
                      </tbody>
                    </table>
                  </div>

                  {/* Totals */}
                  <div className="border-t border-gray-300 pt-4">
                    {customQuotation.globalDiscount > 0 && (
                      <>
                        <div className="flex justify-between mb-2">
                          <span className="font-medium">Subtotal (before discount):</span>
                          <span>{ncResults.totalRevenueBeforeDiscount.toFixed(1)} {customQuotation.currency}</span>
                        </div>
                        <div className="flex justify-between mb-2 text-red-600">
                          <span className="font-medium">Discount ({customQuotation.globalDiscount}%):</span>
                          <span>-{ncResults.discountAmount.toFixed(1)} {customQuotation.currency}</span>
                        </div>
                      </>
                    )}
                    <div className="flex justify-between mb-2">
                      <span className="font-medium">Subtotal{customQuotation.globalDiscount > 0 ? ' (after discount)' : ''}:</span>
                      <span>{ncResults.subtotal.toFixed(1)} {customQuotation.currency}</span>
                    </div>
                    {customQuotation.includeTax && (
                      <div className="flex justify-between mb-2">
                        <span className="font-medium">VAT (16%):</span>
                        <span>{ncResults.tax.toFixed(1)} {customQuotation.currency}</span>
                      </div>
                    )}
                    <div className="flex justify-between text-lg font-bold border-t border-gray-300 pt-2 mt-2">
                      <span>Total:</span>
                      <span className="text-green-600">{ncResults.total.toFixed(1)} {customQuotation.currency}</span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Saved Projects Tab */}
          {activeTab === 'saved-projects' && (
            <div className="space-y-6">
              <div className="flex justify-between items-center">
                <h3 className="text-lg font-semibold text-gray-900">Saved Projects ({savedProjects.length})</h3>
              </div>

              {savedProjects.length === 0 ? (
                <div className="bg-gray-50 border border-gray-200 rounded-xl p-8 text-center">
                  <FolderOpen size={48} className="mx-auto text-gray-400 mb-4" />
                  <h4 className="text-lg font-medium text-gray-900 mb-2">No saved projects yet</h4>
                  <p className="text-gray-600">Create a project with equipment and click "Save Project" to get started.</p>
                </div>
              ) : (
                <div className="grid gap-4">
                  {savedProjects.map((savedProject) => (
                    <div
                      key={savedProject.id}
                      className="bg-white border border-gray-200 rounded-xl p-4 sm:p-6 hover:shadow-md transition-shadow cursor-pointer group"
                      onClick={() => loadSavedProject(savedProject)}
                    >
                      <div className="flex justify-between items-start gap-2">
                        <div className="flex-1 min-w-0">
                          <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mb-2">
                            <h4 className="text-lg font-semibold text-gray-900">{savedProject.projectName}</h4>
                            {savedProject.projectType === 'soundDesign' && (
                              <span className="px-2 py-1 bg-blue-100 text-blue-800 text-xs font-medium rounded">
                                Sound Design
                              </span>
                            )}
                            {savedProject.projectType === 'custom' && (
                              <span className="px-2 py-1 bg-purple-100 text-purple-800 text-xs font-medium rounded">
                                Custom
                              </span>
                            )}
                            {savedProject.hasCalculation && (
                              <span className="px-2 py-1 bg-green-100 text-green-800 text-xs font-medium rounded-full">
                                Calculated
                              </span>
                            )}
                          </div>
                          <p className="text-gray-600 font-medium mb-1">{savedProject.clientName}</p>
                          {(savedProject.created_by_username || savedProject.users) && (
                            <p className="text-blue-600 text-sm mb-1">
                              Created by: {savedProject.created_by_username || savedProject.users?.username} ({savedProject.created_by_email || savedProject.users?.email})
                            </p>
                          )}
                          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-gray-500 mb-2">
                            <span>{(savedProject.equipment?.length || 0) + (savedProject.customEquipment?.length || 0)} items</span>
                            <span>•</span>
                            <span>{(parseFloat(savedProject.total) || 0).toFixed(2)} JOD total</span>
                            <span>•</span>
                            <span>Saved {savedProject.createdAt ? new Date(savedProject.createdAt).toLocaleString('en-US', {
                              year: 'numeric',
                              month: '2-digit',
                              day: '2-digit',
                              hour: '2-digit',
                              minute: '2-digit',
                              hour12: false
                            }).replace(',', '') : 'Unknown date'}</span>
                          </div>
                          
                          {/* Show selected equipment preview */}
                          <div className="mt-3">
                            <p className="text-xs text-gray-500 mb-2">Equipment:</p>
                            <div className="flex flex-wrap gap-1">
                              {(savedProject.equipment || []).slice(0, 3).map((item, index) => {
                                const equipment = equipmentDatabase.find(eq => eq.code === item.code);
                                return equipment ? (
                                  <span key={index} className="inline-block px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded">
                                    {equipment.name} ({item.quantity})
                                  </span>
                                ) : null;
                              })}
                              {(savedProject.equipment?.length || 0) > 3 && (
                                <span className="inline-block px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded">
                                  +{(savedProject.equipment?.length || 0) - 3} more
                                </span>
                              )}
                              {(savedProject.customEquipment || []).map((item, index) => (
                                <span key={`custom-${index}`} className="inline-block px-2 py-1 bg-blue-100 text-blue-700 text-xs rounded">
                                  {item.name} (Custom)
                                </span>
                              ))}
                            </div>
                          </div>
                        </div>
                        
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            if (window.confirm('Are you sure you want to delete this saved project?')) {
                              deleteSavedProject(savedProject.id);
                            }
                          }}
                          className="opacity-0 group-hover:opacity-100 p-2 text-red-500 hover:text-red-700 hover:bg-red-50 rounded-lg transition-all"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Role Assignment Tab */}
          {activeTab === 'roles_REMOVED' && (
            <div className="space-y-6">
              <h3 className="text-lg font-semibold text-gray-900">Project Role Assignment</h3>

              <div className="grid grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Producer</label>
                  <select
                    value={project.roles.producer}
                    onChange={(e) => setProject(prev => ({
                      ...prev,
                      roles: { ...prev.roles, producer: e.target.value }
                    }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-black focus:border-black"
                  >
                    <option value="">Select producer</option>
                    <option value="Nadeem">Nadeem Mazahreh</option>
                    <option value="Issa">Issa Sweiss</option>
                    <option value="Kareem">Kareem Qosous</option>
                    <option value="Omar">Omar Bakri</option>
                    <option value="Ammar">Ammar Heis</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Director</label>
                  <select
                    value={project.roles.director}
                    onChange={(e) => setProject(prev => ({
                      ...prev,
                      roles: { ...prev.roles, director: e.target.value }
                    }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-black focus:border-black"
                  >
                    <option value="">Select director</option>
                    <option value="Nadeem">Nadeem Mazahreh</option>
                    <option value="Issa">Issa Sweiss</option>
                    <option value="Kareem">Kareem Qosous</option>
                    <option value="Omar">Omar Bakri</option>
                    <option value="Ammar">Ammar Heis</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Project Manager</label>
                  <select
                    value={project.roles.projectManager}
                    onChange={(e) => setProject(prev => ({
                      ...prev,
                      roles: { ...prev.roles, projectManager: e.target.value }
                    }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-black focus:border-black"
                  >
                    <option value="">Select project manager</option>
                    <option value="Nadeem">Nadeem Mazahreh</option>
                    <option value="Issa">Issa Sweiss</option>
                    <option value="Kareem">Kareem Qosous</option>
                    <option value="Omar">Omar Bakri</option>
                    <option value="Ammar">Ammar Heis</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Junior Project Manager</label>
                  <select
                    value={project.roles.juniorProjectManager}
                    onChange={(e) => setProject(prev => ({
                      ...prev,
                      roles: { ...prev.roles, juniorProjectManager: e.target.value }
                    }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-black focus:border-black"
                  >
                    <option value="">Select junior project manager</option>
                    <option value="Nadeem">Nadeem Mazahreh</option>
                    <option value="Issa">Issa Sweiss</option>
                    <option value="Kareem">Kareem Qosous</option>
                    <option value="Omar">Omar Bakri</option>
                    <option value="Ammar">Ammar Heis</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Accountant</label>
                  <select
                    value={project.roles.accountant}
                    onChange={(e) => setProject(prev => ({
                      ...prev,
                      roles: { ...prev.roles, accountant: e.target.value }
                    }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-black focus:border-black"
                  >
                    <option value="">Select accountant</option>
                    <option value="Nadeem">Nadeem Mazahreh</option>
                    <option value="Issa">Issa Sweiss</option>
                    <option value="Kareem">Kareem Qosous</option>
                    <option value="Omar">Omar Bakri</option>
                    <option value="Ammar">Ammar Heis</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Logistics Manager</label>
                  <select
                    value={project.roles.logisticsManager}
                    onChange={(e) => setProject(prev => ({
                      ...prev,
                      roles: { ...prev.roles, logisticsManager: e.target.value }
                    }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-black focus:border-black"
                  >
                    <option value="">Select logistics manager</option>
                    <option value="Nadeem">Nadeem Mazahreh</option>
                    <option value="Issa">Issa Sweiss</option>
                    <option value="Kareem">Kareem Qosous</option>
                    <option value="Omar">Omar Bakri</option>
                    <option value="Ammar">Ammar Heis</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Noise Control Engineer</label>
                  <select
                    value={project.roles.noiseControlEngineer}
                    onChange={(e) => setProject(prev => ({
                      ...prev,
                      roles: { ...prev.roles, noiseControlEngineer: e.target.value }
                    }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-black focus:border-black"
                  >
                    <option value="">Select noise control engineer</option>
                    <option value="Nadeem">Nadeem Mazahreh</option>
                    <option value="Issa">Issa Sweiss</option>
                    <option value="Kareem">Kareem Qosous</option>
                    <option value="Omar">Omar Bakri</option>
                    <option value="Ammar">Ammar Heis</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Sound System Designer</label>
                  <select
                    value={project.roles.soundSystemDesigner}
                    onChange={(e) => setProject(prev => ({
                      ...prev,
                      roles: { ...prev.roles, soundSystemDesigner: e.target.value }
                    }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-black focus:border-black"
                  >
                    <option value="">Select sound system designer</option>
                    <option value="Nadeem">Nadeem Mazahreh</option>
                    <option value="Issa">Issa Sweiss</option>
                    <option value="Kareem">Kareem Qosous</option>
                    <option value="Omar">Omar Bakri</option>
                    <option value="Ammar">Ammar Heis</option>
                  </select>
                </div>
              </div>

              {/* Role Responsibilities */}
              <div className="space-y-4">
                <h4 className="font-medium text-gray-900">Role Responsibilities</h4>
                
                <div className="grid grid-cols-2 gap-4">
                  <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
                    <h5 className="font-semibold text-gray-800 mb-3">
                      Producer - {project.roles.producer || 'Not Assigned'}
                    </h5>
                    <ul className="space-y-1 text-sm text-gray-600">
                      <li>• Initial client contact and lead qualification</li>
                      <li>• Site visit and requirements gathering</li>
                      <li>• Contract negotiation</li>
                      <li>• Deal Closing</li>
                      <li>• 5% compensation of void sales profit</li>
                    </ul>
                  </div>

                  <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
                    <h5 className="font-semibold text-gray-800 mb-3">
                      Director - {project.roles.director || 'Not Assigned'}
                    </h5>
                    <ul className="space-y-1 text-sm text-gray-600">
                      <li>• Strategic oversight and leadership</li>
                      <li>• Key client relationship management</li>
                      <li>• Project approval and major decisions</li>
                      <li>• Quality assurance and final review</li>
                      <li>• Technology development</li>
                    </ul>
                  </div>

                  <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
                    <h5 className="font-semibold text-gray-800 mb-3">
                      Project Manager - {project.roles.projectManager || 'Not Assigned'}
                    </h5>
                    <ul className="space-y-1 text-sm text-gray-600">
                      <li>• Project planning and timeline creation</li>
                      <li>• Team coordination and task management</li>
                      <li>• Client communication and updates</li>
                      <li>• Risk management and issue resolution</li>
                      <li>• Project deliverables and milestones</li>
                    </ul>
                  </div>

                  <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
                    <h5 className="font-semibold text-gray-800 mb-3">
                      Junior Project Manager - {project.roles.juniorProjectManager || 'Not Assigned'}
                    </h5>
                    <ul className="space-y-1 text-sm text-gray-600">
                      <li>• Assist with project planning and execution</li>
                      <li>• Documentation and reporting</li>
                      <li>• Vendor coordination and follow-up</li>
                      <li>• Quality control and installation support</li>
                      <li>• Administrative tasks and scheduling</li>
                    </ul>
                  </div>

                  <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
                    <h5 className="font-semibold text-gray-800 mb-3">
                      Accountant - {project.roles.accountant || 'Not Assigned'}
                    </h5>
                    <ul className="space-y-1 text-sm text-gray-600">
                      <li>• Financial planning and budgeting</li>
                      <li>• Invoice generation and processing</li>
                      <li>• Payment tracking and collection</li>
                      <li>• Financial reporting and compliance</li>
                      <li>• Cost analysis and profit optimization</li>
                    </ul>
                  </div>

                  <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
                    <h5 className="font-semibold text-gray-800 mb-3">
                      Logistics Manager - {project.roles.logisticsManager || 'Not Assigned'}
                    </h5>
                    <ul className="space-y-1 text-sm text-gray-600">
                      <li>• Equipment procurement and delivery</li>
                      <li>• Inventory management and tracking</li>
                      <li>• Shipping and customs coordination</li>
                      <li>• Site logistics and installation planning</li>
                      <li>• Vendor relationship management</li>
                    </ul>
                  </div>

                  <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
                    <h5 className="font-semibold text-gray-800 mb-3">
                      Noise Control Engineer - {project.roles.noiseControlEngineer || 'Not Assigned'}
                    </h5>
                    <ul className="space-y-1 text-sm text-gray-600">
                      <li>• Acoustic analysis and measurements</li>
                      <li>• Noise control solution design</li>
                      <li>• Environmental compliance assessment</li>
                      <li>• Sound isolation and treatment planning</li>
                      <li>• Technical documentation and reporting</li>
                      <li>• 40% compensation of noise control service cost</li>
                    </ul>
                  </div>

                  <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
                    <h5 className="font-semibold text-gray-800 mb-3">
                      Sound System Designer - {project.roles.soundSystemDesigner || 'Not Assigned'}
                    </h5>
                    <ul className="space-y-1 text-sm text-gray-600">
                      <li>• Technical design and specifications (EASE, SketchUp, AutoCAD)</li>
                      <li>• Equipment selection and configuration</li>
                      <li>• System integration and optimization</li>
                      <li>• Installation supervision and commissioning</li>
                      <li>• Performance testing and calibration</li>
                      <li>• 50% compensation of sound design service cost</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Financial Analysis Tab */}
          {activeTab === 'results' && (
            <div className="space-y-8">
              {/* Header */}
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-gray-900">Financial Analysis</h3>
                <span className="text-xs text-gray-500 bg-gray-100 px-3 py-1 rounded-full">Board Summary</span>
              </div>

              {!isCalculated && (
                <div className="bg-orange-50 border border-orange-200 rounded-xl p-4">
                  <p className="text-orange-800">Please calculate the project first to see financial analysis.</p>
                </div>
              )}

              {isCalculated && calculationResults && !calculationResults.streams && (
                <div className="bg-orange-50 border border-orange-200 rounded-xl p-4">
                  <p className="text-orange-800">This project was calculated with an older version. Click "Calculate" again to refresh the financial analysis.</p>
                </div>
              )}

              {isCalculated && calculationResults && calculationResults.streams && (() => {
                const r = calculationResults;
                const s = r.streams;
                const blendedNetMargin = r.projectSubtotalJOD > 0
                  ? (r.finalProfitJOD / r.projectSubtotalJOD) * 100
                  : 0;
                const soundSystemRows = [
                  { key: 'voidShipped', label: 'Sound System (Shipped)', data: s.voidShipped },
                ];
                const nogahubRows = [
                  { key: 'customShipped', label: 'Custom Equipment (Shipped)', data: s.customShipped },
                  { key: 'customLocal', label: 'Custom Equipment (Local)', data: s.customLocal },
                  { key: 'services', label: 'Services', data: s.services },
                ];
                const soundSystemProfit = s.voidShipped.profit;
                const soundSystemFinalProfit = soundSystemProfit - r.originationFeeJOD;
                const nogahubGrossProfit = s.customShipped.profit + s.customLocal.profit + s.services.profit;

                return (
                  <>
                    {/* ── 1. EXECUTIVE SUMMARY ── */}
                    <div className="bg-gray-900 text-white rounded-xl p-6">
                      <p className="text-xs font-medium text-gray-400 uppercase tracking-widest mb-5">Executive Summary</p>
                      <div className="grid grid-cols-2 sm:grid-cols-5 gap-6">
                        <div>
                          <p className="text-xs text-gray-400">Total Project Value</p>
                          <p className="text-xl font-bold mt-1">{r.projectTotalJOD.toFixed(2)}</p>
                          <p className="text-xs text-gray-500 mt-0.5">{project.includeTax ? 'JOD (incl. VAT)' : 'JOD (VAT not included)'}</p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-400">Total Landed Cost</p>
                          <p className="text-xl font-bold mt-1">{r.doorToDoorCostJOD.toFixed(2)}</p>
                          <p className="text-xs text-gray-500 mt-0.5">JOD incl. customs, shipping &amp; import tax</p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-400">Final Profit</p>
                          <p className="text-xl font-bold mt-1 text-green-400">{r.finalProfitJOD.toFixed(2)}</p>
                          <p className="text-xs text-gray-500 mt-0.5">JOD after origination fee</p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-400">Blended Margin</p>
                          <p className="text-xl font-bold mt-1 text-green-400">{blendedNetMargin.toFixed(1)}%</p>
                          <p className="text-xs text-gray-500 mt-0.5">final profit ÷ subtotal</p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-400">Effective Discount</p>
                          <p className="text-xl font-bold mt-1 text-yellow-400">{r.effectiveDiscountPct.toFixed(1)}%</p>
                          <p className="text-xs text-gray-500 mt-0.5">vs list pricing</p>
                        </div>
                      </div>
                    </div>

                    {/* ── 2. REVENUE ── */}
                    <div className="border border-gray-200 rounded-xl overflow-hidden">
                      <div className="bg-gray-50 px-6 py-3 border-b border-gray-200">
                        <h4 className="font-semibold text-gray-900">Revenue</h4>
                      </div>
                      <div className="p-6 space-y-6">
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 text-sm">
                          <div className="p-4 bg-gray-50 rounded-lg text-center">
                            <p className="text-xs text-gray-500 font-medium">Sound System (MSRP)</p>
                            <p className="text-lg font-bold text-gray-900 mt-1">{r.discountedEquipmentValueJOD.toFixed(2)}</p>
                            <p className="text-xs text-gray-400 mt-0.5">JOD after discount</p>
                          </div>
                          <div className="p-4 bg-gray-50 rounded-lg text-center">
                            <p className="text-xs text-gray-500 font-medium">Custom Items Shipped</p>
                            <p className="text-lg font-bold text-gray-900 mt-1">{s.customShipped.priceOnly.toFixed(2)}</p>
                            <p className="text-xs text-gray-400 mt-0.5">JOD, excl. logistics</p>
                          </div>
                          <div className="p-4 bg-gray-50 rounded-lg text-center">
                            <p className="text-xs text-gray-500 font-medium">Custom Items Local</p>
                            <p className="text-lg font-bold text-gray-900 mt-1">{s.customLocal.revenue.toFixed(2)}</p>
                            <p className="text-xs text-gray-400 mt-0.5">JOD</p>
                          </div>
                          <div className="p-4 bg-gray-50 rounded-lg text-center">
                            <p className="text-xs text-gray-500 font-medium">Services</p>
                            <p className="text-lg font-bold text-gray-900 mt-1">{r.servicesTotal.toFixed(2)}</p>
                            <p className="text-xs text-gray-400 mt-0.5">JOD</p>
                          </div>
                          <div className="p-4 bg-gray-50 rounded-lg text-center">
                            <p className="text-xs text-gray-500 font-medium">Sound System Logistics Pass-through</p>
                            <p className="text-lg font-bold text-gray-900 mt-1">{r.passThroughJOD.toFixed(2)}</p>
                            <p className="text-xs text-gray-400 mt-0.5">billed to client, 0% margin</p>
                          </div>
                          <div className="p-4 bg-gray-50 rounded-lg text-center">
                            <p className="text-xs text-gray-500 font-medium">Custom Shipped Logistics</p>
                            <p className="text-lg font-bold text-gray-900 mt-1">{s.customShipped.logistics.toFixed(2)}</p>
                            <p className="text-xs text-gray-400 mt-0.5">absorbed in item price</p>
                          </div>
                        </div>

                        <div className="grid grid-cols-1 sm:grid-cols-4 gap-4 text-sm border-t border-gray-100 pt-4">
                          <div className="flex justify-between p-3 bg-gray-50 rounded-lg">
                            <span className="text-gray-600">Subtotal (ex-VAT):</span>
                            <span className="font-semibold">{r.projectSubtotalJOD.toFixed(2)} JOD</span>
                          </div>
                          {project.includeTax && (
                            <div className="flex justify-between p-3 bg-gray-50 rounded-lg">
                              <span className="text-gray-600">VAT (16%):</span>
                              <span className="font-semibold">{r.projectTaxJOD.toFixed(2)} JOD</span>
                            </div>
                          )}
                          {project.includeTax && (
                            <div className="flex justify-between p-3 bg-gray-50 rounded-lg">
                              <span className="text-gray-600">VAT to Remit:</span>
                              <span className="font-semibold">{r.vatToRemitJOD.toFixed(2)} JOD</span>
                            </div>
                          )}
                          <div className="flex justify-between p-3 bg-gray-900 text-white rounded-lg">
                            <span className="text-gray-300">Final Total:</span>
                            <span className="font-bold">{r.projectTotalJOD.toFixed(2)} JOD</span>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* ── 3. COSTS ── */}
                    <div className="border border-gray-200 rounded-xl overflow-hidden">
                      <div className="bg-gray-50 px-6 py-3 border-b border-gray-200">
                        <h4 className="font-semibold text-gray-900">Costs</h4>
                      </div>
                      <div className="p-6">
                        <div className="grid grid-cols-2 sm:grid-cols-5 gap-4 text-sm">
                          <div className="text-center p-4 bg-gray-50 rounded-lg">
                            <p className="text-gray-500 font-medium text-xs">Sound System Dealer</p>
                            <p className="text-base font-bold text-gray-900 mt-1">{r.equipmentDealerTotalJOD.toFixed(2)} JOD</p>
                          </div>
                          <div className="text-center p-4 bg-gray-50 rounded-lg">
                            <p className="text-gray-500 font-medium text-xs">Custom Items</p>
                            <p className="text-base font-bold text-gray-900 mt-1">{(s.customShipped.cost + s.customLocal.cost).toFixed(2)} JOD</p>
                            <p className="text-xs text-gray-400">shipped + local</p>
                          </div>
                          <div className="text-center p-4 bg-gray-50 rounded-lg">
                            <p className="text-gray-500 font-medium text-xs">Freight</p>
                            <p className="text-base font-bold text-gray-900 mt-1">{r.shipping.toFixed(2)} JOD</p>
                          </div>
                          <div className="text-center p-4 bg-gray-50 rounded-lg">
                            <p className="text-gray-500 font-medium text-xs">Customs &amp; Duties</p>
                            <p className="text-base font-bold text-gray-900 mt-1">{r.customs.toFixed(2)} JOD</p>
                          </div>
                          <div className="text-center p-4 bg-gray-50 rounded-lg">
                            <p className="text-gray-500 font-medium text-xs">Import Tax</p>
                            <p className="text-base font-bold text-gray-900 mt-1">{r.vatJOD.toFixed(2)} JOD</p>
                          </div>
                        </div>
                        <div className="flex justify-between p-4 mt-4 bg-gray-900 text-white rounded-lg">
                          <span className="text-gray-300 font-medium">Total Landed Cost</span>
                          <span className="font-bold">{r.doorToDoorCostJOD.toFixed(2)} JOD</span>
                        </div>
                      </div>
                    </div>

                    {/* ── 4. SHIPPING & LOGISTICS ── */}
                    <div className="border border-gray-200 rounded-xl overflow-hidden">
                      <div className="bg-gray-50 px-6 py-3 border-b border-gray-200">
                        <h4 className="font-semibold text-gray-900 flex items-center gap-2">
                          <Truck size={16} />
                          Shipping &amp; Logistics
                        </h4>
                      </div>
                      <div className="p-6 space-y-6">
                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-sm">
                          <div className="text-center p-4 bg-gray-50 rounded-lg">
                            <p className="text-gray-500 font-medium text-xs">Total Weight</p>
                            <p className="text-base font-bold text-gray-900 mt-1">{r.totalWeight.toFixed(1)} kg</p>
                            <p className="text-xs text-gray-400">SS {s.voidShipped.weightKg.toFixed(1)}kg + custom {s.customShipped.weightKg.toFixed(1)}kg</p>
                          </div>
                          <div className="text-center p-4 bg-gray-50 rounded-lg">
                            <p className="text-gray-500 font-medium text-xs">Items Shipped</p>
                            <p className="text-base font-bold text-gray-900 mt-1">{s.voidShipped.itemCount + s.customShipped.itemCount}</p>
                            <p className="text-xs text-gray-400">SS {s.voidShipped.itemCount} + custom {s.customShipped.itemCount}</p>
                          </div>
                          <div className="text-center p-4 bg-gray-50 rounded-lg">
                            <p className="text-gray-500 font-medium text-xs">Items Local</p>
                            <p className="text-base font-bold text-gray-900 mt-1">{s.customLocal.itemCount}</p>
                            <p className="text-xs text-gray-400">custom equipment, no freight</p>
                          </div>
                          <div className="text-center p-4 bg-gray-50 rounded-lg">
                            <p className="text-gray-500 font-medium text-xs">Freight Rate</p>
                            <p className="text-base font-bold text-gray-900 mt-1">{shippingRatePerKg.toFixed(2)} JOD/kg</p>
                            <p className="text-xs text-gray-400">+ 35 clearance, 70 transport, 45 DO</p>
                          </div>
                        </div>

                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                          <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 text-sm">
                            <p className="font-medium text-gray-800 mb-3 text-center">Sound System Logistics</p>
                            <div className="grid grid-cols-3 gap-3 text-center">
                              <div>
                                <p className="text-xs text-gray-500">Shipping</p>
                                <p className="text-base font-bold text-gray-900 mt-0.5">{s.voidShipped.shipping.toFixed(2)}</p>
                              </div>
                              <div>
                                <p className="text-xs text-gray-500">Customs</p>
                                <p className="text-base font-bold text-gray-900 mt-0.5">{s.voidShipped.customs.toFixed(2)}</p>
                              </div>
                              <div>
                                <p className="text-xs text-gray-500">Total</p>
                                <p className="text-base font-bold text-gray-900 mt-0.5">{r.passThroughJOD.toFixed(2)}</p>
                              </div>
                            </div>
                          </div>
                          <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 text-sm">
                            <p className="font-medium text-gray-800 mb-3 text-center">Custom Equipment Logistics</p>
                            <div className="grid grid-cols-3 gap-3 text-center">
                              <div>
                                <p className="text-xs text-gray-500">Shipping</p>
                                <p className="text-base font-bold text-gray-900 mt-0.5">{s.customShipped.shipping.toFixed(2)}</p>
                              </div>
                              <div>
                                <p className="text-xs text-gray-500">Customs</p>
                                <p className="text-base font-bold text-gray-900 mt-0.5">{s.customShipped.customs.toFixed(2)}</p>
                              </div>
                              <div>
                                <p className="text-xs text-gray-500">Total</p>
                                <p className="text-base font-bold text-gray-900 mt-0.5">{s.customShipped.logistics.toFixed(2)}</p>
                              </div>
                            </div>
                          </div>
                        </div>
                        <p className="text-xs text-gray-400">Import Tax ({r.vatJOD.toFixed(2)} JOD) is paid at customs on the combined pool — see Costs section.</p>
                      </div>
                    </div>

                    {/* ── 5. PROFIT ANALYSIS ── */}
                    <div className="border border-gray-200 rounded-xl overflow-hidden">
                      <div className="bg-gray-50 px-6 py-3 border-b border-gray-200">
                        <h4 className="font-semibold text-gray-900">Profit Analysis</h4>
                      </div>
                      <div className="p-6 space-y-8">
                        {/* Sound System */}
                        <div className="space-y-4">
                          <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Sound System</p>
                          <div className="overflow-x-auto">
                            <table className="w-full text-sm">
                              <thead>
                                <tr className="text-left text-xs text-gray-500 border-b border-gray-200">
                                  <th className="py-2 pr-4 font-medium">Stream</th>
                                  <th className="py-2 px-4 font-medium text-right">Revenue</th>
                                  <th className="py-2 px-4 font-medium text-right">Cost</th>
                                  <th className="py-2 px-4 font-medium text-right">Profit</th>
                                  <th className="py-2 pl-4 font-medium text-right">Margin</th>
                                </tr>
                              </thead>
                              <tbody>
                                {soundSystemRows.map(({ key, label, data }) => {
                                  const margin = data.revenue > 0 ? (data.profit / data.revenue) * 100 : 0;
                                  return (
                                    <tr key={key} className="border-b border-gray-100 last:border-0">
                                      <td className="py-2 pr-4 text-gray-800">{label}</td>
                                      <td className="py-2 px-4 text-right text-gray-900">{data.revenue.toFixed(2)}</td>
                                      <td className="py-2 px-4 text-right text-gray-500">{data.cost.toFixed(2)}</td>
                                      <td className={`py-2 px-4 text-right font-medium ${data.profit >= 0 ? 'text-green-600' : 'text-red-600'}`}>{data.profit.toFixed(2)}</td>
                                      <td className="py-2 pl-4 text-right text-gray-500">{margin.toFixed(1)}%</td>
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </table>
                          </div>
                          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 text-sm">
                            <div className="flex justify-between p-3 bg-gray-50 rounded-lg">
                              <span className="text-gray-600">Gross Profit:</span>
                              <span className="font-semibold">{soundSystemProfit.toFixed(2)} JOD</span>
                            </div>
                            <div className="flex justify-between p-3 bg-gray-50 rounded-lg">
                              <span className="text-gray-600">Origination Fee (2% of SS revenue):</span>
                              <span className="font-semibold">−{r.originationFeeJOD.toFixed(2)} JOD</span>
                            </div>
                            <div className="flex justify-between p-3 bg-gray-900 text-white rounded-lg">
                              <span className="text-gray-300">Final Profit:</span>
                              <span className="font-bold text-green-400">{soundSystemFinalProfit.toFixed(2)} JOD</span>
                            </div>
                          </div>
                        </div>

                        {/* Nogahub */}
                        <div className="space-y-4 border-t border-gray-100 pt-6">
                          <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Nogahub</p>
                          <div className="overflow-x-auto">
                            <table className="w-full text-sm">
                              <thead>
                                <tr className="text-left text-xs text-gray-500 border-b border-gray-200">
                                  <th className="py-2 pr-4 font-medium">Stream</th>
                                  <th className="py-2 px-4 font-medium text-right">Revenue</th>
                                  <th className="py-2 px-4 font-medium text-right">Cost</th>
                                  <th className="py-2 px-4 font-medium text-right">Profit</th>
                                  <th className="py-2 pl-4 font-medium text-right">Margin</th>
                                </tr>
                              </thead>
                              <tbody>
                                {nogahubRows.map(({ key, label, data }) => {
                                  const margin = data.revenue > 0 ? (data.profit / data.revenue) * 100 : 0;
                                  return (
                                    <tr key={key} className="border-b border-gray-100 last:border-0">
                                      <td className="py-2 pr-4 text-gray-800">{label}</td>
                                      <td className="py-2 px-4 text-right text-gray-900">{data.revenue.toFixed(2)}</td>
                                      <td className="py-2 px-4 text-right text-gray-500">{data.cost.toFixed(2)}</td>
                                      <td className={`py-2 px-4 text-right font-medium ${data.profit >= 0 ? 'text-green-600' : 'text-red-600'}`}>{data.profit.toFixed(2)}</td>
                                      <td className="py-2 pl-4 text-right text-gray-500">{margin.toFixed(1)}%</td>
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </table>
                          </div>
                          <div className="flex justify-end">
                            <div className="flex justify-between gap-6 p-3 bg-gray-900 text-white rounded-lg min-w-[240px]">
                              <span className="text-gray-300">Final Profit:</span>
                              <span className="font-bold text-green-400">{nogahubGrossProfit.toFixed(2)} JOD</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* ── 6. KEY PERFORMANCE METRICS ── */}
                    <div className="border border-gray-200 rounded-xl overflow-hidden">
                      <div className="bg-gray-50 px-6 py-3 border-b border-gray-200">
                        <h4 className="font-semibold text-gray-900">Key Performance Metrics</h4>
                      </div>
                      <div className="p-6">
                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                          <div className="text-center p-4 bg-gray-50 rounded-lg">
                            <p className="text-xs text-gray-600 font-medium">Equipment Gross Margin</p>
                            <p className="text-2xl font-bold text-gray-800 mt-1">{(r.equipmentGrossMargin * 100).toFixed(1)}%</p>
                            <p className="text-xs text-gray-400 mt-0.5">Sound System profit ÷ MSRP value</p>
                          </div>
                          <div className="text-center p-4 bg-gray-50 rounded-lg">
                            <p className="text-xs text-gray-600 font-medium">Blended Net Margin</p>
                            <p className="text-2xl font-bold text-gray-800 mt-1">{blendedNetMargin.toFixed(1)}%</p>
                            <p className="text-xs text-gray-400 mt-0.5">final profit ÷ subtotal</p>
                          </div>
                          <div className="text-center p-4 bg-gray-50 rounded-lg">
                            <p className="text-xs text-gray-600 font-medium">Equipment Markup</p>
                            <p className="text-2xl font-bold text-gray-800 mt-1">
                              {r.equipmentDealerTotalJOD > 0
                                ? (((r.equipmentMsrpValueJOD - r.equipmentDealerTotalJOD) / r.equipmentDealerTotalJOD) * 100).toFixed(1)
                                : '0.0'}%
                            </p>
                            <p className="text-xs text-gray-400 mt-0.5">MSRP over dealer cost</p>
                          </div>
                          <div className="text-center p-4 bg-gray-50 rounded-lg">
                            <p className="text-xs text-gray-600 font-medium">Effective Discount</p>
                            <p className="text-2xl font-bold text-gray-800 mt-1">{r.effectiveDiscountPct.toFixed(1)}%</p>
                            <p className="text-xs text-gray-400 mt-0.5">vs list pricing</p>
                          </div>
                          <div className="text-center p-4 bg-gray-50 rounded-lg">
                            <p className="text-xs text-gray-600 font-medium">Service Revenue %</p>
                            <p className="text-2xl font-bold text-gray-800 mt-1">
                              {r.projectSubtotalJOD > 0
                                ? ((r.servicesTotal / r.projectSubtotalJOD) * 100).toFixed(1)
                                : '0.0'}%
                            </p>
                            <p className="text-xs text-gray-400 mt-0.5">of subtotal</p>
                          </div>
                          <div className="text-center p-4 bg-gray-50 rounded-lg">
                            <p className="text-xs text-gray-600 font-medium">Cost-to-Revenue</p>
                            <p className="text-2xl font-bold text-gray-800 mt-1">
                              {r.projectSubtotalJOD > 0
                                ? ((r.doorToDoorCostExclTax020JOD / r.projectSubtotalJOD) * 100).toFixed(1)
                                : '0.0'}%
                            </p>
                            <p className="text-xs text-gray-400 mt-0.5">landed cost ÷ subtotal</p>
                          </div>
                          <div className="text-center p-4 bg-gray-50 rounded-lg">
                            <p className="text-xs text-gray-600 font-medium">Origination Fee</p>
                            <p className="text-2xl font-bold text-gray-800 mt-1">{r.originationFeeJOD.toFixed(2)}</p>
                            <p className="text-xs text-gray-400 mt-0.5">JOD, 2% of SS revenue</p>
                          </div>
                          <div className="text-center p-4 bg-gray-50 rounded-lg">
                            <p className="text-xs text-gray-600 font-medium">Final Profit</p>
                            <p className="text-2xl font-bold text-gray-800 mt-1">{r.finalProfitJOD.toFixed(2)}</p>
                            <p className="text-xs text-gray-400 mt-0.5">JOD after origination fee</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </>
                );
              })()}
            </div>
          )}

          {/* Business Entities Tab */}
          {activeTab === 'entities' && (
            <div className="space-y-6">
              <h3 className="text-lg font-semibold text-gray-900">Business Structure & Entities</h3>
              
              <div className="grid grid-cols-1 gap-6">
                {Object.entries(businessEntities).map(([key, entity]) => (
                  <div key={key} className="border border-gray-200 rounded-xl p-6 bg-gray-50">
                    <h4 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
                      <Building2 size={24} className="mr-2" />
                      {entity.name}
                    </h4>
                    
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                      <div>
                        <h5 className="font-medium text-gray-800 mb-2">Ownership Structure</h5>
                        <div className="space-y-2">
                          {Object.entries(entity.ownership).map(([owner, percentage]) => (
                            <div key={owner} className="flex justify-between items-center p-2 bg-white rounded border">
                              <span className="capitalize font-medium">{owner}:</span>
                              <span className="font-semibold">{(percentage * 100).toFixed(1)}%</span>
                            </div>
                          ))}
                        </div>
                      </div>
                      
                      <div>
                        <h5 className="font-medium text-gray-800 mb-2">Core Services</h5>
                        <ul className="space-y-1">
                          {entity.services.map((service, index) => (
                            <li key={index} className="text-sm text-gray-600 p-2 bg-white rounded border">
                              • {service}
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Corporate Structure Overview */}
              <div className="bg-black text-white rounded-xl p-6">
                <h4 className="text-xl font-semibold mb-4">Corporate Umbrella</h4>
                <div className="text-sm space-y-2">
                  <p><strong>Deep Sound for Technical Consultations</strong></p>
                  <p>Housing Bank Complex 93 - Ground Floor 102</p>
                  <p>Q. Nour St. - Welbdeh - Amman - Jordan</p>
                  <p>Phone: +962 (0) 795144821</p>
                  <p className="mt-4 text-gray-300">Legal umbrella for all business operations in Jordan</p>
                </div>
              </div>

              {/* Key Personnel */}
              <div className="bg-gray-50 border border-gray-200 rounded-xl p-6">
                <h4 className="text-xl font-semibold text-gray-900 mb-4">Key Personnel</h4>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  <div>
                    <h5 className="font-medium text-gray-800 mb-2">Core Leadership</h5>
                    <div className="space-y-2">
                      <div className="p-3 bg-white rounded border">
                        <p className="font-semibold">Nadeem Mazahreh</p>
                        <p className="text-sm text-gray-600">Co-founder, Strategic Partnerships, Business Development</p>
                      </div>
                      <div className="p-3 bg-white rounded border">
                        <p className="font-semibold">Issa Sweiss</p>
                        <p className="text-sm text-gray-600">Co-founder, Operations Manager, Technical Expertise</p>
                      </div>
                      <div className="p-3 bg-white rounded border">
                        <p className="font-semibold">Omar</p>
                        <p className="text-sm text-gray-600">Legal Advisor, Board Member, Contract Management</p>
                      </div>
                    </div>
                  </div>
                  
                  <div>
                    <h5 className="font-medium text-gray-800 mb-2">Operational Team</h5>
                    <div className="space-y-2">
                      <div className="p-3 bg-white rounded border">
                        <p className="font-semibold">Ammar Heis</p>
                        <p className="text-sm text-gray-600">Finance Manager - Accounting, Budgeting, Compliance</p>
                      </div>
                      <div className="p-3 bg-white rounded border">
                        <p className="font-semibold">Kareem Qosous</p>
                        <p className="text-sm text-gray-600">Sales Support, Operations Intern</p>
                      </div>
                      <div className="p-3 bg-white rounded border">
                        <p className="font-semibold">Dima Amer</p>
                        <p className="text-sm text-gray-600">Logistics Manager</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Documents Tab */}
          {activeTab === 'documents' && (
            <div className="space-y-6">
              <h3 className="text-lg font-semibold text-gray-900">Project Documentation</h3>

              {!isCalculated && !ncCalculated && (
                <div className="bg-orange-50 border border-orange-200 rounded-xl p-4">
                  <p className="text-orange-800">Please calculate a project first to generate documents.</p>
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Sound Design Quotation */}
                {isCalculated && calculationResults && (
                  <div className="border border-gray-200 rounded-xl p-4">
                    <h4 className="font-semibold text-gray-900 mb-3">Sound Design Quotation</h4>
                    <div className="bg-white border rounded-lg p-4 text-sm space-y-2">
                      <div className="border-b pb-2 mb-3">
                        <h5 className="font-bold">Deep Sound For Technical Consultations</h5>
                        <p className="text-xs text-gray-600">Housing Bank Complex 93 - Ground Floor 102</p>
                        <p className="text-xs text-gray-600">Q. Nour St. - Welbdeh - Amman - Jordan</p>
                        <p className="text-xs text-gray-600">+962 (0) 795144821</p>
                        <p className="text-xs text-gray-600">Website: www.nogahub.com</p>
                      </div>
                      
                      <div className="flex justify-between text-xs">
                        <span>Client: {project.clientName || 'Client Name'}</span>
                        <span>Date: {new Date().toLocaleDateString()}</span>
                      </div>
                      <div className="text-xs">Project: {project.projectName || 'Project Name'}</div>
                      
                      <div className="mt-4">
                        <h6 className="font-semibold mb-2">Void Acoustics Equipment:</h6>
                        {calculationResults.equipmentDetails.map((item, index) => (
                          <div key={index} className="flex justify-between text-xs">
                            <span>{item.name} x{item.quantity}</span>
                            <span>{Math.round(item.finalTotalJOD) || 'N/A'} JOD</span>
                          </div>
                        ))}
                        {calculationResults.customEquipmentDetails && calculationResults.customEquipmentDetails.length > 0 && (
                          <>
                            <h6 className="font-semibold mt-3 mb-2">Custom Equipment:</h6>
                            {calculationResults.customEquipmentDetails.map((item, index) => (
                              <div key={index} className="flex justify-between text-xs">
                                <span>{item.name} x{item.quantity}</span>
                                <span>{Math.round(item.finalTotalJOD) || 'N/A'} JOD</span>
                              </div>
                            ))}
                          </>
                        )}
                        
                        {calculationResults.servicesTotal > 0 && (
                          <>
                            <h6 className="font-semibold mt-3 mb-2">Professional Services:</h6>
                            {project.services.commissioning.enabled && (
                              <div className="flex justify-between text-xs">
                                <span>Sub-contracting Commissioning</span>
                                <span>{Math.round(calculationResults.commissioningServiceCost)} JOD</span>
                              </div>
                            )}
                            {project.services.noiseControl.enabled && (
                              <div className="flex justify-between text-xs">
                                <span>Noise Control Studies</span>
                                <span>{Math.round(calculationResults.noiseControlServiceCost)} JOD</span>
                              </div>
                            )}
                            {project.services.soundDesign.enabled && (
                              <div className="flex justify-between text-xs">
                                <span>Sound System Design</span>
                                <span>{Math.round(calculationResults.soundDesignServiceCost)} JOD</span>
                              </div>
                            )}
                            {project.services.projectManagement && (
                              <div className="flex justify-between text-xs">
                                <span>Project Management</span>
                                <span>{(calculationResults.equipmentDealerTotalJOD * servicePricing.projectManagement).toFixed(2)} JOD</span>
                              </div>
                            )}
                            {project.customServices.map((service, index) => (
                              <div key={index} className="flex justify-between text-xs">
                                <span>{service.name}</span>
                                <span>{service.price.toFixed(2)} JOD</span>
                              </div>
                            ))}
                          </>
                        )}

                        <div className="border-t mt-3 pt-2">
                          <div className="flex justify-between text-xs">
                            <span>Subtotal:</span>
                            <span>{Math.round((calculationResults.equipmentTotalJODBeforeDiscount || 0) + (calculationResults.customEquipmentTotalJOD || 0) + (calculationResults.servicesTotal || 0))} JOD</span>
                          </div>
                          {project.globalDiscount > 0 && (
                            <div className="flex justify-between text-xs text-red-600">
                              <span>Equipment Discount:</span>
                              <span>-{(calculationResults.discountAmount || 0).toFixed(2)} JOD</span>
                            </div>
                          )}
                          {project.globalDiscount > 0 && project.includeTax && (
                            <div className="flex justify-between text-xs">
                              <span>Subtotal:</span>
                              <span>{Math.round(calculationResults.projectSubtotalJOD || 0)} JOD</span>
                            </div>
                          )}
                          {project.includeTax && (
                            <div className="flex justify-between text-xs">
                              <span>VAT (16%):</span>
                              <span>{(calculationResults.projectTaxJOD || 0).toFixed(2)} JOD</span>
                            </div>
                          )}
                          <div className="flex justify-between font-semibold">
                            <span>Total:</span>
                            <span>{Math.round(calculationResults.projectTotalJOD || 0)} JOD</span>
                          </div>
                        </div>
                      </div>
                    </div>

                    <button 
                      onClick={() => downloadQuotationPDF(calculationResults, project)}
                      className="mt-3 w-full flex items-center justify-center space-x-2 px-4 py-2 bg-black text-white rounded-lg hover:bg-gray-800 transition-colors"
                    >
                      <Download size={16} />
                      <span>Download Quotation PDF</span>
                    </button>
                  </div>
                )}

                {/* Custom Quotation */}
                {ncCalculated && ncResults && (
                    <div className="border border-gray-200 rounded-xl p-4">
                      <h4 className="font-semibold text-gray-900 mb-3">Custom Quotation</h4>
                      <div className="bg-white border rounded-lg p-4 text-sm space-y-2">
                        <div className="border-b pb-2 mb-3">
                          <h5 className="font-bold">Deep Sound For Technical Consultations</h5>
                          <p className="text-xs text-gray-600">Housing Bank Complex 93 - Ground Floor 102</p>
                          <p className="text-xs text-gray-600">Q. Nour St. - Welbdeh - Amman - Jordan</p>
                          <p className="text-xs text-gray-600">+962 (0) 795144821</p>
                        </div>

                        <div className="flex justify-between text-xs">
                          <span>Client: {customQuotation.clientName || 'Client Name'}</span>
                          <span>Date: {new Date().toLocaleDateString()}</span>
                        </div>
                        <div className="text-xs">Project: {customQuotation.projectName || 'Project Name'}</div>

                        <div className="mt-4">
                          <h6 className="font-semibold mb-2">Custom Equipment:</h6>
                          {ncResults.items.map((item, index) => (
                            <div key={index} className="flex justify-between text-xs">
                              <span>{item.name} x{item.quantity}</span>
                              <span>{Math.round((Number(item.clientPrice) || 0) * (Number(item.quantity) || 0))} JOD</span>
                            </div>
                          ))}

                          <div className="border-t mt-3 pt-2">
                            <div className="flex justify-between text-xs">
                              <span>Subtotal:</span>
                              <span>{Math.round(ncResults.totalRevenueBeforeDiscount || 0)} JOD</span>
                            </div>
                            {customQuotation.globalDiscount > 0 && (
                              <div className="flex justify-between text-xs text-red-600">
                                <span>Discount:</span>
                                <span>-{Math.round(ncResults.discountAmount || 0)} JOD</span>
                              </div>
                            )}
                            {customQuotation.globalDiscount > 0 && customQuotation.includeTax && (
                              <div className="flex justify-between text-xs">
                                <span>Subtotal:</span>
                                <span>{Math.round(ncResults.subtotal || 0)} JOD</span>
                              </div>
                            )}
                            {customQuotation.includeTax && (
                              <div className="flex justify-between text-xs">
                                <span>VAT (16%):</span>
                                <span>{Math.round(ncResults.tax || 0)} JOD</span>
                              </div>
                            )}
                            <div className="flex justify-between font-semibold">
                              <span>Total:</span>
                              <span>{Math.round(ncResults.total || 0)} JOD</span>
                            </div>
                          </div>
                        </div>
                      </div>

                      <button
                        onClick={downloadCustomPDF}
                        className="mt-3 w-full flex items-center justify-center space-x-2 px-4 py-2 bg-black text-white rounded-lg hover:bg-gray-800 transition-colors"
                      >
                        <Download size={16} />
                        <span>Download Custom Quotation PDF</span>
                      </button>
                    </div>
                  )}

                {/* Sound Design Invoice */}
                {isCalculated && calculationResults && (
                  <div className="border border-gray-200 rounded-xl p-4">
                    <h4 className="font-semibold text-gray-900 mb-3">Sound Design Invoice</h4>
                    <div className="bg-white border rounded-lg p-4 text-sm space-y-2">
                      <div className="border-b pb-2 mb-3">
                        <h5 className="font-bold">Deep Sound For Technical Consultation LLC</h5>
                        <p className="text-xs text-gray-600">Housing Bank Complex 93 - Ground Floor 102</p>
                        <p className="text-xs text-gray-600">Q. Nour St. - Welbdeh - Amman - Jordan</p>
                        <p className="text-xs text-gray-600">Tax Reg No: 40261328</p>
                      </div>
                      <div className="flex justify-between text-xs">
                        <span>Bill To: {project.clientName || 'Client Name'}</span>
                        <span>Due: on receipt</span>
                      </div>
                      <div className="text-xs">Project: {project.projectName || 'Project Name'}</div>
                      <div className="border-t mt-3 pt-2">
                        <div className="flex justify-between text-xs">
                          <span>Subtotal:</span>
                          <span>{(calculationResults.projectSubtotalJOD || 0).toFixed(2)} JOD</span>
                        </div>
                        {project.includeTax && (
                          <div className="flex justify-between text-xs">
                            <span>VAT (16%):</span>
                            <span>{(calculationResults.projectTaxJOD || 0).toFixed(2)} JOD</span>
                          </div>
                        )}
                        <div className="flex justify-between font-semibold">
                          <span>Total Due:</span>
                          <span>{Math.round(calculationResults.projectTotalJOD || 0)} JOD</span>
                        </div>
                      </div>
                      <div className="mt-3 pt-2 border-t text-xs text-gray-500">
                        <p>Bank: Etihad Bank · CliQ: Deepsound</p>
                        <p>IBAN: JO37UBSI1200000310179526015101</p>
                      </div>
                    </div>
                    <button
                      onClick={() => downloadSoundDesignInvoicePDF(calculationResults, project)}
                      className="mt-3 w-full flex items-center justify-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                    >
                      <Download size={16} />
                      <span>Download Invoice PDF</span>
                    </button>
                  </div>
                )}

                {/* Custom Invoice */}
                {ncCalculated && ncResults && (
                  <div className="border border-gray-200 rounded-xl p-4">
                    <h4 className="font-semibold text-gray-900 mb-3">Custom Invoice</h4>
                    <div className="bg-white border rounded-lg p-4 text-sm space-y-2">
                      <div className="border-b pb-2 mb-3">
                        <h5 className="font-bold">Deep Sound For Technical Consultation LLC</h5>
                        <p className="text-xs text-gray-600">Housing Bank Complex 93 - Ground Floor 102</p>
                        <p className="text-xs text-gray-600">Q. Nour St. - Welbdeh - Amman - Jordan</p>
                        <p className="text-xs text-gray-600">Tax Reg No: 40261328</p>
                      </div>
                      <div className="flex justify-between text-xs">
                        <span>Bill To: {customQuotation.clientName || 'Client Name'}</span>
                        <span>Due: on receipt</span>
                      </div>
                      <div className="text-xs">Project: {customQuotation.projectName || 'Project Name'}</div>
                      <div className="border-t mt-3 pt-2">
                        <div className="flex justify-between text-xs">
                          <span>Subtotal:</span>
                          <span>{(ncResults.subtotal || 0).toFixed(2)} {customQuotation.currency || 'JOD'}</span>
                        </div>
                        {customQuotation.includeTax && (
                          <div className="flex justify-between text-xs">
                            <span>VAT (16%):</span>
                            <span>{(ncResults.tax || 0).toFixed(2)} {customQuotation.currency || 'JOD'}</span>
                          </div>
                        )}
                        <div className="flex justify-between font-semibold">
                          <span>Total Due:</span>
                          <span>{(ncResults.total || 0).toFixed(2)} {customQuotation.currency || 'JOD'}</span>
                        </div>
                      </div>
                      <div className="mt-3 pt-2 border-t text-xs text-gray-500">
                        <p>Bank: Etihad Bank · CliQ: Deepsound</p>
                        <p>IBAN: JO37UBSI1200000310179526015101</p>
                      </div>
                    </div>
                    <button
                      onClick={downloadCustomInvoicePDF}
                      className="mt-3 w-full flex items-center justify-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                    >
                      <Download size={16} />
                      <span>Download Invoice PDF</span>
                    </button>
                  </div>
                )}

                  {/* Void UK Purchase Order - Admin Only */}
                  {userRole === 'admin' && isCalculated && calculationResults && (
                  <div className="border border-gray-200 rounded-xl p-4">
                    <h4 className="font-semibold text-gray-900 mb-3">Void UK Purchase Order</h4>
                    <div className="bg-white border rounded-lg p-6 text-sm">
                      {/* Header Section */}
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mb-4">
                        <div>
                          <h5 className="text-lg font-bold mb-1">Deep Sound For Technical Consultation LLC</h5>
                        </div>
                        <div className="text-right">
                          <div className="bg-gray-700 text-white px-3 py-2 text-center font-bold mb-4 text-sm">
                            PURCHASE ORDER
                          </div>
                          <div className="text-xs space-y-1">
                            <p><strong>Document Number:</strong> {(() => {
                              const now = new Date();
                              const year = now.getFullYear().toString().slice(-2);
                              const month = String(now.getMonth() + 1).padStart(2, '0');
                              const day = String(now.getDate()).padStart(2, '0');
                              return `20${year}${month}${day}1`;
                            })()}</p>
                            <p><strong>Document Date:</strong> {new Date().toLocaleDateString('en-GB')}</p>
                            <p><strong>Page:</strong> 1/1</p>
                            <div className="mt-3">
                              <p><strong>Customer No.:</strong> _____________</p>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Bill To Section */}
                      <div className="mb-4">
                        <p className="font-semibold mb-2">Bill To:</p>
                        <div className="text-xs space-y-1">
                          <p className="font-medium">Deep Sound For Technical Consultation LLC</p>
                          <p>Trading Name: Nogahub</p>
                          <p>Housing Bank Complex 93 - Ground Floor 102</p>
                          <p>Q. Nour St. - Welbdeh - Amman - Jordan</p>
                        </div>
                      </div>

                      {/* Ship To Section */}
                      <div className="mb-4">
                        <p className="font-semibold mb-2">Ship To:</p>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-xs">
                          <div>
                            <p><strong>Company:</strong> Deep Sound For Technical Consultation LLC</p>
                            <p><strong>Contact Person:</strong> Tarik Zuraikat</p>
                            <p><strong>Address:</strong> 15 Al rojoum, Downtown Amman, 11118, Jordan</p>
                            <p><strong>City, Country:</strong> Amman, Jordan</p>
                            <p><strong>Phone:</strong> +96779061007</p>
                            <p><strong>Email:</strong> T.zuraikat@moab-llift.com</p>
                          </div>
                        </div>
                      </div>

                      {/* Vendor Contact */}
                      <div className="mb-6">
                        <p className="font-semibold mb-2">Vendor Contact:</p>
                        <div className="text-xs space-y-1">
                          <p>Void Acoustics Research Ltd.</p>
                          <p>Unit L, The Fulcrum</p>
                          <p>Vantage Way</p>
                          <p>Poole, Dorset, BH12 4NU</p>
                          <p>United Kingdom</p>
                        </div>
                      </div>

                      {/* Delivery Info */}
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mb-6 bg-gray-50 p-3">
                        <div>
                          <p className="font-semibold text-sm">Delivery Date: {(() => {
                            const deliveryDate = new Date();
                            deliveryDate.setDate(deliveryDate.getDate() + 14); // 2 weeks from now
                            return deliveryDate.toLocaleDateString('en-GB');
                          })()}</p>
                        </div>
                        <div className="text-right">
                          <p className="font-semibold text-sm">Currency: USD</p>
                        </div>
                      </div>

                      {/* Equipment Table */}
                      <div className="mb-6 overflow-x-auto">
                        <table className="w-full border-collapse border border-gray-300 text-xs">
                          <thead>
                            <tr className="bg-gray-100">
                              <th className="border border-gray-300 p-2 text-left w-12">#</th>
                              <th className="border border-gray-300 p-2 text-left">Description</th>
                              <th className="border border-gray-300 p-2 text-center w-16">Qty</th>
                              <th className="border border-gray-300 p-2 text-right w-24">Unit Price</th>
                              <th className="border border-gray-300 p-2 text-right w-24">Total</th>
                            </tr>
                          </thead>
                          <tbody>
                            {calculationResults.equipmentDetails
                              .filter(item => {
                                const equipment = equipmentDatabase.find(eq => eq.name === item.name);
                                return equipment && equipment.category === 'void';
                              })
                              .map((item, index) => (
                              <tr key={index}>
                                <td className="border border-gray-300 p-2 text-center">{String(index + 1).padStart(3, '0')}</td>
                                <td className="border border-gray-300 p-2">
                                  <div className="font-medium">{item.name}</div>
                                  <div className="text-gray-500 text-xs">Delivery Date: {(() => {
                                    const deliveryDate = new Date();
                                    deliveryDate.setDate(deliveryDate.getDate() + 14);
                                    return deliveryDate.toLocaleDateString('en-GB');
                                  })()}</div>
                                </td>
                                <td className="border border-gray-300 p-2 text-center">{item.quantity}</td>
                                <td className="border border-gray-300 p-2 text-right">${(parseFloat(item.dealerPriceUSD || item.dealerUSD) || 0).toFixed(2)}</td>
                                <td className="border border-gray-300 p-2 text-right">${(parseFloat(item.dealerPriceUSD || item.dealerUSD || 0) * parseFloat(item.quantity || 0)).toFixed(2)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>

                      {/* Totals Section */}
                      <div className="text-right mb-6">
                        <div className="inline-block text-sm space-y-2">
                          <div className="flex justify-between w-64">
                            <span>Total Amount:</span>
                            <span className="font-semibold">${(() => {
                              const voidEquipmentTotal = calculationResults.equipmentDetails
                                .filter(item => {
                                  const equipment = equipmentDatabase.find(eq => eq.name === item.name);
                                  return equipment && equipment.category === 'void';
                                })
                                .reduce((sum, item) => sum + ((item.dealerPriceUSD || item.dealerUSD || 0) * (item.quantity || 0)), 0);
                              return voidEquipmentTotal.toFixed(2);
                            })()}</span>
                          </div>
                        </div>
                      </div>

                      {/* Description */}
                      <div className="mb-6 text-xs">
                        <p className="mb-2">Professional audio equipment purchase for manufacturing facility.</p>
                        <p>Based on Purchase Request {(() => {
                          const now = new Date();
                          const year = now.getFullYear().toString().slice(-2);
                          const month = String(now.getMonth() + 1).padStart(2, '0');
                          const day = String(now.getDate()).padStart(2, '0');
                          return `20${year}${month}${day}1`;
                        })()}.</p>
                      </div>

                      {/* Special Instructions */}
                      <div className="mb-6">
                        <p className="font-semibold text-sm mb-2">Special Instructions:</p>
                        <div className="text-xs space-y-1 pl-4">
                          <p>• Invoice to: Deep Sound For Technical Consultation LLC (Bill To address above)</p>
                          <p>• Coordinate delivery schedule with shipping company contact person</p>
                        </div>
                      </div>

                      {/* Signature Section */}
                      <div className="border-t pt-4">
                        <p className="font-semibold text-sm mb-4">Confirmation of Purchase Order</p>
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 text-xs">
                          <div>
                            <p className="font-medium mb-2">Name</p>
                            <div className="border-b border-gray-400 h-6 mb-2"></div>
                          </div>
                          <div>
                            <p className="font-medium mb-2">Signature</p>
                            <div className="border-b border-gray-400 h-6 mb-2"></div>
                          </div>
                          <div>
                            <p className="font-medium mb-2">Date</p>
                            <div className="border-b border-gray-400 h-6 mb-2"></div>
                          </div>
                        </div>
                      </div>

                      {/* Footer */}
                      <div className="text-center text-xs text-gray-500 mt-6 pt-4 border-t">
                        <p className="font-medium">Deep Sound For Technical Consultation LLC</p>
                        <p>Phone: +962 (0) 795 4468 | Email: purchasing@nogahub.jo</p>
                        <p>Website: www.nogahub.com</p>
                      </div>
                    </div>
                    
                    <button 
                      onClick={() => downloadPurchaseOrderPDF(calculationResults, project)}
                      className="mt-3 w-full flex items-center justify-center space-x-2 px-4 py-2 bg-black text-white rounded-lg hover:bg-gray-800 transition-colors"
                    >
                      <Download size={16} />
                      <span>Download PO for Void UK</span>
                    </button>
                  </div>
                  )}
              </div>

              {/* Action Items */}
              {isCalculated && calculationResults && (
                <div className="bg-orange-50 border border-orange-200 rounded-xl p-4">
                  <h4 className="font-semibold text-gray-900 mb-3">Next Steps & Action Items</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                    <div>
                      <h6 className="font-medium text-gray-800 mb-2">Immediate Actions</h6>
                      <ul className="space-y-1 text-gray-600">
                        <li>• Send quotation to client via email</li>
                        <li>• Schedule site visit and technical assessment</li>
                        <li>• Prepare contract documents (Omar)</li>
                        <li>• Set up project tracking system (Ammar)</li>
                        <li>• Assign Producer and Project Manager roles</li>
                        <li>• Create 3D models and technical drawings</li>
                      </ul>
                    </div>
                    <div>
                      <h6 className="font-medium text-gray-800 mb-2">Post-Approval Actions</h6>
                      <ul className="space-y-1 text-gray-600">
                        <li>• Process equipment order to Void Acoustics UK</li>
                        <li>• Arrange international shipping & logistics</li>
                        <li>• Coordinate customs clearance procedures</li>
                        <li>• Schedule installation team and resources</li>
                        <li>• Prepare commissioning and testing procedures</li>
                        <li>• Document project for future reference</li>
                      </ul>
                    </div>
                  </div>
                  
                  <div className="mt-4 p-3 bg-white rounded-lg border-l-4 border-black">
                    <p className="text-sm text-gray-700">
                      <strong>Business Model Note:</strong> This system implements the complete NogaHub business structure 
                      with accurate profit distribution across Void Acoustics Jordan, Nogahub, and Avecion entities 
                      according to the official business report and chart of accounts.
                    </p>
                  </div>
                </div>
              )}
            </div>
          )}
            </>
          )}

          {/* Rental Section Content — standalone, no redirects to Installation */}
          {activeSection === 'rental' && (
            <div>
              {/* Rental sub-nav */}
              <div className="border-b border-gray-200 mb-6">
                <nav className="flex flex-wrap gap-2 sm:gap-0 sm:space-x-8 px-1 overflow-x-auto">
                  {[
                    { id: 'calendar', label: 'Calendar', icon: RefreshCw },
                    { id: 'quotation', label: 'Quotation', icon: Calculator },
                    { id: 'saved-projects', label: 'Saved Quotes', icon: FolderOpen },
                    { id: 'documents', label: 'Documents', icon: FileText },
                    { id: 'financials', label: 'Financials', icon: TrendingUp },
                  ].map(tab => {
                    const Icon = tab.icon;
                    return (
                      <button
                        key={tab.id}
                        onClick={() => setRentalTab(tab.id)}
                        className={`py-2 sm:py-3 px-2 sm:px-1 border-b-2 font-medium text-xs sm:text-sm flex items-center space-x-1 sm:space-x-2 transition-all whitespace-nowrap ${
                          rentalTab === tab.id
                            ? 'border-black text-black'
                            : 'border-transparent text-gray-500 hover:text-gray-700'
                        }`}
                      >
                        <Icon size={14} />
                        <span className="hidden sm:inline">{tab.label}</span>
                        <span className="sm:hidden">{tab.label.split(' ')[0]}</span>
                      </button>
                    );
                  })}
                </nav>
              </div>

              {/* Calendar */}
              {rentalTab === 'calendar' && <RentalCalendar />}

              {/* Rental Quotation Calculator */}
              {rentalTab === 'quotation' && (
                <div className="space-y-6">
                  <div className="flex justify-between items-center">
                    <h3 className="text-lg font-semibold text-gray-900">Rental Quotation Generator</h3>
                    <button onClick={startNewRentalQuotation} className="text-sm text-gray-500 hover:text-gray-700 underline">New Quotation</button>
                  </div>

                  {/* Project Info */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Client Name</label>
                      <input type="text" placeholder="Enter client name" value={rentalQuotation.clientName}
                        onChange={e => { setRentalQuotation(prev => ({ ...prev, clientName: e.target.value })); setRentalCalculated(false); }}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-black focus:border-black" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Project / Event Name</label>
                      <input type="text" placeholder="Enter project name" value={rentalQuotation.projectName}
                        onChange={e => { setRentalQuotation(prev => ({ ...prev, projectName: e.target.value })); setRentalCalculated(false); }}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-black focus:border-black" />
                    </div>
                  </div>

                  {/* Items */}
                  <div className="bg-white border border-gray-200 rounded-xl p-6">
                    <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 mb-4">
                      <h3 className="text-lg font-semibold text-gray-900">BOQ</h3>
                      <div className="flex flex-wrap items-center gap-2 sm:gap-3">
                        <div className="flex rounded-lg border border-gray-300 overflow-hidden text-sm">
                          {[{ code: 'JOD', label: 'JOD' }, { code: 'USD', label: '$' }, { code: 'EUR', label: '€' }].map(({ code, label }) => (
                            <button key={code} onClick={() => { setRentalQuotation(prev => ({ ...prev, currency: code })); setRentalCalculated(false); }}
                              className={`px-3 py-2 font-medium transition-colors ${rentalQuotation.currency === code ? 'bg-black text-white' : 'bg-white text-gray-700 hover:bg-gray-100'}`}>
                              {label}
                            </button>
                          ))}
                        </div>
                        <button onClick={addRentalItem} className="flex items-center space-x-2 px-4 py-2 bg-black text-white rounded-lg hover:bg-gray-800 transition-colors">
                          <Plus size={16} /><span>Add Item</span>
                        </button>
                      </div>
                    </div>

                    {rentalQuotation.items.length === 0 ? (
                      <div className="text-center py-8 text-gray-500"><p>No items yet. Click "Add Item" to start.</p></div>
                    ) : renderRentalItemRows('items', rentalQuotation.items)}

                    {/* Technical Rider */}
                    <div className="mt-6 border-t border-gray-200 pt-6">
                      <div className="flex justify-between items-center mb-4">
                        <h4 className="text-base font-semibold text-gray-900">Technical Rider</h4>
                        <button onClick={addRentalRiderItem} className="flex items-center space-x-2 px-3 py-2 bg-gray-800 text-white rounded-lg hover:bg-gray-700 transition-colors text-sm">
                          <Plus size={14} /><span>Add Rider Item</span>
                        </button>
                      </div>
                      {(rentalQuotation.riderItems || []).length === 0 ? (
                        <p className="text-sm text-gray-400 italic">No technical rider items. Add mixers, mics, monitors, backline, etc.</p>
                      ) : renderRentalItemRows('riderItems', rentalQuotation.riderItems)}
                    </div>

                    {/* Services */}
                    <div className="mt-6 border-t border-gray-200 pt-6">
                      <div className="flex justify-between items-center mb-4">
                        <h4 className="text-base font-semibold text-gray-900">Additional Services</h4>
                        <button onClick={addRentalService} className="flex items-center space-x-2 px-3 py-2 bg-gray-800 text-white rounded-lg hover:bg-gray-700 transition-colors text-sm">
                          <Plus size={14} /><span>Add Service</span>
                        </button>
                      </div>
                      {rentalQuotation.services.length === 0 ? (
                        <p className="text-sm text-gray-400 italic">No services added. Add delivery, setup, engineering, etc.</p>
                      ) : (
                        <div className="space-y-2">
                          {rentalQuotation.services.map((service, index) => (
                            <div key={index} className="grid grid-cols-1 md:grid-cols-3 gap-3 items-center p-3 bg-gray-50 rounded-lg">
                              <input type="text" placeholder="Service name (e.g. Setup & Teardown)" value={service.name}
                                onChange={e => { setRentalQuotation(prev => ({ ...prev, services: prev.services.map((s, i) => i === index ? { ...s, name: e.target.value } : s) })); setRentalCalculated(false); }}
                                className="md:col-span-2 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-black focus:border-black text-sm" />
                              <div className="flex items-center space-x-2">
                                <input type="number" placeholder="Price" value={service.price}
                                  onChange={e => { setRentalQuotation(prev => ({ ...prev, services: prev.services.map((s, i) => i === index ? { ...s, price: e.target.value } : s) })); setRentalCalculated(false); }}
                                  className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-black focus:border-black text-sm" />
                                <button onClick={() => { setRentalQuotation(prev => ({ ...prev, services: prev.services.filter((_, i) => i !== index) })); setRentalCalculated(false); }}
                                  className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"><Trash2 size={16} /></button>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>

                    {/* Discount / Tax / Terms */}
                    <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Global Discount (%)</label>
                        <input type="number" min="0" max="100" value={rentalQuotation.globalDiscount}
                          onChange={e => { setRentalQuotation(prev => ({ ...prev, globalDiscount: Math.min(100, Math.max(0, parseFloat(e.target.value) || 0)) })); setRentalCalculated(false); }}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-black focus:border-black" />
                      </div>
                      <div className="flex items-end">
                        <label className="flex items-center space-x-3 cursor-pointer">
                          <input type="checkbox" checked={rentalQuotation.includeTax}
                            onChange={e => { setRentalQuotation(prev => ({ ...prev, includeTax: e.target.checked })); setRentalCalculated(false); }}
                            className="w-5 h-5 text-black border-gray-300 rounded focus:ring-2 focus:ring-black cursor-pointer" />
                          <span className="font-medium text-gray-700">Include VAT (16%)</span>
                        </label>
                      </div>
                    </div>
                    <div className="mt-6">
                      <label className="block font-medium text-gray-700 mb-2 text-sm">Terms &amp; Conditions</label>
                      <textarea value={rentalQuotation.terms} rows="5"
                        onChange={e => setRentalQuotation(prev => ({ ...prev, terms: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-black focus:border-black resize-y text-sm" />
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div className="flex flex-wrap justify-end gap-3">
                    <button onClick={calculateRental} className="flex items-center space-x-2 px-6 py-3 bg-black text-white rounded-lg hover:bg-gray-800 transition-colors">
                      <Calculator size={16} /><span>Calculate</span>
                    </button>
                    <button onClick={() => setShowRentalSaveModal(true)}
                      disabled={rentalQuotation.items.length === 0 && (rentalQuotation.riderItems || []).length === 0 && rentalQuotation.services.length === 0}
                      className="flex items-center space-x-2 px-6 py-3 bg-black text-white rounded-lg hover:bg-gray-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed">
                      <Save size={16} /><span>{loadedRentalProjectId ? 'Update Quotation' : 'Save Quotation'}</span>
                    </button>
                    {loadedRentalProjectId && (
                      <button onClick={() => { setLoadedRentalProjectId(null); setShowRentalSaveModal(true); }}
                        className="flex items-center space-x-2 px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 transition-colors">
                        <Save size={16} /><span>Save as New</span>
                      </button>
                    )}
                  </div>

                  {/* Results */}
                  {rentalCalculated && rentalResults && (
                    <div className="bg-gray-50 border border-gray-200 rounded-xl p-6 space-y-6">
                      <h3 className="text-lg font-semibold text-gray-900">Quotation Summary</h3>
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="bg-black text-white">
                              <th className="text-left p-3 font-medium">Item Description</th>
                              <th className="text-center p-3 font-medium">Qty</th>
                              <th className="text-right p-3 font-medium">Unit Price</th>
                              <th className="text-right p-3 font-medium">Total</th>
                            </tr>
                          </thead>
                          <tbody>
                            {[...rentalResults.items, ...(rentalResults.riderItems || [])].map((item, index) => (
                              <tr key={index} className="border-b border-gray-200">
                                <td className="p-3">{item.name || 'Unnamed Item'}</td>
                                <td className="text-center p-3">{item.quantity}</td>
                                <td className="text-right p-3">{(Number(item.clientPrice) || 0).toFixed(1)} {rentalQuotation.currency}</td>
                                <td className="text-right p-3">{((Number(item.clientPrice) || 0) * (Number(item.quantity) || 0)).toFixed(1)} {rentalQuotation.currency}</td>
                              </tr>
                            ))}
                            {(rentalResults.services || []).length > 0 && (
                              <>
                                <tr className="bg-gray-100"><td colSpan="4" className="p-2 text-xs font-semibold text-gray-600 uppercase tracking-wide">Services</td></tr>
                                {rentalResults.services.map((svc, index) => (
                                  <tr key={`svc-${index}`} className="border-b border-gray-200">
                                    <td className="p-3">{svc.name || 'Service'}</td>
                                    <td className="text-center p-3">1</td>
                                    <td className="text-right p-3">{(Number(svc.price) || 0).toFixed(1)} {rentalQuotation.currency}</td>
                                    <td className="text-right p-3">{(Number(svc.price) || 0).toFixed(1)} {rentalQuotation.currency}</td>
                                  </tr>
                                ))}
                              </>
                            )}
                          </tbody>
                        </table>
                      </div>
                      <div className="border-t border-gray-300 pt-4">
                        <div className="flex justify-between mb-2"><span className="font-medium">Subtotal:</span><span>{rentalResults.totalRevenueBeforeDiscount.toFixed(1)} {rentalQuotation.currency}</span></div>
                        {rentalQuotation.globalDiscount > 0 && (
                          <div className="flex justify-between mb-2 text-red-600"><span className="font-medium">Discount:</span><span>-{rentalResults.discountAmount.toFixed(1)} {rentalQuotation.currency}</span></div>
                        )}
                        {rentalQuotation.globalDiscount > 0 && rentalQuotation.includeTax && (
                          <div className="flex justify-between mb-2"><span className="font-medium">Subtotal:</span><span>{rentalResults.subtotal.toFixed(1)} {rentalQuotation.currency}</span></div>
                        )}
                        {rentalQuotation.includeTax && <div className="flex justify-between mb-2"><span className="font-medium">VAT (16%):</span><span>{rentalResults.tax.toFixed(1)} {rentalQuotation.currency}</span></div>}
                        <div className="flex justify-between text-lg font-bold border-t border-gray-300 pt-2 mt-2"><span>Total:</span><span className="text-green-600">{rentalResults.total.toFixed(1)} {rentalQuotation.currency}</span></div>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Rental Saved Projects */}
              {rentalTab === 'saved-projects' && (
                <div className="space-y-6">
                  <div className="flex justify-between items-center">
                    <h3 className="text-lg font-semibold text-gray-900">Saved Rental Quotations ({savedRentalProjects.length})</h3>
                    <button onClick={() => setRentalTab('quotation')} className="flex items-center space-x-2 px-4 py-2 bg-black text-white rounded-lg hover:bg-gray-800 transition-colors text-sm">
                      <Plus size={14} /><span>New Quotation</span>
                    </button>
                  </div>
                  {savedRentalProjects.length === 0 ? (
                    <div className="bg-gray-50 border border-gray-200 rounded-xl p-8 text-center">
                      <FolderOpen size={48} className="mx-auto text-gray-400 mb-4" />
                      <h4 className="text-lg font-medium text-gray-900 mb-2">No rental quotations yet</h4>
                      <p className="text-gray-600">Go to the Quotation tab, add items, and click "Save Quotation".</p>
                    </div>
                  ) : (
                    <div className="grid gap-4">
                      {savedRentalProjects.map(saved => (
                        <div key={saved.id} className="bg-white border border-gray-200 rounded-xl p-4 sm:p-6 hover:shadow-md transition-shadow cursor-pointer group" onClick={() => loadRentalProject(saved)}>
                          <div className="flex justify-between items-start gap-2">
                            <div className="flex-1 min-w-0">
                              <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mb-2">
                                <h4 className="text-lg font-semibold text-gray-900">{saved.projectName}</h4>
                                <span className="px-2 py-1 bg-orange-100 text-orange-800 text-xs font-medium rounded">Rental</span>
                                {saved.isCalculated && <span className="px-2 py-1 bg-green-100 text-green-800 text-xs font-medium rounded-full">Calculated</span>}
                              </div>
                              <p className="text-gray-600 font-medium mb-1">{saved.clientName}</p>
                              {saved.created_by_username && (
                                <p className="text-blue-600 text-sm mb-1">Created by: {saved.created_by_username} ({saved.created_by_email})</p>
                              )}
                              <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-gray-500 mb-2">
                                <span>{(saved.customEquipment || []).length} items</span>
                                <span>•</span>
                                <span>{(parseFloat(saved.total) || 0).toFixed(2)} {saved.currency || 'JOD'} total</span>
                                <span>•</span>
                                <span>Saved {saved.createdAt ? new Date(saved.createdAt).toLocaleString('en-US', { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', hour12: false }).replace(',', '') : 'Unknown'}</span>
                              </div>
                              <div className="flex flex-wrap gap-1 mt-2">
                                {(saved.customEquipment || []).slice(0, 4).map((item, i) => (
                                  <span key={i} className="inline-block px-2 py-1 bg-orange-50 text-orange-700 text-xs rounded">{item.name}</span>
                                ))}
                                {(saved.customEquipment || []).length > 4 && (
                                  <span className="inline-block px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded">+{(saved.customEquipment || []).length - 4} more</span>
                                )}
                              </div>
                            </div>
                            <button onClick={e => { e.stopPropagation(); deleteRentalProject(saved.id); }}
                              className="opacity-0 group-hover:opacity-100 p-2 text-red-500 hover:text-red-700 hover:bg-red-50 rounded-lg transition-all">
                              <Trash2 size={16} />
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Rental Documents */}
              {rentalTab === 'documents' && (
                <div className="space-y-4">
                  {(!rentalCalculated || !rentalResults) ? (
                    <div className="bg-amber-50 border border-amber-200 rounded-xl p-6 text-center">
                      <Calculator size={40} className="mx-auto text-amber-400 mb-3" />
                      <h4 className="font-semibold text-amber-900 mb-1">No rental quotation calculated yet</h4>
                      <p className="text-amber-700 text-sm">Go to the Quotation tab, add items, and click Calculate — then come back here to download documents.</p>
                      <button onClick={() => setRentalTab('quotation')} className="mt-4 px-4 py-2 bg-black text-white rounded-lg text-sm hover:bg-gray-800 transition-colors">Go to Quotation</button>
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {/* Rental Quotation */}
                      <div className="border border-gray-200 rounded-xl p-4">
                        <h4 className="font-semibold text-gray-900 mb-3">Rental Quotation</h4>
                        <div className="bg-white border rounded-lg p-4 text-sm space-y-2">
                          <div className="border-b pb-2 mb-3">
                            <h5 className="font-bold">Deep Sound For Technical Consultations</h5>
                            <p className="text-xs text-gray-600">Housing Bank Complex 93 - Ground Floor 102</p>
                            <p className="text-xs text-gray-600">Q. Nour St. - Welbdeh - Amman - Jordan</p>
                            <span className="inline-block mt-1 px-2 py-0.5 bg-orange-100 text-orange-800 text-xs font-medium rounded">RENTAL</span>
                          </div>
                          <div className="flex justify-between text-xs">
                            <span>Client: {rentalQuotation.clientName || 'Client Name'}</span>
                            <span>Date: {new Date().toLocaleDateString()}</span>
                          </div>
                          <div className="text-xs">Project: {rentalQuotation.projectName || 'Project Name'}</div>
                          <div className="mt-3">
                            {[...rentalResults.items, ...(rentalResults.riderItems || [])].map((item, i) => (
                              <div key={i} className="flex justify-between text-xs">
                                <span>{item.name} x{item.quantity}</span>
                                <span>{((Number(item.clientPrice) || 0) * (Number(item.quantity) || 0)).toFixed(1)} {rentalQuotation.currency}</span>
                              </div>
                            ))}
                            <div className="border-t mt-2 pt-2">
                              <div className="flex justify-between text-xs"><span>Subtotal:</span><span>{(rentalResults.totalRevenueBeforeDiscount || 0).toFixed(1)} {rentalQuotation.currency}</span></div>
                              {rentalQuotation.globalDiscount > 0 && <div className="flex justify-between text-xs text-red-600"><span>Discount:</span><span>-{(rentalResults.discountAmount || 0).toFixed(1)} {rentalQuotation.currency}</span></div>}
                              {rentalQuotation.globalDiscount > 0 && rentalQuotation.includeTax && <div className="flex justify-between text-xs"><span>Subtotal:</span><span>{(rentalResults.subtotal || 0).toFixed(1)} {rentalQuotation.currency}</span></div>}
                              {rentalQuotation.includeTax && <div className="flex justify-between text-xs"><span>VAT (16%):</span><span>{(rentalResults.tax || 0).toFixed(1)} {rentalQuotation.currency}</span></div>}
                              <div className="flex justify-between font-semibold"><span>Total:</span><span>{(rentalResults.total || 0).toFixed(1)} {rentalQuotation.currency}</span></div>
                            </div>
                          </div>
                        </div>
                        <button onClick={downloadRentalQuotationPDF} className="mt-3 w-full flex items-center justify-center space-x-2 px-4 py-2 bg-black text-white rounded-lg hover:bg-gray-800 transition-colors">
                          <Download size={16} /><span>Download Rental Quotation PDF</span>
                        </button>
                      </div>

                      {/* Rental Invoice */}
                      <div className="border border-gray-200 rounded-xl p-4">
                        <h4 className="font-semibold text-gray-900 mb-3">Rental Invoice</h4>
                        <div className="bg-white border rounded-lg p-4 text-sm space-y-2">
                          <div className="border-b pb-2 mb-3">
                            <h5 className="font-bold">Deep Sound For Technical Consultation LLC</h5>
                            <p className="text-xs text-gray-600">Q. Nour St. - Welbdeh - Amman - Jordan</p>
                            <p className="text-xs text-gray-600">Tax Reg No: 40261328</p>
                          </div>
                          <div className="flex justify-between text-xs">
                            <span>Bill To: {rentalQuotation.clientName || 'Client Name'}</span>
                            <span>Due: on receipt</span>
                          </div>
                          <div className="text-xs">Project: {rentalQuotation.projectName || 'Project Name'}</div>
                          <div className="border-t mt-3 pt-2">
                            <div className="flex justify-between text-xs"><span>Subtotal:</span><span>{(rentalResults.subtotal || 0).toFixed(2)} {rentalQuotation.currency}</span></div>
                            {rentalQuotation.includeTax && <div className="flex justify-between text-xs"><span>VAT (16%):</span><span>{(rentalResults.tax || 0).toFixed(2)} {rentalQuotation.currency}</span></div>}
                            <div className="flex justify-between font-semibold"><span>Total Due:</span><span>{(rentalResults.total || 0).toFixed(2)} {rentalQuotation.currency}</span></div>
                          </div>
                          <div className="mt-2 pt-2 border-t text-xs text-gray-500">
                            <p>Bank: Etihad Bank · CliQ: Deepsound</p>
                            <p>IBAN: JO37UBSI1200000310179526015101</p>
                          </div>
                        </div>
                        <button onClick={downloadRentalInvoicePDF} className="mt-3 w-full flex items-center justify-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors">
                          <Download size={16} /><span>Download Rental Invoice PDF</span>
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Financials (admin only) */}
              {rentalTab === 'financials' && <RentalsFinancials />}
            </div>
          )}
        </div>
      </div>

      {/* Save Project Modal */}
      {showSaveModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg max-w-md w-full mx-4 max-h-[90vh] overflow-y-auto">
            <h3 className="text-lg font-semibold mb-4">Save Project</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Project Name</label>
                <input
                  type="text"
                  value={project.projectName}
                  onChange={(e) => setProject(prev => ({ ...prev, projectName: e.target.value }))}
                  className="w-full border border-gray-300 rounded px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Enter project name"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Client Name</label>
                <input
                  type="text"
                  value={project.clientName}
                  onChange={(e) => setProject(prev => ({ ...prev, clientName: e.target.value }))}
                  className="w-full border border-gray-300 rounded px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Enter client name"
                />
              </div>
              <div className="flex gap-3 pt-4">
                <button
                  onClick={() => setShowSaveModal(false)}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={saveCurrentProject}
                  disabled={!project.projectName.trim() || !project.clientName.trim()}
                  className="flex-1 px-4 py-2 bg-black text-white rounded-lg hover:bg-red-600 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed"
                >
                  Save Project
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Save Custom Project Modal */}
      {showNcSaveModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg max-w-md w-full mx-4 max-h-[90vh] overflow-y-auto">
            <h3 className="text-lg font-semibold mb-4">Save Custom Project</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Project Name</label>
                <input
                  type="text"
                  value={customQuotation.projectName}
                  onChange={(e) => setCustomQuotation(prev => ({ ...prev, projectName: e.target.value }))}
                  className="w-full border border-gray-300 rounded px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Enter project name"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Client Name</label>
                <input
                  type="text"
                  value={customQuotation.clientName}
                  onChange={(e) => setCustomQuotation(prev => ({ ...prev, clientName: e.target.value }))}
                  className="w-full border border-gray-300 rounded px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Enter client name"
                />
              </div>
              <div className="flex gap-3 pt-4">
                <button
                  onClick={() => setShowNcSaveModal(false)}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={saveCustomProject}
                  disabled={!customQuotation.projectName.trim() || !customQuotation.clientName.trim()}
                  className="flex-1 px-4 py-2 bg-black text-white rounded-lg hover:bg-red-600 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed"
                >
                  Save Project
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Rental Save Modal */}
      {showRentalSaveModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg max-w-md w-full mx-4 max-h-[90vh] overflow-y-auto">
            <h3 className="text-lg font-semibold mb-4">Save Rental Quotation</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Project / Event Name</label>
                <input type="text" value={rentalQuotation.projectName}
                  onChange={e => setRentalQuotation(prev => ({ ...prev, projectName: e.target.value }))}
                  className="w-full border border-gray-300 rounded px-3 py-2 focus:ring-2 focus:ring-black focus:border-black"
                  placeholder="Enter project name" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Client Name</label>
                <input type="text" value={rentalQuotation.clientName}
                  onChange={e => setRentalQuotation(prev => ({ ...prev, clientName: e.target.value }))}
                  className="w-full border border-gray-300 rounded px-3 py-2 focus:ring-2 focus:ring-black focus:border-black"
                  placeholder="Enter client name" />
              </div>
              <div className="flex gap-3 pt-4">
                <button onClick={() => setShowRentalSaveModal(false)}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded hover:bg-gray-50">Cancel</button>
                <button onClick={saveRentalProject}
                  disabled={!rentalQuotation.projectName.trim() || !rentalQuotation.clientName.trim()}
                  className="flex-1 px-4 py-2 bg-black text-white rounded-lg hover:bg-gray-800 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed">
                  Save Quotation
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Toast Notifications */}
      <Toaster
        position="top-center"
        reverseOrder={false}
        gutter={8}
        containerClassName=""
        containerStyle={{}}
        toastOptions={{
          className: '',
          duration: 4000,
          style: {
            background: '#ffffff',
            color: '#374151',
            borderRadius: '0.75rem',
            border: '1px solid #e5e7eb',
            padding: '16px',
            fontSize: '14px',
            fontWeight: '500',
            boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
          },
          success: {
            duration: 3000,
            style: {
              background: '#f0fdf4',
              color: '#166534',
              border: '1px solid #bbf7d0',
            },
            iconTheme: {
              primary: '#22c55e',
              secondary: '#f0fdf4',
            },
          },
          error: {
            duration: 5000,
            style: {
              background: '#fef2f2',
              color: '#991b1b',
              border: '1px solid #fecaca',
            },
            iconTheme: {
              primary: '#ef4444',
              secondary: '#fef2f2',
            },
          },
          loading: {
            style: {
              background: '#fefbf3',
              color: '#92400e',
              border: '1px solid #fed7aa',
            },
            iconTheme: {
              primary: '#f59e0b',
              secondary: '#fefbf3',
            },
          },
        }}
      />
    </div>
  );
};

export default NogaHubAutomation;